import { confirmInGrain } from "../utils/grainDialogs.js";
import React from "react";
import { buildOrderEditSubmission } from "../utils/planningOrderEditDiagnostics.js";
import "./FleetContext.css";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { Expand, Route, Sailboat } from "lucide-react";
import {
  ActionButton,
  HoverCard,
  Pill,
  Tooltip,
  joinClasses,
} from "./ResultsPrimitives.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";
import EndOfDayLoadRemovalDialog from "./EndOfDayLoadRemovalDialog.jsx";
import {
  getRouteMetaChipDisplayLabel,
  resolveRouteChipHoverCardContent,
  resolveRouteChipHoverCardTitle,
  resolveRouteMetaChipPresentation,
} from "./RouteMetaChipRow.jsx";
import { DEFAULT_OPTIMIZE_CONFIRMATION } from "./resultsPageChrome/shared.js";
import TruckProtectionActionPanel from "./TruckProtectionActionPanel.jsx";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import {
  ORDER_LOADING_PRIORITY_OPTIONS,
  buildLoadingClientCorrespondenceIndex,
  buildUpdatePayload,
  canEditSingleDeliveryWindowFields,
  createEditableOrderDraft,
  formatQuantityTons,
  getOrderDraftErrors,
  hasOrderDraftChanges,
} from "../hooks/useDataManagerOrdersUtils.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import {
  buildSegmentActionFeedbackLines,
  getSegmentActionPopoverWidthClass,
  shouldHideSupersededSegmentConfirmButton,
  getSegmentApprovalBlockingReason,
  getSegmentHoverCardAccentColor,
  getSegmentHoverSurfaceClassName,
  normalizeSegmentMetaValue,
  resolveEndOfDayLoadDisplayMode,
  resolveEndOfDayLoadVisualMode,
  resolveSegmentPrimaryTitle,
  resolveSegmentActionMetaLabel,
  resolvePmSegmentContext,
} from "../utils/fleetSegmentPresentation.js";
import {
  getEndOfDayLoadModeIconName,
  getEndOfDayLoadModeLongLabel,
  getEndOfDayLoadModeShortLabel,
  normalizeEndOfDayLoadDateValue,
} from "../utils/endOfDayLoadModeLabels.js";
import {
  buildEndOfDayLoadSegmentEditorEmptyMessage,
  buildEndOfDayLoadTargetTrucksEmptyMessage,
  filterEndOfDayLoadSourceTrucks,
  filterEndOfDayLoadTargetTrucks,
  isTransferTargetTractorMissing,
  resolveEndOfDayLoadInitialTruckLabel,
  resolveEndOfDayLoadTargetTruckLabel,
  resolveEndOfDayLoadTrailerFieldLabel,
  resolveEndOfDayLoadTrailerLabel,
  resolveEndOfDayLoadTrailerWords,
  resolveEndOfDayLoadSourceProductLabel,
  resolveSelectableTransferTarget,
  transferTargetLabel,
} from "../utils/fleetReassignmentHelpers.js";
import { isMergedOperationalMissionSegment } from "../utils/fleetTimelinePresentation.js";
import { formatDurationShort, MANUAL_BUFFER_REASON_OPTIONS } from "../utils/routeModelsData.js";
import { resolveNextDispatchDateValue } from "../utils/dispatchDateUrls.js";
import {
  PROTECTION_VARIANT_REORDERED,
  REORDERED_PROTECTION_ACCENT_COLOR,
  resolveProtectionVariant,
} from "../utils/protectionVisualState.js";
import { isFrozenDispatcherStatusCode, isImplicitFrozenDispatcherStatusCode } from "../utils/dispatcherFreezeStatus.js";
import { resolveAssignmentDisplayName } from "../utils/resultsFleetCardShell.js";

import {
  FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS,
  GroupedSegmentOrdersHoverIcon,
  resolveEndOfDayLoadLabelOptions,
  resolveSegmentDestinationAddress,
  renderSegmentStatusIcon,
  SegmentSummaryBlock,
  fetchRouteAlternatives,
  normalizeFleetRouteSegmentIndexes,
} from "./FleetTimelinePresentation.jsx";

function buildTransferTargetOption(target = {}) {
  const truckRawData = target?.truck_raw_data && typeof target.truck_raw_data === "object"
    ? target.truck_raw_data
    : (target?.truckRawData && typeof target.truckRawData === "object" ? target.truckRawData : null);
  // Le produit associé vient du seul champ du Data Manager, jamais d'une copie d'import.
  const preferredGrainType = normalizeSegmentMetaValue(target?.preferredGrainType || target?.preferred_grain_type);
  return {
    aspirator: normalizeSegmentMetaValue(target?.aspirator || truckRawData?.aspirator || truckRawData?.Aspirateur),
    compartmentName: normalizeSegmentMetaValue(target?.compartmentName || target?.compartment_name),
    isActive: target?.isActive !== false && target?.is_active !== false && truckRawData?.is_active !== false,
    isDispatchDisabled: target?.isDispatchDisabled === true || target?.is_dispatch_disabled === true || truckRawData?.is_dispatch_disabled === true,
    isDompeur: target?.isDompeur ?? target?.is_dompeur ?? truckRawData?.is_dompeur ?? truckRawData?.Dompeur,
    isInitialPreloadEnabled: target?.isInitialPreloadEnabled ?? target?.is_initial_preload_enabled ?? truckRawData?.is_initial_preload_enabled ?? truckRawData?.pool_preload ?? truckRawData?.["Pool preload"] ?? truckRawData?.["Pool Preload"],
    isTractorMissing: isTransferTargetTractorMissing(target),
    isTruckProtected: target?.isTruckProtected ?? target?.is_truck_protected ?? truckRawData?.is_truck_protected,
    meta: normalizeSegmentMetaValue(target?.meta),
    ontario: normalizeSegmentMetaValue(target?.ontario || truckRawData?.ontario || truckRawData?.Ontario),
    preferredGrainType,
    preferred_grain_type: preferredGrainType,
    truckType: normalizeSegmentMetaValue(target?.truckType || target?.truck_type || target?.type || truckRawData?.truck_type || truckRawData?.type || truckRawData?.Type),
    truckRawData,
    truck_raw_data: truckRawData,
    truckId: normalizeSegmentMetaValue(target?.truckId || target?.truck_id || target?.assignmentId),
    truckName: normalizeSegmentMetaValue(target?.truckName || target?.truck_name || target?.label),
  };
}

function buildTransferTargetOptionsFromTruckReassignmentTargets(truckReassignmentTargets = []) {
  return (Array.isArray(truckReassignmentTargets) ? truckReassignmentTargets : [])
    .map(buildTransferTargetOption)
    .filter((target) => target.truckId);
}

function buildTransferTargetOptionsFromOptimizationAssignments(assignments = []) {
  return (Array.isArray(assignments) ? assignments : [])
    .map((assignment) => buildTransferTargetOption({
      ...assignment,
      isDispatchDisabled: assignment?.isDispatchDisabled === true || assignment?.is_dispatch_disabled === true,
      truckId: assignment?.truckId || assignment?.truck_id || assignment?.assignmentId,
      // Nom du Gantt : « W27 » pour une remorque « Camion à choisir », pas « 1906-W27 ».
      truckName: assignment?.truckName || resolveAssignmentDisplayName(assignment) || assignment?.label,
    }))
    .filter((target) => target.truckId);
}

function buildAvailableEndOfDayLoadSourceTruckOptions(assignments = []) {
  return filterEndOfDayLoadSourceTrucks(
    buildTransferTargetOptionsFromOptimizationAssignments(assignments),
  );
}

const END_OF_DAY_LOAD_PENDING_SOURCE_CHANGES = "pending_source_changes";
const END_OF_DAY_LOAD_PENDING_TRUCK_SOURCE_CHANGES = "pending_truck_source_changes";
const END_OF_DAY_LOAD_STALE_SNAPSHOT = "stale_snapshot";

// Fenêtre Chargé PM : mêmes champs depuis un segment du Gantt et depuis les suspendues.
const END_OF_DAY_LOAD_EDITOR_PANEL_CLASS_NAME = "fleet-pm-editor min-w-0 rounded-[4px]! bg-white! wrap-anywhere";
const END_OF_DAY_LOAD_FIELDS_GRID_CLASS_NAME = "grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-2";
const END_OF_DAY_LOAD_FIELD_COLUMN_CLASS_NAME = "grid min-w-0 content-start gap-2";
const END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME = "grid min-w-0 gap-1 text-[0.7rem] font-semibold text-content-copy";
const END_OF_DAY_LOAD_FIELD_CLASS_NAME = "h-9 w-full min-w-0 max-w-full rounded-[4px] border border-slate-300 bg-white px-2 text-[0.82rem] text-text outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100";

function resolveEndOfDayLoadTruckSourceChangesMessage({
  allowMissingSnapshot = false,
  dispatchDate = "",
  dispatchPeriod = "day",
  dispatchState = null,
  role = "source",
  truck = null,
} = {}) {
  const normalizedTruckId = normalizeSegmentMetaValue(truck?.truckId);
  const normalizedDispatchDate = normalizeEndOfDayLoadDateValue(dispatchDate);
  const normalizedDispatchPeriod = dispatchPeriod === "evening" ? "evening" : "day";
  if (!normalizedTruckId || !normalizedDispatchDate) {
    return "";
  }

  const words = resolveEndOfDayLoadTrailerWords(truck, role);
  const hasMatchingScope = dispatchState?.dispatchDate === normalizedDispatchDate
    && dispatchState?.dispatchPeriod === normalizedDispatchPeriod;
  if (!hasMatchingScope || dispatchState?.isLoading) {
    return `Vérification des M-à-J commandes ${words.of}...`;
  }
  if (dispatchState?.errorMessage) {
    return dispatchState.errorMessage;
  }
  if (dispatchState?.snapshotAvailable === false) {
    return allowMissingSnapshot
      ? ""
      : `Impossible de confirmer que ${words.the} est à jour. Chargé PM reste indisponible.`;
  }
  if (dispatchState?.isStaleSnapshot !== true) {
    return "";
  }

  const sourceChangeTruckPolicy = dispatchState?.sourceChangeTruckPolicy;
  const hasExplicitTruckPolicy = Boolean(
    sourceChangeTruckPolicy
    && typeof sourceChangeTruckPolicy === "object"
    && !Array.isArray(sourceChangeTruckPolicy)
    && Object.prototype.hasOwnProperty.call(sourceChangeTruckPolicy, normalizedTruckId),
  );
  if (hasExplicitTruckPolicy && sourceChangeTruckPolicy[normalizedTruckId] === null) {
    return "";
  }

  if (
    hasExplicitTruckPolicy
    && sourceChangeTruckPolicy[normalizedTruckId] === END_OF_DAY_LOAD_PENDING_SOURCE_CHANGES
  ) {
    return `${words.The} contient une ou plusieurs commandes touchées par les M-à-J en attente. Effectuez les M-à-J commandes avant d'utiliser ${words.this}.`;
  }
  if (
    hasExplicitTruckPolicy
    && sourceChangeTruckPolicy[normalizedTruckId] === END_OF_DAY_LOAD_PENDING_TRUCK_SOURCE_CHANGES
  ) {
    const dispatchPeriodLabel = normalizedDispatchPeriod === "evening" ? "Soir" : "Jour";
    return `${words.The} a des modifications en attente. Consultez « Mise à jour » (Camions, Remorques ou Chauffeurs) du dispatch ${dispatchPeriodLabel} du ${normalizedDispatchDate}, puis réessayez.`;
  }
  if (
    hasExplicitTruckPolicy
    && sourceChangeTruckPolicy[normalizedTruckId] === END_OF_DAY_LOAD_STALE_SNAPSHOT
  ) {
    const dispatchPeriodLabel = normalizedDispatchPeriod === "evening" ? "Soir" : "Jour";
    return `Le dispatch ${dispatchPeriodLabel} du ${normalizedDispatchDate} est obsolète pour ${words.the}. Relancez l'optimisation de cette date avant d'utiliser ${words.this}.`;
  }
  return `Impossible de vérifier l'état ${words.of}. Rechargez le dispatch, puis réessayez.`;
}

// La remorque cible du Chargé PM (sa source) doit avoir un camion au dispatch cible quand celui-ci existe.
function resolveEndOfDayLoadTargetTrailerMessage({
  dispatchDate = "",
  dispatchPeriod = "day",
  dispatchState = null,
  truck = null,
} = {}) {
  const truckId = normalizeSegmentMetaValue(truck?.truckId);
  const normalizedDispatchPeriod = dispatchPeriod === "evening" ? "evening" : "day";
  if (
    !truckId
    || dispatchState?.snapshotAvailable !== true
    || dispatchState?.dispatchDate !== dispatchDate
    || dispatchState?.dispatchPeriod !== normalizedDispatchPeriod
  ) {
    return "";
  }

  const target = (Array.isArray(dispatchState.options) ? dispatchState.options : [])
    .find((option) => option.truckId === truckId);
  const label = resolveEndOfDayLoadTrailerLabel(truck);
  const dispatchLabel = `dispatch ${normalizedDispatchPeriod === "evening" ? "Soir" : "Jour"} du ${dispatchDate}`;
  if (!target) {
    return truck?.compartmentName
      ? `La remorque ${label} n'est pas au ${dispatchLabel} : le Chargé en PM reste dans sa remorque. Attelez-la d'abord à un camion de ce dispatch (« Changer de remorque »).`
      : `Le camion ${label} n'est pas au ${dispatchLabel} : le Chargé en PM reste dans ce camion.`;
  }
  return target.isTractorMissing
    ? `La remorque ${label} n'a pas de camion au ${dispatchLabel} : choisissez-lui d'abord un camion (« Changer de camion »).`
    : "";
}

function useEndOfDayLoadSourceTruckOptions({
  allowMissingSnapshot = false,
  dispatchDate = "",
  dispatchPeriod = "day",
  enabled = false,
} = {}) {
  const normalizedDispatchDate = normalizeEndOfDayLoadDateValue(dispatchDate);
  const normalizedDispatchPeriod = dispatchPeriod === "evening" ? "evening" : "day";
  const [state, setState] = React.useState({
    dispatchDate: "",
    dispatchPeriod: "day",
    errorMessage: "",
    isStaleSnapshot: false,
    isLoading: false,
    options: [],
    snapshotAvailable: false,
    sourceChangeTruckPolicy: null,
  });

  React.useEffect(() => {
    if (!enabled || !normalizedDispatchDate) {
      setState({
        dispatchDate: normalizedDispatchDate,
        dispatchPeriod: normalizedDispatchPeriod,
        errorMessage: "",
        isStaleSnapshot: false,
        isLoading: false,
        options: [],
        snapshotAvailable: false,
        sourceChangeTruckPolicy: null,
      });
      return undefined;
    }

    const abortController = new AbortController();
    setState({
      dispatchDate: normalizedDispatchDate,
      dispatchPeriod: normalizedDispatchPeriod,
      errorMessage: "",
      isStaleSnapshot: false,
      isLoading: true,
      options: [],
      snapshotAvailable: false,
      sourceChangeTruckPolicy: null,
    });
    const searchParams = new URLSearchParams();
    searchParams.set("dispatch_date", normalizedDispatchDate);
    searchParams.set("dispatch_period", normalizedDispatchPeriod);
    searchParams.set("include_source_change_truck_policy", "true");

    fetchWithSupabaseAuth(`/api/results/latest?${searchParams.toString()}`, { signal: abortController.signal })
      .then(async (response) => {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // Keep the contextual message when the response does not contain JSON.
        }
        const isMissingSnapshot = response.status === 404
          || (payload?.code === "resource_not_found" && payload?.resource === "latest_optimization");
        if (isMissingSnapshot && allowMissingSnapshot) {
          return { payload: null, snapshotAvailable: false };
        }
        const isStaleSnapshot = payload?.code === "latest_optimization_stale"
          || payload?.source_data_state?.stale === true;
        const sourceChangeTruckPolicy = payload?.source_change_truck_policy;
        const hasSourceChangeTruckPolicy = Boolean(
          sourceChangeTruckPolicy
          && typeof sourceChangeTruckPolicy === "object"
          && !Array.isArray(sourceChangeTruckPolicy),
        );
        const canUseStaleSnapshotPolicy = isStaleSnapshot && hasSourceChangeTruckPolicy;
        if (
          (!response.ok && !canUseStaleSnapshotPolicy)
          || (payload?.error && !isStaleSnapshot)
        ) {
          const errorMessage = normalizeSegmentMetaValue(payload?.error || payload?.detail)
            || (response.status === 404
              ? "Aucun dispatch Jour optimisé trouvé pour la date source."
              : "Impossible de charger les remorques du dispatch source.");
          throw new Error(errorMessage);
        }
        if (isStaleSnapshot && !hasSourceChangeTruckPolicy) {
          throw new Error("Impossible de vérifier les M-à-J commandes pour ce dispatch. Chargé PM reste indisponible.");
        }
        return { payload, snapshotAvailable: true };
      })
      .then(({ payload, snapshotAvailable }) => {
        if (abortController.signal.aborted) {
          return;
        }
        setState({
          dispatchDate: normalizedDispatchDate,
          dispatchPeriod: normalizedDispatchPeriod,
          errorMessage: "",
          isStaleSnapshot: payload?.code === "latest_optimization_stale"
            || payload?.source_data_state?.stale === true,
          isLoading: false,
          options: buildAvailableEndOfDayLoadSourceTruckOptions(payload?.assignments),
          snapshotAvailable,
          sourceChangeTruckPolicy: payload?.source_change_truck_policy || null,
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setState({
          dispatchDate: normalizedDispatchDate,
          dispatchPeriod: normalizedDispatchPeriod,
          errorMessage: error?.message || "Impossible de charger les remorques du dispatch source.",
          isStaleSnapshot: false,
          isLoading: false,
          options: [],
          snapshotAvailable: false,
          sourceChangeTruckPolicy: null,
        });
      });

    return () => abortController.abort();
  }, [allowMissingSnapshot, enabled, normalizedDispatchDate, normalizedDispatchPeriod]);

  return state;
}

function buildTransferTargetCompatibilityIndex(compatibilities = []) {
  return (Array.isArray(compatibilities) ? compatibilities : []).reduce((index, compatibility) => {
    const truckId = normalizeSegmentMetaValue(compatibility?.truck_id || compatibility?.truckId);
    if (truckId) {
      index[truckId] = compatibility;
    }
    return index;
  }, {});
}

function normalizeTransferBooleanText(value) {
  if (typeof value !== "string") {
    return "";
  }
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function resolveTransferBooleanLike(values = [], fallback = false) {
  for (const value of Array.isArray(values) ? values : [values]) {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return value !== 0;
    }

    const normalizedValue = normalizeTransferBooleanText(value);
    if (["1", "o", "oui", "true", "vrai", "yes"].includes(normalizedValue)) {
      return true;
    }
    if (["0", "n", "non", "false", "faux", "no"].includes(normalizedValue)) {
      return false;
    }
  }
  return fallback;
}

function normalizeTransferTruckType(value) {
  return normalizeSegmentMetaValue(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function isTransferTargetDompeur(target = {}) {
  const truckRawData = target?.truck_raw_data && typeof target.truck_raw_data === "object"
    ? target.truck_raw_data
    : (target?.truckRawData && typeof target.truckRawData === "object" ? target.truckRawData : null);

  if (resolveTransferBooleanLike([
    target?.isDompeur,
    target?.is_dompeur,
    truckRawData?.is_dompeur,
    truckRawData?.Dompeur,
  ], false)) {
    return true;
  }

  return normalizeTransferTruckType(
    target?.truckType
    || target?.truck_type
    || target?.type
    || truckRawData?.truck_type
    || truckRawData?.type
    || truckRawData?.Type,
  ) === "dompeur";
}

function buildSuspendedDompeurTargetCompatibility(target = {}, { sourceIsDompeur = false } = {}) {
  if (sourceIsDompeur !== true || isTransferTargetDompeur(target)) {
    return null;
  }

  const truckId = normalizeSegmentMetaValue(target?.truckId || target?.truck_id);
  if (!truckId) {
    return null;
  }

  return {
    truck_id: truckId,
    truck_name: normalizeSegmentMetaValue(target?.truckName || target?.truck_name) || truckId,
    is_product_compatible: true,
    is_assignable: true,
    is_current_truck: false,
    reason_code: "dompeur_override_required",
    reason_label: "Type de camion incompatible",
    detail: "Attention: cette commande Dompeur est normalement réservée aux camions dompeur. Cette réaffectation contourne cette règle.",
    tone: "warning",
    requires_confirmation: true,
    confirmation_code: "dompeur_override",
    time_window_mode: null,
    time_window_diagnostics: [],
  };
}

function attachTransferTargetCompatibilities(targets = [], compatibilityByTruckId = {}, { sourceIsDompeur = false } = {}) {
  return (Array.isArray(targets) ? targets : []).map((target) => {
    const truckId = normalizeSegmentMetaValue(target?.truckId || target?.truck_id);
    const targetCompatibility = compatibilityByTruckId?.[truckId]
      || buildSuspendedDompeurTargetCompatibility(target, { sourceIsDompeur });
    return {
      ...target,
      targetCompatibility: targetCompatibility || null,
    };
  });
}

function mergeTransferTargetOptions(primaryTargets = [], fallbackTargets = []) {
  const mergedTargetsById = new Map();
  for (const target of [...(Array.isArray(fallbackTargets) ? fallbackTargets : []), ...(Array.isArray(primaryTargets) ? primaryTargets : [])]) {
    const truckId = normalizeSegmentMetaValue(target?.truckId || target?.truck_id);
    if (!truckId) {
      continue;
    }
    mergedTargetsById.set(truckId, { ...target, truckId });
  }
  return [...mergedTargetsById.values()];
}

function buildEveningStandardTargetOptions(primaryTargets = [], fallbackTargets = [], filterOptions = {}) {
  return filterEndOfDayLoadTargetTrucks(
    mergeTransferTargetOptions(primaryTargets, fallbackTargets),
    filterOptions,
  );
}

const SOREL_TRACY_ROUTE_ALTERNATIVE_ID = "sorel_tracy_saint_ignace";

function formatTruckTotalHoverDistance(distanceKm) {
  const normalizedDistanceKm = Number(distanceKm);
  if (!Number.isFinite(normalizedDistanceKm) || normalizedDistanceKm < 0) {
    return "";
  }

  return `${Math.round(normalizedDistanceKm)} km`;
}

const SEGMENT_STATUS_PILL_CLASS_NAMES = {
  danger: "border-[rgba(220,38,38,0.18)] bg-[rgba(254,242,242,0.96)] text-[rgb(185,28,28)]",
  info: "border-[rgba(59,130,246,0.16)] bg-[rgba(239,246,255,0.96)] text-[rgb(29,78,216)]",
  manual: "border-[rgba(37,99,235,0.16)] bg-[rgba(239,246,255,0.96)] text-[rgb(29,78,216)]",
  "protection-reordered": "border-[rgba(109,40,217,0.2)] bg-[rgba(245,243,255,0.98)] text-[rgb(109,40,217)]",
  success: "border-[rgba(15,92,87,0.16)] bg-[rgba(238,247,245,0.96)] text-[var(--color-legacy-teal)]",
  warning: "border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)]",
};

function FleetSegmentComparisonDurationPill({ chip = null }) {
  const displayLabel = getRouteMetaChipDisplayLabel(chip);
  if (!displayLabel) {
    return null;
  }

  const chipPresentation = resolveRouteMetaChipPresentation(chip);
  const hoverCardContent = resolveRouteChipHoverCardContent(chip);
  const title = typeof chip?.title === "string" && chip.title.trim()
    ? chip.title.trim()
    : displayLabel;

  const pill = (
    <Pill
      tone={chipPresentation.tone}
      className={joinClasses([
        "min-h-5 max-w-full shrink-0 gap-0.75 overflow-hidden px-1.5 py-px text-[0.58rem] leading-none font-semibold shadow-none",
        chipPresentation.className || "",
      ])}
      style={chipPresentation.style}
      title={hoverCardContent == null ? title || undefined : undefined}
    >
      <span className="truncate">{displayLabel}</span>
    </Pill>
  );

  if (hoverCardContent == null) {
    return pill;
  }

  return (
    <HoverCard
      title={resolveRouteChipHoverCardTitle(chip)}
      content={hoverCardContent}
      placement="top"
      delay={120}
      {...FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS}
      accentColor={chip?.hoverAccentColor || chipPresentation.accentColor}
    >
      {pill}
    </HoverCard>
  );
}

function SegmentStatusPills({ className = "", segment }) {
  const markers = Array.isArray(segment?.statusMarkers) ? segment.statusMarkers.filter(Boolean) : [];

  if (!markers.length) {
    return null;
  }

  return (
    <div className={joinClasses(["flex max-w-full flex-wrap items-center gap-1.25", className])}>
      {markers.map((marker) => (
        <span
          key={marker.key || marker.label}
          className={joinClasses([
            "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.85 text-[0.62rem] font-semibold leading-none",
            SEGMENT_STATUS_PILL_CLASS_NAMES[marker.tone] || SEGMENT_STATUS_PILL_CLASS_NAMES.success,
          ])}
        >
          {renderSegmentStatusIcon(marker.icon, "h-3 w-3")}
          <span>{marker.label}</span>
        </span>
      ))}
    </div>
  );
}

function resolveSegmentDispatcherStatus(businessStep = null) {
  if (businessStep?.dispatcherStatus && typeof businessStep.dispatcherStatus === "object") {
    return businessStep.dispatcherStatus;
  }

  if (businessStep?.dispatcherActionButton?.mode === "freeze") {
    return {
      code: "planifiable",
      description: "Commande planifiable. Vous pouvez la geler ou utiliser les autres actions.",
      icon: "check",
      label: "Planifiable",
      tone: "info",
    };
  }

  return null;
}

function ReleaseActionHoverCardContent({ segment }) {
  const manualStatusDescription = normalizeSegmentMetaValue(segment?.businessStep?.manualStatus?.description);

  return (
    <div className="grid gap-2">
      <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">Libérer pour optimisation</div>
      {manualStatusDescription ? (
        <div className="text-[0.68rem] leading-normal text-content-muted">{manualStatusDescription}</div>
      ) : null}
      <div className="text-[0.68rem] leading-normal text-content-muted">
        Retire la réaffectation manuelle active et rend cette commande à nouveau éligible au prochain clic sur Optimiser.
      </div>
    </div>
  );
}

const SEGMENT_BUTTON_NEUTRAL_CLASS_NAME = "border-slate-300! bg-white! text-slate-700! shadow-none! hover:border-slate-400! hover:bg-slate-50! hover:text-slate-900!";
const SEGMENT_BUTTON_ACTIVE_CLASS_NAME = "border-accent! bg-accent! text-white! shadow-none! hover:border-accent-strong! hover:bg-accent-strong! hover:text-white!";
const SEGMENT_BUTTON_DISABLED_CLASS_NAME = "border-slate-200! bg-slate-50! text-slate-400! shadow-none! opacity-100!";
// Neutre par défaut ; la vue flotte peut réchauffer le contour et l'icône via des variables CSS.
const SUSPENDED_TRANSFER_BUTTON_NEUTRAL_CLASS_NAME = "border-[color:var(--suspended-transfer-border,var(--color-slate-300))]! bg-white! text-[color:var(--suspended-transfer-ink,var(--color-slate-700))]! shadow-none! hover:border-[color:var(--suspended-transfer-border-hover,var(--color-slate-400))]! hover:bg-slate-50! hover:text-slate-900!";

// Bulle des gestes de « Actions segment » sans le droit « Modifier les affectations dispatch » ; vide avec le droit.
const SegmentPlanningRightContext = React.createContext("");

function SegmentActionButton({
  ariaExpanded,
  ariaLabel,
  ariaPressed,
  className = "",
  disabled = false,
  hoverCardAccentColor = "var(--hovercard-accent-warning)",
  hoverCardContent = null,
  hoverCardTitle = "",
  icon,
  label,
  onClick,
  topContent = null,
  trailingContent = null,
  tone = "subtle",
}) {
  const noPlanningRightTitle = React.useContext(SegmentPlanningRightContext);
  // Une pastille déjà inactive (« Livrée ») garde sa propre bulle.
  const blockedByRight = !disabled && Boolean(noPlanningRightTitle);
  const buttonNode = (
    <ActionButton
      size="sm"
      tone={tone}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel || label}
      aria-pressed={ariaPressed}
      className={joinClasses([
        topContent
          ? "min-h-0! border! px-2.25! py-[0.32rem]! text-[0.64rem]! font-semibold! shadow-none!"
          : "min-h-7.5 border px-2.5 py-1.25 text-[0.68rem] font-semibold shadow-none [&>span]:flex [&>span]:h-3.5 [&>span]:items-center",
        className,
      ])}
      disabled={disabled || blockedByRight}
      onClick={onClick}
      title={blockedByRight ? noPlanningRightTitle : undefined}
    >
      <span className={joinClasses([
        "inline-flex leading-none",
        topContent ? "flex-col items-start gap-[0.18rem]" : "items-center gap-1.5 whitespace-nowrap",
      ])}>
        {topContent ? <span className="inline-flex">{topContent}</span> : null}
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap leading-none">
          {renderSegmentStatusIcon(icon, "h-3.5 w-3.5")}
          <span>{label}</span>
          {trailingContent}
        </span>
      </span>
    </ActionButton>
  );

  if (!hoverCardContent) {
    return buttonNode;
  }

  return (
    <HoverCard
      title={hoverCardTitle}
      content={hoverCardContent}
      placement="top"
      delay={120}
      accentColor={hoverCardAccentColor}
      maxWidth={260}
      referenceAsChild
    >
      <span className="inline-flex">{buttonNode}</span>
    </HoverCard>
  );
}

const MANUAL_BUFFER_UPDATED_AT_FORMATTER = new Intl.DateTimeFormat("fr-CA", {
  dateStyle: "short",
  timeStyle: "short",
});

const SEGMENT_ORDER_SAVE_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Modification commande",
  title: "Confirmer l'enregistrement de la commande ?",
  description: "La commande liée sera mise à jour avec les valeurs modifiées depuis ce segment.",
  impactItems: [
    "La commande est enregistrée comme depuis la table Commandes, puis son camion est recalculé.",
  ],
  confirmLabel: "Enregistrer maintenant",
};

const SEGMENT_PRELOAD_ASSIGNMENT_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Preload",
  title: "Confirmer le Preload ?",
  description: "Chargé la veille passera à Oui et le camion courant sera reconstruit localement.",
  impactItems: [
    "Aucune optimisation globale ne sera relancée.",
    "Le dispatch ne sera pas marqué à revalider si la reconstruction locale réussit.",
  ],
  confirmLabel: "Activer Preload",
};

const SEGMENT_PRELOAD_REMOVAL_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Preload",
  title: "Retirer le Preload ?",
  description: "Chargé la veille repassera à Non et le camion courant sera reconstruit localement.",
  impactItems: [
    "Le stock initial disparaîtra du début logique de la tournée pour cette portée.",
  ],
  confirmLabel: "Retirer Preload",
};

const SEGMENT_END_OF_DAY_LOAD_ASSIGNMENT_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Chargé en PM",
  title: "Confirmer Chargé en PM ?",
  description: "Le chargement de fin de journée sera ajouté à la portée demandée.",
  impactItems: [
    "La livraison sera reportée à la date cible sélectionnée.",
    "La tournée qui livre le Chargé PM sera recalculée.",
  ],
  confirmLabel: "Assigner PM",
};

const SEGMENT_END_OF_DAY_LOAD_EDIT_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Chargé en PM",
  title: "Modifier le PM ?",
  description: "Le mode ou la date cible du Chargé en PM seront mis à jour.",
  impactItems: [
    "Si le mode ou la date change, l'ancienne cible est retirée.",
    "La tournée qui livre le Chargé PM sera recalculée.",
  ],
  confirmLabel: "Modifier",
};

const SEGMENT_END_OF_DAY_LOAD_REMOVAL_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Chargé en PM",
  title: "Retirer le PM ?",
  description: "La pastille Chargé en PM et le verrou source associé seront retirés.",
  impactItems: [
    "Le camion concerné sera reconstruit après le retrait.",
  ],
  confirmLabel: "Retirer PM",
};

const SEGMENT_EVENING_STANDARD_TRANSFER_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Soir standard",
  title: "Transférer vers Soir ?",
  description: "La commande deviendra une livraison standard de soir pour la date courante.",
  impactItems: [
    "Le camion source jour sera reconstruit après le retrait.",
    "Le camion cible soir sera reconstruit sans créer de Preload ni de Chargé PM.",
  ],
  confirmLabel: "Transférer soir",
};

const SEGMENT_DAY_STANDARD_TRANSFER_CONFIRMATION = {
  ...DEFAULT_OPTIMIZE_CONFIRMATION,
  eyebrow: "Jour standard",
  title: "Transférer vers Jour ?",
  description: "La commande redeviendra une livraison standard de jour pour la date courante.",
  impactItems: [
    "Le camion source soir sera reconstruit après le retrait.",
    "Le camion cible jour sera reconstruit sans créer de Preload ni de Chargé PM.",
  ],
  confirmLabel: "Transférer jour",
};

function resolveSegmentManualBufferEditorState(segment = null) {
  return {
    minutes: Number(segment?.manualBufferMinutes) > 0 ? String(Number(segment.manualBufferMinutes)) : "",
    note: typeof segment?.manualBufferNote === "string" ? segment.manualBufferNote : "",
    reason: typeof segment?.manualBufferReason === "string" ? segment.manualBufferReason : "",
  };
}

function resolveSegmentEditableOrder(segment = null) {
  const editableOrder = segment?.businessStep?.orderDetails?.editableOrder;
  return editableOrder && typeof editableOrder === "object" ? editableOrder : null;
}

function resolveSegmentPreloadedPreviousDayOrderId(segment = null) {
  const editableOrder = resolveSegmentEditableOrder(segment);
  return normalizeSegmentMetaValue(
    editableOrder?.order_id
    || segment?.operationOrderId
    || segment?.orderLabel
    || segment?.to?.orderLabel
  );
}

function buildPreloadedPreviousDayCommandOption(segment = null, index = 0) {
  const orderDetails = segment?.businessStep?.orderDetails || {};
  const editableOrder = resolveSegmentEditableOrder(segment);
  const orderId = resolveSegmentPreloadedPreviousDayOrderId(segment);
  const deliveryClientName = normalizeSegmentMetaValue(
    orderDetails?.deliveryClientName
    || editableOrder?.delivery_client_name
    || editableOrder?.client_name
    || segment?.operationOwnerLabel
    || segment?.clientName
    || segment?.client_name
  );
  const loadingClientName = normalizeSegmentMetaValue(
    orderDetails?.loadingClientName
    || editableOrder?.loading_client_name
    || segment?.loading_client_name
  );
  const deliveryAddress = normalizeSegmentMetaValue(
    orderDetails?.deliveryAddress
    || editableOrder?.delivery_address
    || editableOrder?.address
    || resolveSegmentDestinationAddress(segment)
  );
  const fallbackTitle = normalizeSegmentMetaValue(resolveSegmentPrimaryTitle(segment));
  const title = deliveryClientName || fallbackTitle || orderId || `Commande ${index + 1}`;
  const secondaryParts = [
    orderId ? `Cmd ${orderId}` : "",
    loadingClientName && loadingClientName.toLowerCase() !== title.toLowerCase()
      ? `Chargement ${loadingClientName}`
      : "",
  ].filter(Boolean);

  return {
    address: deliveryAddress,
    key: orderId || `${title}:${index}`,
    orderId,
    secondaryLabel: secondaryParts.join(" · "),
    title,
  };
}

function resolveSegmentEndOfDayLoadDeliveryIds(segment = null, row = null) {
  const deliveryIds = [];
  const appendDeliveryId = (value) => {
    const normalizedValue = normalizeSegmentMetaValue(value);
    if (normalizedValue && !deliveryIds.includes(normalizedValue)) {
      deliveryIds.push(normalizedValue);
    }
  };

  if (Array.isArray(segment?.endOfDayLoadDeliveryIds)) {
    segment.endOfDayLoadDeliveryIds.forEach(appendDeliveryId);
  }
  appendDeliveryId(segment?.operationDeliveryId);
  appendDeliveryId(segment?.deliveryId || segment?.delivery_id);
  appendDeliveryId(segment?.businessStep?.deliveryId);
  appendDeliveryId(segment?.segmentReorder?.deliveryId);

  const editableOrder = resolveSegmentEditableOrder(segment);
  if (Array.isArray(editableOrder?.deliveries)) {
    editableOrder.deliveries.forEach((delivery) => {
      appendDeliveryId(delivery?.delivery_id || delivery?.deliveryId || delivery?.id);
    });
  }
  appendDeliveryId(editableOrder?.delivery_id || editableOrder?.deliveryId);

  if (!deliveryIds.length) {
    const operationOrderId = normalizeSegmentMetaValue(
      segment?.operationOrderId
      || editableOrder?.order_id
      || segment?.businessStep?.orderDetails?.editableOrder?.order_id,
    );
    if (operationOrderId && Array.isArray(row?.segments)) {
      for (const candidate of row.segments) {
        if (!candidate || candidate === segment) {
          continue;
        }
        if (normalizeSegmentMetaValue(candidate?.operationOrderId) !== operationOrderId) {
          continue;
        }
        appendDeliveryId(candidate?.operationDeliveryId);
        appendDeliveryId(candidate?.deliveryId || candidate?.delivery_id);
        appendDeliveryId(candidate?.businessStep?.deliveryId);
        appendDeliveryId(candidate?.segmentReorder?.deliveryId);
      }
    }
  }

  return deliveryIds;
}

function buildSegmentOrderValidationLookup(editableOrder = null) {
  const loadingClientName = typeof editableOrder?.loading_client_name === "string"
    ? editableOrder.loading_client_name.trim()
    : "";

  return buildLoadingClientCorrespondenceIndex(loadingClientName ? [{
    client_name: loadingClientName,
    correspondence_name: loadingClientName,
  }] : []).byLookupKey;
}

function resolveSegmentOrderTextLabel(value, emptyLabel = "Vide") {
  const resolvedValue = typeof value === "string" ? value.trim() : String(value || "").trim();
  return resolvedValue || emptyLabel;
}

function resolveSegmentOrderOptionLabel(options = [], value = "", emptyLabel = "Vide") {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  const match = Array.isArray(options)
    ? options.find((option) => option?.value === normalizedValue)
    : null;
  return typeof match?.label === "string" && match.label.trim() ? match.label.trim() : emptyLabel;
}

function resolveSegmentOrderWindowLabel(windowValue = "", priorityValue = "", emptyLabel = "Vide") {
  const summaryParts = [
    typeof windowValue === "string" ? windowValue.trim() : "",
    typeof priorityValue === "string" ? priorityValue.trim() : "",
  ].filter(Boolean);

  return summaryParts.join(" · ") || emptyLabel;
}

function resolveSegmentOrderQuantityLabel(value) {
  const formattedValue = formatQuantityTons(value);
  return formattedValue && formattedValue !== "—" ? `${formattedValue} t` : "Vide";
}

function resolveSegmentOrderFieldMessages(errors = {}, fieldKeys = []) {
  return Array.from(new Set(
    (Array.isArray(fieldKeys) ? fieldKeys : [])
      .map((fieldKey) => errors?.[fieldKey])
      .filter((message) => typeof message === "string" && message.trim()),
  ));
}

function SegmentActionEditorButton({
  hasError = false,
  isActive = false,
  onClick = null,
  title = "",
  valueLabel = "",
}) {
  const noPlanningRightTitle = React.useContext(SegmentPlanningRightContext);
  const blockedByRight = typeof onClick === "function" && Boolean(noPlanningRightTitle);
  const isInteractive = typeof onClick === "function" && !blockedByRight;

  return (
    <button
      type="button"
      aria-pressed={isInteractive ? isActive : undefined}
      className={joinClasses([
        "flex h-7.5 min-w-0 items-center gap-1 rounded-[11px] border px-1.5 py-0.75 text-left shadow-inset-highlight transition",
        blockedByRight ? "cursor-not-allowed opacity-45" : isInteractive ? "cursor-pointer" : "cursor-default",
        isActive
          ? "border-[rgba(100,116,139,0.26)] bg-[rgba(241,245,249,0.96)] ring-1 ring-[rgba(148,163,184,0.16)]"
          : "border-[rgba(148,163,184,0.22)] bg-[rgba(248,250,252,0.84)] hover:border-[rgba(148,163,184,0.3)] hover:bg-white/96",
      ])}
      disabled={!isInteractive}
      onClick={isInteractive ? onClick : undefined}
      title={blockedByRight ? noPlanningRightTitle : undefined}
    >
      <span className="inline-flex h-full shrink-0 items-center text-[0.62rem] font-extrabold leading-none uppercase tracking-widest text-content-copy">
        {title}:
      </span>
      <span className={joinClasses([
        "inline-flex h-full min-w-0 flex-1 items-center truncate text-[0.62rem] font-medium leading-none",
        hasError ? "text-[rgb(154,52,18)]" : "text-text",
      ])}>
        {valueLabel || "Vide"}
      </span>
    </button>
  );
}

function SegmentActionEditorPanel({ children = null, errorMessages = [], onKeyDownCapture = null, className = "" }) {
  return (
    <div
      className={joinClasses(["grid gap-2 rounded-[16px] border border-[rgba(15,23,42,0.1)] bg-white/90 px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.76)]", className])}
      onKeyDownCapture={onKeyDownCapture}
    >
      {children}
      {errorMessages.length ? (
        <div className="grid gap-1">
          {errorMessages.map((message) => (
            <p key={message} className="m-0 text-[0.72rem] leading-5 text-[rgb(154,52,18)]">{message}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EndOfDayLoadDeliveryModeField({ value = "next_day", onChange = null }) {
  return (
    <div className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
      <span>Mode de livraison</span>
      <div className="grid grid-cols-2 gap-1 rounded-[4px] border border-[rgba(148,163,184,0.22)] bg-white p-1 shadow-inset-highlight">
        {[
          { icon: "sun", label: "Livrer demain", value: "next_day" },
          { icon: "moon", label: "Livrer soir", value: "evening" },
        ].map((option) => {
          const isActive = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={[
                "inline-flex min-h-8 min-w-0 items-center justify-center gap-1 rounded-[4px] border px-2 py-1 text-[0.7rem] font-extrabold transition-colors duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
                isActive ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME : SEGMENT_BUTTON_NEUTRAL_CLASS_NAME,
              ].join(" ")}
              aria-pressed={isActive}
              onClick={() => onChange?.(option.value)}
            >
              {renderSegmentStatusIcon(option.icon, "h-3.5 w-3.5")}
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EndOfDayLoadEditorActions({ blockedMessage = "", isSubmitDisabled = false, onClose = null, onSubmit = null, submitLabel = "Assigner" }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tooltip title="Chargé PM indisponible" content={blockedMessage} placement="top" disabled={!isSubmitDisabled}>
        <span className={joinClasses(["inline-flex", isSubmitDisabled ? "cursor-not-allowed" : ""])}>
          <ActionButton
            className={joinClasses([
              "h-9 rounded-[4px] px-3 text-[0.72rem] font-semibold",
              isSubmitDisabled ? SEGMENT_BUTTON_DISABLED_CLASS_NAME : SEGMENT_BUTTON_ACTIVE_CLASS_NAME,
              isSubmitDisabled ? "pointer-events-none" : "",
            ])}
            disabled={isSubmitDisabled}
            onClick={onSubmit}
          >
            {submitLabel}
          </ActionButton>
        </span>
      </Tooltip>
      <ActionButton className={joinClasses(["h-9 rounded-[4px] px-3 text-[0.72rem] font-semibold", SEGMENT_BUTTON_NEUTRAL_CLASS_NAME])} onClick={onClose}>
        Fermer
      </ActionButton>
    </div>
  );
}

function formatSegmentManualBufferUpdatedAt(value = "") {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return MANUAL_BUFFER_UPDATED_AT_FORMATTER.format(parsedDate);
}

function formatRouteAlternativeSignedDuration(minutes) {
  const normalizedMinutes = Number(minutes);
  if (!Number.isFinite(normalizedMinutes) || Math.abs(normalizedMinutes) < 0.5) {
    return "0 min";
  }
  const prefix = normalizedMinutes > 0 ? "+" : "-";
  return `${prefix}${formatDurationShort(Math.abs(normalizedMinutes)) || `${Math.round(Math.abs(normalizedMinutes))} min`}`;
}

function formatRouteAlternativeSignedDistance(meters) {
  const normalizedMeters = Number(meters);
  if (!Number.isFinite(normalizedMeters) || Math.abs(normalizedMeters) < 1) {
    return "0 km";
  }
  const prefix = normalizedMeters > 0 ? "+" : "-";
  return `${prefix}${formatTruckTotalHoverDistance(Math.abs(normalizedMeters) / 1000) || `${Math.round(Math.abs(normalizedMeters) / 1000)} km`}`;
}

function normalizeEndOfDayLoadDeliveryMode(value = "") {
  const normalizedValue = normalizeSegmentMetaValue(value).toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" || normalizedValue === "livrer_soir"
    ? "evening"
    : "next_day";
}

function normalizeExplicitEndOfDayLoadDeliveryMode(value = "") {
  const normalizedValue = normalizeSegmentMetaValue(value).toLowerCase();
  if (normalizedValue === "evening" || normalizedValue === "soir" || normalizedValue === "livrer_soir") {
    return "evening";
  }
  if (
    normalizedValue === "next_day"
    || normalizedValue === "tomorrow"
    || normalizedValue === "demain"
    || normalizedValue === "day"
    || normalizedValue === "jour"
    || normalizedValue === "livrer_demain"
  ) {
    return "next_day";
  }
  return "";
}

function resolveEndOfDayLoadTargetPeriod(deliveryMode = "next_day") {
  return normalizeEndOfDayLoadDeliveryMode(deliveryMode) === "evening" ? "evening" : "day";
}

function resolveEndOfDayLoadDeliveryModeFromSegment(segment = null, fallbackSourceDispatchDate = "") {
  const explicitMode = normalizeExplicitEndOfDayLoadDeliveryMode(segment?.endOfDayLoadDeliveryMode || segment?.end_of_day_load_delivery_mode);
  if (explicitMode) {
    return explicitMode;
  }

  const targetPeriod = normalizeExplicitEndOfDayLoadDeliveryMode(segment?.endOfDayLoadTargetPeriod || segment?.end_of_day_load_target_period);
  if (targetPeriod) {
    return targetPeriod;
  }

  const sourceDispatchDate = normalizeSegmentMetaValue(segment?.endOfDayLoadSourceDispatchDate || segment?.end_of_day_load_source_dispatch_date)
    || normalizeSegmentMetaValue(fallbackSourceDispatchDate);
  const targetDispatchDate = normalizeSegmentMetaValue(segment?.endOfDayLoadTargetDispatchDate || segment?.end_of_day_load_target_dispatch_date);
  if (sourceDispatchDate && targetDispatchDate && sourceDispatchDate === targetDispatchDate) {
    return "evening";
  }

  return "next_day";
}

function resolveEndOfDayLoadTargetDispatchDateForMode(sourceDispatchDate = "", deliveryMode = "next_day", currentTargetDispatchDate = "") {
  const safeSourceDispatchDate = normalizeSegmentMetaValue(sourceDispatchDate);
  if (normalizeEndOfDayLoadDeliveryMode(deliveryMode) === "evening") {
    return safeSourceDispatchDate;
  }

  const safeCurrentTargetDispatchDate = normalizeSegmentMetaValue(currentTargetDispatchDate);
  return safeCurrentTargetDispatchDate && safeCurrentTargetDispatchDate !== safeSourceDispatchDate
    ? safeCurrentTargetDispatchDate
    : resolveNextDispatchDateValue(safeSourceDispatchDate);
}

const SEGMENT_RELEASE_ACTION_BUTTON_CLASS_NAME = "border-border-warning-subtle! bg-amber-soft! text-status-warning-ink! hover:border-border-warning-muted! hover:bg-[rgba(255,244,226,0.96)]! hover:text-status-warning-ink!";

const SEGMENT_REORDERED_RELEASE_ACTION_BUTTON_CLASS_NAME = "border-[rgba(109,40,217,0.2)]! bg-[rgba(245,243,255,0.98)]! text-[rgb(109,40,217)]! hover:border-[rgba(109,40,217,0.32)]! hover:bg-white! hover:text-[rgb(91,33,182)]!";

function resolveSegmentReleaseActionPresentation(manualStatus = null) {
  const isReorderedProtection = resolveProtectionVariant(manualStatus) === PROTECTION_VARIANT_REORDERED;

  return {
    className: isReorderedProtection
      ? SEGMENT_REORDERED_RELEASE_ACTION_BUTTON_CLASS_NAME
      : SEGMENT_RELEASE_ACTION_BUTTON_CLASS_NAME,
    hoverCardAccentColor: isReorderedProtection
      ? REORDERED_PROTECTION_ACCENT_COLOR
      : "var(--hovercard-accent-warning)",
  };
}

function SegmentActionPanel({ assignmentInteractions = {}, className = "", dispatchDate = "", dispatchPeriod = "day", row, segment: sourceSegment, truckReassignmentTargets = [] }) {
  const segment = React.useMemo(() => resolvePmSegmentContext(sourceSegment), [sourceSegment]);
  const businessStep = segment?.businessStep || null;
  const groupActionTarget = segment?.isGroupActionTarget === true && segment?.groupActionTarget
    ? segment.groupActionTarget
    : null;
  const isGroupActionMode = Boolean(groupActionTarget);
  const groupMemberSegments = React.useMemo(() => (
    Array.isArray(groupActionTarget?.memberSegments)
      ? groupActionTarget.memberSegments.filter(Boolean)
      : []
  ), [groupActionTarget?.memberSegments]);
  const groupDeliveryIds = React.useMemo(() => (
    Array.isArray(groupActionTarget?.deliveryIds)
      ? Array.from(new Set(groupActionTarget.deliveryIds.filter(Boolean)))
      : []
  ), [groupActionTarget?.deliveryIds]);
  const groupStepIds = React.useMemo(() => (
    Array.isArray(groupActionTarget?.groupStepIds)
      ? Array.from(new Set(groupActionTarget.groupStepIds.filter(Boolean)))
      : []
  ), [groupActionTarget?.groupStepIds]);
  const groupMutationPayload = React.useMemo(() => ({
    deliveryIds: groupDeliveryIds,
    groupStepIds,
    sourceGroupKey: normalizeSegmentMetaValue(groupActionTarget?.sourceGroupKey),
    dispatchPeriod,
  }), [groupActionTarget?.sourceGroupKey, groupDeliveryIds, groupStepIds, dispatchPeriod]);
  const mergedMemberSegments = React.useMemo(() => {
    return Array.isArray(segment?.mergedMemberSegments) ? segment.mergedMemberSegments.filter(Boolean) : [];
  }, [segment?.mergedMemberSegments]);
  const orderMemberSegments = isGroupActionMode
    ? groupMemberSegments
    : isMergedOperationalMissionSegment(segment)
      ? mergedMemberSegments
      : [segment];
  const ordersHoverIcon = <GroupedSegmentOrdersHoverIcon memberSegments={orderMemberSegments} allowSingleOrder />;
  const feedbackLines = buildSegmentActionFeedbackLines(segment);
  const segmentMetaLabel = resolveSegmentActionMetaLabel(segment);
  const approvalBlockingReason = getSegmentApprovalBlockingReason(segment);
  const confirmButtonDisabledReason = normalizeSegmentMetaValue(businessStep?.confirmButton?.disabledReason) || approvalBlockingReason;
  const dispatcherStatus = resolveSegmentDispatcherStatus(businessStep);
  const dispatcherStatusCode = normalizeSegmentMetaValue(dispatcherStatus?.code);
  const dispatcherStatusDescription = normalizeSegmentMetaValue(dispatcherStatus?.description);
  const groupDispatcherStatusCodes = groupMemberSegments.map((candidate) => (
    normalizeSegmentMetaValue(resolveSegmentDispatcherStatus(candidate?.businessStep)?.code)
  ));
  const groupAllFrozen = isGroupActionMode
    && groupDispatcherStatusCodes.length > 0
    && groupDispatcherStatusCodes.every((code) => code === "frozen");
  const groupHasImplicitFrozenMember = isGroupActionMode && groupDispatcherStatusCodes.some((code) => (
    isImplicitFrozenDispatcherStatusCode(code)
  ));
  const groupTailGanttDispatcherActionButton = isGroupActionMode
    ? (groupMemberSegments.find((candidate) => (
      candidate?.businessStep?.sharedLoadingFreezeChain?.isTail === true
    ))?.businessStep?.ganttDispatcherActionButton || null)
    : null;
  const groupIsNonTailSharedLoadingMember = isGroupActionMode
    && groupMemberSegments.some((candidate) => candidate?.businessStep?.sharedLoadingFreezeChain?.isMember === true)
    && !groupTailGanttDispatcherActionButton;
  const ganttDispatcherActionButton = isGroupActionMode
    ? groupTailGanttDispatcherActionButton
    : (businessStep?.ganttDispatcherActionButton || null);
  const groupHasFrozenOrDeliveredMember = isGroupActionMode && groupDispatcherStatusCodes.some((code) => (
    isFrozenDispatcherStatusCode(code) || code === "delivered"
  ));
  const groupAllProtected = isGroupActionMode
    && groupMemberSegments.length > 0
    && groupMemberSegments.every((candidate) => candidate?.businessStep?.confirmButton?.isActive === true);
  const groupAllManualReleaseLock = isGroupActionMode
    && groupMemberSegments.length > 0
    && groupMemberSegments.every((candidate) => {
      const manualStatusCode = candidate?.businessStep?.manualStatus?.code;
      return (manualStatusCode === "reassigned" || manualStatusCode === "reordered")
        && Boolean(candidate?.businessStep?.releaseButton?.deliveryId);
    });
  const groupManualReleaseIsReordered = groupAllManualReleaseLock
    && groupMemberSegments.every((candidate) => (
      candidate?.businessStep?.manualStatus?.code === "reordered"
    ));
  const groupScopeIsComplete = isGroupActionMode
    && groupDeliveryIds.length >= 2
    && groupDeliveryIds.length === Number(groupActionTarget?.orderIds?.length || 0)
    && groupStepIds.length >= groupDeliveryIds.length;
  const shouldHideDispatcherActions = isGroupActionMode
    ? groupHasFrozenOrDeliveredMember
    : isFrozenDispatcherStatusCode(dispatcherStatusCode)
      || dispatcherStatusCode === "delivered";
  const dispatcherStatusTrailingContent = dispatcherStatus
    ? (
      <span
        className={joinClasses([
          "inline-flex items-center rounded-full border px-1.25 py-px text-[0.5rem] font-bold leading-none",
          SEGMENT_STATUS_PILL_CLASS_NAMES[dispatcherStatus.tone] || SEGMENT_STATUS_PILL_CLASS_NAMES.info,
        ])}
      >
        {dispatcherStatus.label}
      </span>
    )
    : null;
  const dispatcherStatusHoverCardContent = dispatcherStatusDescription
    ? (
      <div className="grid gap-1.25">
        <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">{dispatcherStatus?.label || "Statut d'exécution"}</div>
        <div className="text-[0.68rem] leading-normal text-content-muted">{dispatcherStatusDescription}</div>
      </div>
    )
    : null;
  const manualBufferEditable = Boolean(segment?.isManualBufferEditable && segment?.manualBufferStepKey);
  const editableOrder = resolveSegmentEditableOrder(segment);
  const isLoadingStep = businessStep?.stepType === "loading";
  const isDeliveryStep = businessStep?.stepType === "delivery";
  const isEndOfDayLoadManagedSegment = Boolean(
    segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload,
  );
  const endOfDayLoadMarkerKey = normalizeSegmentMetaValue(segment?.endOfDayLoadMarkerKey);
  const isExistingEndOfDayLoad = Boolean(
    isEndOfDayLoadManagedSegment
    && endOfDayLoadMarkerKey,
  );
  const ganttClickedEndOfDayLoadSourceTruckId = normalizeSegmentMetaValue(
    row?.assignmentId
    || row?.assignment?.truck_id
    || segment?.assignmentId,
  );
  const persistedEndOfDayLoadSourceTruckId = normalizeSegmentMetaValue(
    segment?.endOfDayLoadSourceTruckId
    || segment?.end_of_day_load_source_truck_id,
  );
  // Comme au Data Manager : le camion qui porte déjà la commande la charge en PM, et sa remorque la livre.
  // Segment du soir : la commande revient d'abord au jour sur la même remorque.
  const isEveningEndOfDayLoadSource = dispatchPeriod === "evening" && !isExistingEndOfDayLoad;
  const endOfDayLoadSourceTruckId = normalizeSegmentMetaValue(
    (isExistingEndOfDayLoad ? persistedEndOfDayLoadSourceTruckId : "")
    || ganttClickedEndOfDayLoadSourceTruckId,
  );
  const defaultEndOfDayLoadDeliveryMode = isEveningEndOfDayLoadSource
    ? "evening"
    : resolveEndOfDayLoadDeliveryModeFromSegment(segment, dispatchDate);
  const defaultEndOfDayLoadSourceTruckLabel = normalizeSegmentMetaValue(
    (isExistingEndOfDayLoad
      ? (
        segment?.endOfDayLoadSourceTruckName
        || segment?.end_of_day_load_source_truck_name
      )
      : "")
    || (endOfDayLoadSourceTruckId === ganttClickedEndOfDayLoadSourceTruckId
      ? (row?.truckLabel || row?.assignment?.truck_name)
      : ""),
  );
  const isPreloadedPreviousDayActionOnly = Boolean(
    !isEndOfDayLoadManagedSegment
    && (
      segment?.isInitialPreload
      || segment?.isPreloadedPreviousDay
      || segment?.is_preloaded_previous_day
    ),
  );
  const editableOrderValidationLookup = React.useMemo(
    () => buildSegmentOrderValidationLookup(editableOrder),
    [editableOrder?.loading_client_name],
  );
  const [{ minutes: manualBufferMinutesInput, note: manualBufferNoteInput, reason: manualBufferReasonInput }, setManualBufferEditorState] = React.useState(
    () => resolveSegmentManualBufferEditorState(segment),
  );
  const [isManualBufferExpanded, setIsManualBufferExpanded] = React.useState(false);
  const [isRouteAlternativeExpanded, setIsRouteAlternativeExpanded] = React.useState(false);
  const [routeAlternativeViewState, setRouteAlternativeViewState] = React.useState({
    errorMessage: "",
    isLoading: false,
    payload: null,
  });
  const [routeAlternativeSelectedMode, setRouteAlternativeSelectedMode] = React.useState(
    () => (segment?.routeAlternativeApplied ? "alternative" : "current"),
  );
  const [openOrderEditorKey, setOpenOrderEditorKey] = React.useState("");
  const [orderEditorDraft, setOrderEditorDraft] = React.useState(() => createEditableOrderDraft(editableOrder));
  const [saveConfirmationDialog, setSaveConfirmationDialog] = React.useState(null);
  const [endOfDayLoadRemovalDialog, setEndOfDayLoadRemovalDialog] = React.useState(null);
  const [isEndOfDayLoadEditorOpen, setIsEndOfDayLoadEditorOpen] = React.useState(false);
  const [endOfDayLoadSourceDispatchDate, setEndOfDayLoadSourceDispatchDate] = React.useState(() => (
    normalizeEndOfDayLoadDateValue(
      segment?.endOfDayLoadSourceDispatchDate
      || segment?.end_of_day_load_source_dispatch_date
      || dispatchDate,
    )
  ));
  const [endOfDayLoadTargetDispatchDate, setEndOfDayLoadTargetDispatchDate] = React.useState("");
  const endOfDayLoadDayTargetDispatchDateRef = React.useRef("");
  const [endOfDayLoadDeliveryMode, setEndOfDayLoadDeliveryMode] = React.useState(defaultEndOfDayLoadDeliveryMode);
  const [isTransferActionMenuOpen, setIsTransferActionMenuOpen] = React.useState(false);
  const [isEveningStandardEditorOpen, setIsEveningStandardEditorOpen] = React.useState(false);
  const [eveningStandardTargetTruckId, setEveningStandardTargetTruckId] = React.useState("");
  const [eveningStandardTargetsState, setEveningStandardTargetsState] = React.useState({
    errorMessage: "",
    isLoading: false,
    options: [],
  });
  const [isPreloadedPreviousDayEditorOpen, setIsPreloadedPreviousDayEditorOpen] = React.useState(false);
  const [preloadedPreviousDayScope, setPreloadedPreviousDayScope] = React.useState("order");
  const [preloadedPreviousDayEditorMode, setPreloadedPreviousDayEditorMode] = React.useState("assign");
  const actionButtons = [];
  let transferActionButton = null;
  let confirmButtonElement = null;
  const releaseActionPresentation = resolveSegmentReleaseActionPresentation(businessStep?.manualStatus);

  React.useEffect(() => {
    setManualBufferEditorState(resolveSegmentManualBufferEditorState(segment));
  }, [
    segment?.manualBufferMinutes,
    segment?.manualBufferNote,
    segment?.manualBufferReason,
    segment?.manualBufferStepKey,
  ]);

  React.useEffect(() => {
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setEveningStandardTargetTruckId("");
    setEveningStandardTargetsState({ errorMessage: "", isLoading: false, options: [] });
    setIsPreloadedPreviousDayEditorOpen(false);
    setPreloadedPreviousDayScope("order");
    setPreloadedPreviousDayEditorMode("assign");
    setEndOfDayLoadRemovalDialog(null);
    setEndOfDayLoadDeliveryMode(defaultEndOfDayLoadDeliveryMode);
    setEndOfDayLoadSourceDispatchDate(normalizeEndOfDayLoadDateValue(
      segment?.endOfDayLoadSourceDispatchDate
      || segment?.end_of_day_load_source_dispatch_date
      || dispatchDate,
    ));
    setEndOfDayLoadTargetDispatchDate("");
    endOfDayLoadDayTargetDispatchDateRef.current = "";
    setRouteAlternativeSelectedMode(segment?.routeAlternativeApplied ? "alternative" : "current");
    setRouteAlternativeViewState({ errorMessage: "", isLoading: false, payload: null });
  }, [
    dispatchDate,
    defaultEndOfDayLoadDeliveryMode,
    isGroupActionMode,
    segment?.endOfDayLoadDeliveryMode,
    segment?.endOfDayLoadSourceDispatchDate,
    segment?.endOfDayLoadSourceTruckId,
    segment?.end_of_day_load_source_dispatch_date,
    segment?.end_of_day_load_source_truck_id,
    segment?.endOfDayLoadTargetDispatchDate,
    segment?.endOfDayLoadTargetPeriod,
    segment?.manualBufferStepKey,
    segment?.routeAlternativeApplied,
    segment?.routeAlternativeId,
    segment?.routeSegmentIndex,
    segment?.tripId,
    row?.assignment?.truck_id,
    row?.assignmentId,
  ]);

  React.useEffect(() => {
    setOrderEditorDraft(createEditableOrderDraft(editableOrder));
  }, [editableOrder?.order_id, editableOrder?.updated_at, businessStep?.stepType, businessStep?.deliveryId]);

  React.useEffect(() => {
    setOpenOrderEditorKey("");
  }, [editableOrder?.order_id, editableOrder?.updated_at, businessStep?.stepType, businessStep?.deliveryId, isGroupActionMode]);

  React.useEffect(() => {
    setSaveConfirmationDialog(null);
  }, [editableOrder?.order_id, editableOrder?.updated_at, businessStep?.stepType, businessStep?.deliveryId, isGroupActionMode, segment?.manualBufferStepKey]);

  const endOfDayLoadGroupSegments = React.useMemo(() => {
    const sameSiteGroupKey = normalizeSegmentMetaValue(segment?.sameSiteGroupKey);
    if (!sameSiteGroupKey) {
      return [];
    }

    return (Array.isArray(row?.segments) ? row.segments : [])
      .filter((candidate) => (
        normalizeSegmentMetaValue(candidate?.sameSiteGroupKey) === sameSiteGroupKey
        && resolveSegmentEndOfDayLoadDeliveryIds(candidate, row).length > 0
      ));
  }, [row?.segments, segment?.sameSiteGroupKey]);
  const endOfDayLoadSourceProductLabel = React.useMemo(
    () => resolveEndOfDayLoadSourceProductLabel({
      editableOrder,
      groupSegments: endOfDayLoadGroupSegments,
      segment,
    }),
    [editableOrder, endOfDayLoadGroupSegments, segment],
  );
  const endOfDayLoadConstraintSegments = endOfDayLoadGroupSegments.length ? endOfDayLoadGroupSegments : [segment].filter(Boolean);
  const endOfDayLoadRequiresAspirator = endOfDayLoadConstraintSegments.some((candidate) => candidate?.requiresAspirator === true);
  const endOfDayLoadRequiresOntario = endOfDayLoadConstraintSegments.some((candidate) => candidate?.requiresOntario === true);
  const endOfDayLoadFilterDeliveryIds = React.useMemo(() => {
    const sourceSegments = endOfDayLoadGroupSegments.length > 1
      ? endOfDayLoadGroupSegments
      : [segment];
    const deliveryIds = [];
    const seenDeliveryIds = new Set();
    for (const candidate of sourceSegments) {
      for (const deliveryId of resolveSegmentEndOfDayLoadDeliveryIds(candidate, row)) {
        if (!deliveryId || seenDeliveryIds.has(deliveryId)) {
          continue;
        }
        seenDeliveryIds.add(deliveryId);
        deliveryIds.push(deliveryId);
      }
    }
    return deliveryIds;
  }, [endOfDayLoadGroupSegments, row, segment]);
  const eveningStandardTargetOptions = eveningStandardTargetsState.options;
  const standardTransferTargetPeriod = dispatchPeriod === "evening" ? "day" : "evening";
  const standardTransferTargetLabel = standardTransferTargetPeriod === "day" ? "Jour" : "Soir";
  const standardTransferTargetOptions = eveningStandardTargetOptions;
  const eveningStandardTargetOption = resolveSelectableTransferTarget(standardTransferTargetOptions, eveningStandardTargetTruckId);
  const endOfDayLoadExistingSourceTruckId = normalizeSegmentMetaValue(
    segment?.endOfDayLoadSourceTruckId
    || segment?.end_of_day_load_source_truck_id,
  );
  const endOfDayLoadExistingTargetTruckId = normalizeSegmentMetaValue(
    segment?.endOfDayLoadTargetTruckId
    || segment?.end_of_day_load_target_truck_id,
  );
  const endOfDayLoadExistingSourceDispatchDate = normalizeSegmentMetaValue(
    segment?.endOfDayLoadSourceDispatchDate
    || segment?.end_of_day_load_source_dispatch_date,
  );
  const endOfDayLoadExistingTargetDispatchDate = normalizeSegmentMetaValue(
    segment?.endOfDayLoadTargetDispatchDate
    || segment?.end_of_day_load_target_dispatch_date,
  );
  const endOfDayLoadMarkerDispatchDate = normalizeSegmentMetaValue(
    segment?.endOfDayLoadMarkerDispatchDate
    || segment?.end_of_day_load_marker_dispatch_date
    || endOfDayLoadExistingSourceDispatchDate
    || dispatchDate,
  );
  const endOfDayLoadEditorSourceDispatchDate = normalizeEndOfDayLoadDateValue(endOfDayLoadSourceDispatchDate);
  const endOfDayLoadSourceTrucksState = useEndOfDayLoadSourceTruckOptions({
    dispatchDate: endOfDayLoadEditorSourceDispatchDate,
    dispatchPeriod: "day",
    enabled: isEndOfDayLoadEditorOpen,
  });
  const endOfDayLoadSourceTruckOptions = React.useMemo(() => filterEndOfDayLoadSourceTrucks(
    endOfDayLoadSourceTrucksState.options,
    { keepTruckId: endOfDayLoadSourceTruckId, sourceProductLabel: endOfDayLoadSourceProductLabel },
  ), [endOfDayLoadSourceProductLabel, endOfDayLoadSourceTruckId, endOfDayLoadSourceTrucksState.options]);
  const selectedEndOfDayLoadSourceTruckId = normalizeSegmentMetaValue(endOfDayLoadSourceTruckId);
  const selectedEndOfDayLoadSourceTruck = endOfDayLoadSourceTruckOptions.find(
    (target) => target.truckId === selectedEndOfDayLoadSourceTruckId,
  ) || null;
  const selectedEndOfDayLoadKnownSourceTruck = buildTransferTargetOptionsFromTruckReassignmentTargets(
    truckReassignmentTargets,
  ).find((target) => target.truckId === selectedEndOfDayLoadSourceTruckId) || null;
  const selectedEndOfDayLoadSourceTruckFallbackLabel = normalizeSegmentMetaValue(
    defaultEndOfDayLoadSourceTruckLabel
    || selectedEndOfDayLoadKnownSourceTruck?.truckName
    || selectedEndOfDayLoadSourceTruckId,
  );
  const endOfDayLoadSourceTruckIsUnavailable = Boolean(
    selectedEndOfDayLoadSourceTruckId
    && !endOfDayLoadSourceTrucksState.isLoading
    && !endOfDayLoadSourceTrucksState.errorMessage
    && endOfDayLoadSourceTrucksState.dispatchDate === endOfDayLoadEditorSourceDispatchDate
    && !selectedEndOfDayLoadSourceTruck,
  );
  const currentEndOfDayLoadDeliveryMode = resolveEndOfDayLoadDeliveryModeFromSegment(segment, dispatchDate);
  const currentEndOfDayLoadDisplayMode = isExistingEndOfDayLoad
    ? resolveEndOfDayLoadDisplayMode(segment)
    : currentEndOfDayLoadDeliveryMode;
  const currentEndOfDayLoadLabelOptions = resolveEndOfDayLoadLabelOptions(segment);
  const currentEndOfDayLoadIsEveningMode = currentEndOfDayLoadDeliveryMode === "evening";
  const currentEndOfDayLoadModeShortLabel = getEndOfDayLoadModeShortLabel(
    currentEndOfDayLoadDisplayMode,
    currentEndOfDayLoadLabelOptions,
  );
  const currentEndOfDayLoadModeLongLabel = getEndOfDayLoadModeLongLabel(
    currentEndOfDayLoadDisplayMode,
    currentEndOfDayLoadLabelOptions,
  );
  const currentEndOfDayLoadModeIconName = getEndOfDayLoadModeIconName(currentEndOfDayLoadDisplayMode);
  const currentEndOfDayLoadModeButtonClassName = SEGMENT_BUTTON_NEUTRAL_CLASS_NAME;
  const currentEndOfDayLoadModeButtonOpenClassName = SEGMENT_BUTTON_ACTIVE_CLASS_NAME;
  const currentEndOfDayLoadModeAccentColor = currentEndOfDayLoadIsEveningMode
    ? "rgba(15,23,42,0.38)"
    : "rgba(202,138,4,0.34)";
  const normalizedEndOfDayLoadDeliveryMode = normalizeEndOfDayLoadDeliveryMode(endOfDayLoadDeliveryMode);
  const endOfDayLoadTargetPeriod = resolveEndOfDayLoadTargetPeriod(normalizedEndOfDayLoadDeliveryMode);
  const isEndOfDayLoadEveningMode = normalizedEndOfDayLoadDeliveryMode === "evening";
  const normalizedEndOfDayLoadEditorSourceDispatchDate = normalizeEndOfDayLoadDateValue(endOfDayLoadEditorSourceDispatchDate);
  const normalizedEndOfDayLoadTargetDispatchDate = normalizeEndOfDayLoadDateValue(endOfDayLoadTargetDispatchDate);
  const endOfDayLoadTargetPolicyDispatchDate = isEndOfDayLoadEveningMode
    ? normalizedEndOfDayLoadEditorSourceDispatchDate
    : normalizedEndOfDayLoadTargetDispatchDate;
  const endOfDayLoadTargetUsesSourceDispatchState = endOfDayLoadTargetPeriod === "day"
    && endOfDayLoadTargetPolicyDispatchDate === normalizedEndOfDayLoadEditorSourceDispatchDate;
  const endOfDayLoadTargetTrucksState = useEndOfDayLoadSourceTruckOptions({
    allowMissingSnapshot: true,
    dispatchDate: endOfDayLoadTargetPolicyDispatchDate,
    dispatchPeriod: endOfDayLoadTargetPeriod,
    enabled: isEndOfDayLoadEditorOpen
      && Boolean(endOfDayLoadTargetPolicyDispatchDate)
      && !endOfDayLoadTargetUsesSourceDispatchState,
  });
  const endOfDayLoadTargetPolicyState = endOfDayLoadTargetUsesSourceDispatchState
    ? endOfDayLoadSourceTrucksState
    : endOfDayLoadTargetTrucksState;
  // La cible est la remorque source ; la ligne cliquée donne son nom tant que le dispatch source se charge.
  const endOfDayLoadSourceTrailer = selectedEndOfDayLoadSourceTruck
    || (endOfDayLoadSourceTruckId === ganttClickedEndOfDayLoadSourceTruckId && row?.assignment
      ? buildTransferTargetOption(row.assignment)
      : selectedEndOfDayLoadKnownSourceTruck || { truckId: endOfDayLoadSourceTruckId, truckName: selectedEndOfDayLoadSourceTruckFallbackLabel });
  const endOfDayLoadSourceFieldLabel = resolveEndOfDayLoadTrailerFieldLabel(endOfDayLoadSourceTrailer, "source");
  const endOfDayLoadSourceTrailerLabel = resolveEndOfDayLoadTrailerLabel(endOfDayLoadSourceTrailer);
  const endOfDayLoadTargetTrailerLabel = endOfDayLoadSourceTrailerLabel;
  const endOfDayLoadTargetFieldLabel = resolveEndOfDayLoadTrailerFieldLabel(endOfDayLoadSourceTrailer);
  const endOfDayLoadTargetTrailerMessage = resolveEndOfDayLoadTargetTrailerMessage({
    dispatchDate: endOfDayLoadTargetPolicyDispatchDate,
    dispatchPeriod: endOfDayLoadTargetPeriod,
    dispatchState: endOfDayLoadTargetPolicyState,
    truck: endOfDayLoadSourceTrailer,
  });
  const endOfDayLoadSourceDateValidationMessage = !normalizedEndOfDayLoadEditorSourceDispatchDate
    ? "Date source obligatoire."
    : "";
  const endOfDayLoadSourceOptionsBlockedMessage = isEndOfDayLoadEditorOpen
    && normalizedEndOfDayLoadEditorSourceDispatchDate
    && (
      endOfDayLoadSourceTrucksState.isLoading
      || endOfDayLoadSourceTrucksState.dispatchDate !== normalizedEndOfDayLoadEditorSourceDispatchDate
    )
    ? "Chargement des remorques du dispatch source..."
    : endOfDayLoadSourceTrucksState.errorMessage
      ? endOfDayLoadSourceTrucksState.errorMessage
      : normalizedEndOfDayLoadEditorSourceDispatchDate
        && endOfDayLoadSourceTrucksState.dispatchDate === normalizedEndOfDayLoadEditorSourceDispatchDate
        && !endOfDayLoadSourceTruckOptions.length
        ? "Aucune remorque active n'est disponible dans le dispatch source."
        : "";
  const endOfDayLoadSourceTruckValidationMessage = !selectedEndOfDayLoadSourceTruckId
    ? `${endOfDayLoadSourceFieldLabel} obligatoire.`
    : endOfDayLoadSourceTruckIsUnavailable
      ? `${endOfDayLoadSourceFieldLabel} indisponible pour cette date.`
      : "";
  const endOfDayLoadSourceChangesValidationMessage = (
    !endOfDayLoadSourceOptionsBlockedMessage
    && !endOfDayLoadSourceTruckValidationMessage
  )
    ? resolveEndOfDayLoadTruckSourceChangesMessage({
      dispatchDate: normalizedEndOfDayLoadEditorSourceDispatchDate,
      dispatchPeriod: "day",
      dispatchState: endOfDayLoadSourceTrucksState,
      role: "source",
      truck: endOfDayLoadSourceTrailer,
    })
    : "";
  const endOfDayLoadDateValidationMessage = !normalizedEndOfDayLoadTargetDispatchDate
    ? "Date cible obligatoire."
    : "";
  const selectedEndOfDayLoadTargetDispatchDate = isEndOfDayLoadEveningMode
    ? normalizedEndOfDayLoadEditorSourceDispatchDate
    : normalizedEndOfDayLoadTargetDispatchDate;
  const endOfDayLoadTargetSourceChangesValidationMessage = (
    !endOfDayLoadTargetTrailerMessage
    && selectedEndOfDayLoadSourceTruckId
    && selectedEndOfDayLoadTargetDispatchDate
  )
    ? resolveEndOfDayLoadTruckSourceChangesMessage({
      allowMissingSnapshot: true,
      dispatchDate: selectedEndOfDayLoadTargetDispatchDate,
      dispatchPeriod: endOfDayLoadTargetPeriod,
      dispatchState: endOfDayLoadTargetPolicyState,
      role: "cible",
      truck: endOfDayLoadSourceTrailer,
    })
    : "";
  const existingEndOfDayLoadTargetTruckIdForComparison = normalizeSegmentMetaValue(
    endOfDayLoadExistingTargetTruckId
    || row?.assignmentId
    || row?.assignment?.truck_id
    || segment?.assignmentId,
  );
  const existingEndOfDayLoadTargetDispatchDateForComparison = normalizeEndOfDayLoadDateValue(
    endOfDayLoadExistingTargetDispatchDate
    || (currentEndOfDayLoadDeliveryMode === "evening" ? endOfDayLoadEditorSourceDispatchDate : ""),
  );
  const normalizedCurrentEndOfDayLoadDeliveryMode = normalizeEndOfDayLoadDeliveryMode(currentEndOfDayLoadDeliveryMode);
  const existingEndOfDayLoadSourceTruckIdForComparison = normalizeSegmentMetaValue(
    endOfDayLoadExistingSourceTruckId
    || row?.assignmentId
    || row?.assignment?.truck_id
    || segment?.assignmentId,
  );
  const existingEndOfDayLoadSourceDispatchDateForComparison = normalizeEndOfDayLoadDateValue(
    endOfDayLoadExistingSourceDispatchDate || dispatchDate,
  );
  const endOfDayLoadEditHasNoChange = Boolean(
    isExistingEndOfDayLoad
    && normalizedCurrentEndOfDayLoadDeliveryMode === normalizedEndOfDayLoadDeliveryMode
    && selectedEndOfDayLoadSourceTruckId
    && existingEndOfDayLoadSourceTruckIdForComparison === selectedEndOfDayLoadSourceTruckId
    && normalizedEndOfDayLoadEditorSourceDispatchDate
    && existingEndOfDayLoadSourceDispatchDateForComparison === normalizedEndOfDayLoadEditorSourceDispatchDate
    && existingEndOfDayLoadTargetTruckIdForComparison === selectedEndOfDayLoadSourceTruckId
    && selectedEndOfDayLoadTargetDispatchDate
    && existingEndOfDayLoadTargetDispatchDateForComparison === selectedEndOfDayLoadTargetDispatchDate,
  );
  const endOfDayLoadSubmitDisabledReason = endOfDayLoadEditHasNoChange
    ? "Aucun changement à appliquer : la source, le mode et la cible sont déjà ceux du Chargé en PM actuel."
    : "";
  const endOfDayLoadSubmitBlockedMessage = endOfDayLoadSourceOptionsBlockedMessage
    || endOfDayLoadSourceDateValidationMessage
    || endOfDayLoadSourceTruckValidationMessage
    || endOfDayLoadSourceChangesValidationMessage
    || endOfDayLoadTargetTrailerMessage
    || endOfDayLoadDateValidationMessage
    || endOfDayLoadTargetSourceChangesValidationMessage
    || endOfDayLoadSubmitDisabledReason;
  const isEndOfDayLoadSubmitDisabled = Boolean(
    !normalizedEndOfDayLoadEditorSourceDispatchDate
    || !selectedEndOfDayLoadSourceTruckId
    || !selectedEndOfDayLoadTargetDispatchDate
    || endOfDayLoadSubmitBlockedMessage,
  );
  const endOfDayLoadSelectedDisplayMode = normalizedEndOfDayLoadDeliveryMode;
  const endOfDayLoadSelectionLabelOptions = {
    sourceDispatchDate: endOfDayLoadEditorSourceDispatchDate,
    targetDispatchDate: endOfDayLoadTargetDispatchDate,
  };
  const endOfDayLoadModeLongLabel = getEndOfDayLoadModeLongLabel(
    endOfDayLoadSelectedDisplayMode,
    endOfDayLoadSelectionLabelOptions,
  );
  const endOfDayLoadModeIconName = getEndOfDayLoadModeIconName(endOfDayLoadSelectedDisplayMode);
  const endOfDayLoadModeButtonClassName = isEndOfDayLoadEveningMode
    ? "border-[rgba(15,23,42,0.72)]! bg-[rgba(15,23,42,0.95)]! text-white! shadow-none! hover:border-[rgba(15,23,42,0.86)]! hover:bg-[rgba(30,41,59,0.98)]! hover:text-white!"
    : "border-[rgba(202,138,4,0.34)]! bg-[rgba(254,240,138,0.82)]! text-[rgb(113,63,18)]! shadow-none! hover:border-[rgba(202,138,4,0.46)]! hover:bg-[rgba(254,249,195,0.96)]! hover:text-[rgb(113,63,18)]!";
  const endOfDayLoadModeButtonOpenClassName = isEndOfDayLoadEveningMode
    ? "border-[rgba(15,23,42,0.82)]! bg-[rgba(15,23,42,0.98)]! text-white! shadow-none! hover:border-[rgba(15,23,42,0.92)]! hover:bg-[rgba(30,41,59,0.98)]! hover:text-white!"
    : "border-[rgba(202,138,4,0.42)]! bg-[rgba(254,240,138,0.95)]! text-[rgb(113,63,18)]! shadow-none! hover:border-[rgba(202,138,4,0.52)]! hover:bg-[rgba(254,249,195,0.98)]! hover:text-[rgb(113,63,18)]!";
  const endOfDayLoadModeAccentColor = isEndOfDayLoadEveningMode
    ? "rgba(15,23,42,0.38)"
    : "rgba(202,138,4,0.34)";

  const handleEndOfDayLoadDeliveryModeChange = React.useCallback((nextMode) => {
    const deliveryMode = normalizeEndOfDayLoadDeliveryMode(nextMode);
    const currentMode = normalizeEndOfDayLoadDeliveryMode(endOfDayLoadDeliveryMode);
    if (currentMode !== "evening" && deliveryMode === "evening") {
      endOfDayLoadDayTargetDispatchDateRef.current = normalizeEndOfDayLoadDateValue(endOfDayLoadTargetDispatchDate);
    }
    setEndOfDayLoadDeliveryMode(deliveryMode);
    setEndOfDayLoadTargetDispatchDate((currentValue) => {
      if (deliveryMode === "evening") {
        return endOfDayLoadEditorSourceDispatchDate;
      }
      // Un PM soir rouvert a date cible = date source : le mode jour cible au moins le lendemain.
      return resolveEndOfDayLoadTargetDispatchDateForMode(
        endOfDayLoadEditorSourceDispatchDate,
        "next_day",
        endOfDayLoadDayTargetDispatchDateRef.current || currentValue,
      );
    });
  }, [endOfDayLoadDeliveryMode, endOfDayLoadEditorSourceDispatchDate, endOfDayLoadTargetDispatchDate]);

  const handleEndOfDayLoadSourceDispatchDateChange = React.useCallback((nextDispatchDate) => {
    const normalizedDate = normalizeEndOfDayLoadDateValue(nextDispatchDate);
    setEndOfDayLoadSourceDispatchDate(normalizedDate);
    if (isEndOfDayLoadEveningMode) {
      setEndOfDayLoadTargetDispatchDate(normalizedDate);
    }
  }, [isEndOfDayLoadEveningMode]);

  React.useEffect(() => {
    if (!isEndOfDayLoadEditorOpen) {
      return;
    }

    setEndOfDayLoadTargetDispatchDate((currentValue) => {
      if (normalizedEndOfDayLoadDeliveryMode === "evening") {
        return endOfDayLoadEditorSourceDispatchDate;
      }
      const nextValue = normalizeEndOfDayLoadDateValue(
        currentValue
        || endOfDayLoadExistingTargetDispatchDate
        || resolveNextDispatchDateValue(endOfDayLoadEditorSourceDispatchDate),
      );
      endOfDayLoadDayTargetDispatchDateRef.current = nextValue;
      return nextValue;
    });
  }, [
    endOfDayLoadEditorSourceDispatchDate,
    endOfDayLoadExistingTargetDispatchDate,
    isEndOfDayLoadEditorOpen,
    normalizedEndOfDayLoadDeliveryMode,
  ]);

  React.useEffect(() => {
    if (!isEveningStandardEditorOpen) {
      return;
    }

    const preferredTruckId = normalizeSegmentMetaValue(row?.assignmentId || row?.assignment?.truck_id);
    const preferredTarget = resolveSelectableTransferTarget(standardTransferTargetOptions, preferredTruckId);
    if (!preferredTarget) {
      setEveningStandardTargetTruckId("");
      return;
    }

    setEveningStandardTargetTruckId((currentValue) => (
      standardTransferTargetOptions.some((target) => target.truckId === currentValue && !target.isTractorMissing)
        ? currentValue
        : preferredTarget.truckId
    ));
  }, [
    standardTransferTargetOptions,
    isEveningStandardEditorOpen,
    row?.assignment?.truck_id,
    row?.assignmentId,
  ]);

  const endOfDayLoadDeliveryIds = React.useMemo(() => {
    return endOfDayLoadFilterDeliveryIds;
  }, [endOfDayLoadFilterDeliveryIds]);
  const endOfDayLoadUsesGroup = endOfDayLoadDeliveryIds.length > 1;
  const isGroupTransferScopeComplete = !isGroupActionMode || (
    groupScopeIsComplete
    && endOfDayLoadDeliveryIds.length === groupDeliveryIds.length
    && groupDeliveryIds.every((deliveryId) => endOfDayLoadDeliveryIds.includes(deliveryId))
  );
  const endOfDayLoadGroupStepIds = endOfDayLoadUsesGroup
    ? endOfDayLoadGroupSegments
      .map((candidate) => normalizeSegmentMetaValue(candidate?.segmentReorder?.stepId || candidate?.businessStep?.stepId))
      .filter(Boolean)
    : [];
  const canAssignEndOfDayLoad = Boolean(
    !shouldHideDispatcherActions
    && isLoadingStep
    && endOfDayLoadDeliveryIds.length > 0
    && typeof assignmentInteractions.onAssignEndOfDayLoad === "function",
  );
  const canAssignEveningStandard = Boolean(
    !shouldHideDispatcherActions
    && !isEndOfDayLoadManagedSegment
    && isLoadingStep
    && endOfDayLoadDeliveryIds.length > 0
    && typeof assignmentInteractions.onAssignEveningStandard === "function",
  );
  const canEditEndOfDayLoad = Boolean(
    isExistingEndOfDayLoad
    && endOfDayLoadDeliveryIds.length > 0
    && typeof assignmentInteractions.onAssignEndOfDayLoad === "function"
  );
  const canRemoveEndOfDayLoad = Boolean(
    isExistingEndOfDayLoad
    && typeof assignmentInteractions.onRemoveEndOfDayLoad === "function",
  );
  const canManageEndOfDayLoad = canAssignEndOfDayLoad || canEditEndOfDayLoad;
  React.useEffect(() => {
    const shouldLoadEveningTargets = Boolean(
      dispatchDate
      && canAssignEveningStandard
      && (isTransferActionMenuOpen || isEveningStandardEditorOpen)
    );
    if (!shouldLoadEveningTargets) {
      return undefined;
    }
    const targetsLoadErrorMessage = `Impossible de charger les camions du dispatch ${standardTransferTargetPeriod === "day" ? "jour" : "soir"}.`;

    const abortController = new AbortController();
    setEveningStandardTargetsState((currentState) => ({
      ...currentState,
      errorMessage: "",
      isLoading: true,
      options: [],
    }));
    const searchParams = new URLSearchParams();
    searchParams.set("dispatch_date", dispatchDate);
    searchParams.set("dispatch_period", standardTransferTargetPeriod);
    fetchWithSupabaseAuth(`/api/results/latest?${searchParams.toString()}`, { signal: abortController.signal })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = targetsLoadErrorMessage;
          try {
            const errorPayload = await response.json();
            errorMessage = normalizeSegmentMetaValue(errorPayload?.error || errorPayload?.detail) || errorMessage;
          } catch {
            // Keep the generic message; the response may not be JSON.
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((payload) => {
        if (abortController.signal.aborted) {
          return;
        }
        const baseTargets = buildTransferTargetOptionsFromOptimizationAssignments(payload?.assignments);
        // Cibles de l'autre période : Ontario, Protégé et Mis de côté viennent de leur propre ligne (camion attelé de cette
        // période), jamais des lignes du Gantt affiché.
        setEveningStandardTargetsState({
          errorMessage: "",
          isLoading: false,
          options: buildEveningStandardTargetOptions(baseTargets, [], {
            requiresAspirator: endOfDayLoadRequiresAspirator,
            requiresOntario: endOfDayLoadRequiresOntario,
            sourceProductLabel: endOfDayLoadSourceProductLabel,
          }),
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setEveningStandardTargetsState({
          errorMessage: error?.message || targetsLoadErrorMessage,
          isLoading: false,
          options: [],
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    canAssignEveningStandard,
    dispatchDate,
    endOfDayLoadRequiresAspirator,
    endOfDayLoadRequiresOntario,
    endOfDayLoadSourceProductLabel,
    isEveningStandardEditorOpen,
    isTransferActionMenuOpen,
    standardTransferTargetPeriod,
  ]);
  const endOfDayLoadInitialTruckLabel = resolveEndOfDayLoadInitialTruckLabel({
    row,
    segment,
    truckReassignmentTargets,
  });
  const endOfDayLoadTargetTruckLabel = resolveEndOfDayLoadTargetTruckLabel({
    row,
    segment,
    truckReassignmentTargets,
  });
  const endOfDayLoadEditorEmptyMessage = canRemoveEndOfDayLoad
    ? buildEndOfDayLoadSegmentEditorEmptyMessage()
    : "";
  const preloadedPreviousDayGroupSegments = React.useMemo(() => {
    const sameSiteGroupKey = normalizeSegmentMetaValue(segment?.sameSiteGroupKey);
    if (!sameSiteGroupKey) {
      return [];
    }

    return (Array.isArray(row?.segments) ? row.segments : [])
      .filter((candidate) => (
        normalizeSegmentMetaValue(candidate?.sameSiteGroupKey) === sameSiteGroupKey
        && !candidate?.isEndOfDayLoad
        && !candidate?.isEndOfDayLoadSourcePreload
        && resolveSegmentPreloadedPreviousDayOrderId(candidate)
      ));
  }, [row?.segments, segment?.sameSiteGroupKey]);
  const selectedPreloadedPreviousDayOrderId = resolveSegmentPreloadedPreviousDayOrderId(segment);
  const preloadedPreviousDayUsesGroup = preloadedPreviousDayGroupSegments.length > 1;
  const preloadedPreviousDayGroupOrderIds = preloadedPreviousDayUsesGroup
    ? preloadedPreviousDayGroupSegments
      .map((candidate) => resolveSegmentPreloadedPreviousDayOrderId(candidate))
      .filter(Boolean)
      .filter((orderId, index, orderIds) => orderIds.indexOf(orderId) === index)
    : [];
  const preloadedPreviousDayGroupStepIds = preloadedPreviousDayUsesGroup
    ? preloadedPreviousDayGroupSegments
      .map((candidate) => normalizeSegmentMetaValue(candidate?.segmentReorder?.stepId || candidate?.businessStep?.stepId))
      .filter(Boolean)
    : [];
  const preloadedPreviousDayCommandOptions = React.useMemo(() => {
    const sourceSegments = preloadedPreviousDayUsesGroup ? preloadedPreviousDayGroupSegments : [segment];
    const seenOrderIds = new Set();
    return sourceSegments
      .map((candidate, index) => buildPreloadedPreviousDayCommandOption(candidate, index))
      .filter((option) => {
        if (!option.orderId || seenOrderIds.has(option.orderId)) {
          return false;
        }
        seenOrderIds.add(option.orderId);
        return true;
      });
  }, [preloadedPreviousDayGroupSegments, preloadedPreviousDayUsesGroup, segment]);
  const preloadedPreviousDayScopeOptions = isGroupActionMode
    ? [{ label: `Groupe (${preloadedPreviousDayGroupOrderIds.length})`, value: "group" }]
    : [
        { label: "Commande", value: "order" },
        { label: `Groupe (${preloadedPreviousDayGroupOrderIds.length})`, value: "group" },
      ];
  const selectedPreloadedPreviousDayScope = isGroupActionMode
    ? "group"
    : preloadedPreviousDayUsesGroup && preloadedPreviousDayScope === "group"
      ? "group"
      : "order";
  const selectedPreloadedPreviousDayOrderIds = selectedPreloadedPreviousDayScope === "group"
    ? preloadedPreviousDayGroupOrderIds
    : [selectedPreloadedPreviousDayOrderId].filter(Boolean);
  const canAssignPreloadedPreviousDay = Boolean(
    !shouldHideDispatcherActions
    && !isEndOfDayLoadManagedSegment
    && isLoadingStep
    && selectedPreloadedPreviousDayOrderId
    && !segment?.isPreloadedPreviousDay
    && !segment?.is_preloaded_previous_day
    && editableOrder?.is_preloaded_previous_day !== true
    && typeof assignmentInteractions.onAssignPreloadedPreviousDay === "function",
  );
  const canRemovePreloadedPreviousDay = Boolean(
    !shouldHideDispatcherActions
    && !isEndOfDayLoadManagedSegment
    && (isLoadingStep || segment?.stopType === "loading")
    && selectedPreloadedPreviousDayOrderId
    && (
      segment?.isPreloadedPreviousDay
      || segment?.is_preloaded_previous_day
      || editableOrder?.is_preloaded_previous_day === true
    )
    && typeof assignmentInteractions.onRemovePreloadedPreviousDay === "function",
  );

  const parsedManualBufferMinutes = manualBufferMinutesInput.trim()
    ? Number.parseInt(manualBufferMinutesInput.trim(), 10)
    : null;
  const hasValidManualBufferMinutes = Number.isInteger(parsedManualBufferMinutes) && parsedManualBufferMinutes > 0;
  const hasManualBufferValidationError = Boolean(
    manualBufferMinutesInput.trim()
    && (!Number.isInteger(parsedManualBufferMinutes) || parsedManualBufferMinutes < 0),
  );
  const manualBufferRequiresReason = hasValidManualBufferMinutes && !manualBufferReasonInput;
  const manualBufferValidationMessage = hasManualBufferValidationError
    ? "Saisissez un nombre entier positif de minutes, ou laissez vide pour retirer le buffer."
    : manualBufferRequiresReason
      ? "Choisissez un motif avant d'enregistrer le buffer manuel."
      : "";
  const manualBufferUpdatedAtLabel = formatSegmentManualBufferUpdatedAt(segment?.manualBufferUpdatedAt);
  const hasPersistedManualBuffer = Number(segment?.manualBufferMinutes) > 0;
  const manualBufferSummaryItems = [
    { label: "Base", value: segment?.baseTimelineDurationLabel || formatDurationShort(segment?.baseTimelineDurationMinutes) || "0 min" },
    { label: "Buffer", value: segment?.manualBufferLabel || "+0 min" },
    { label: "Total", value: segment?.timelineDurationLabel || formatDurationShort(segment?.timelineDurationMinutes) || "0 min" },
  ];
  const routeAlternativeCanManage = typeof assignmentInteractions.onApplyRouteAlternative === "function"
    && typeof assignmentInteractions.onRemoveRouteAlternative === "function";
  const routeAlternativeAvailable = routeAlternativeCanManage && Boolean(segment?.routeAlternativeAvailable || segment?.routeAlternativeApplied);
  const routeAlternativeApplied = Boolean(segment?.routeAlternativeApplied);
  const selectedRouteAlternativeMode = routeAlternativeSelectedMode === "alternative" ? "alternative" : "current";
  const isCurrentRouteSelected = selectedRouteAlternativeMode === "current";
  const isAlternativeRouteSelected = selectedRouteAlternativeMode === "alternative";
  const routeAlternativeId = segment?.routeAlternativeId || SOREL_TRACY_ROUTE_ALTERNATIVE_ID;
  const routeAlternativeLabel = segment?.routeAlternativeLabel || "Traverse Sorel-Tracy";
  const routeAlternativeSummary = segment?.routeAlternativeSummary && typeof segment.routeAlternativeSummary === "object"
    ? segment.routeAlternativeSummary
    : null;
  const routeAlternativeViewPayload = routeAlternativeViewState.payload && typeof routeAlternativeViewState.payload === "object"
    ? routeAlternativeViewState.payload
    : null;
  const fetchedRouteAlternative = Array.isArray(routeAlternativeViewPayload?.alternatives)
    ? routeAlternativeViewPayload.alternatives.find((item) => item && typeof item === "object") || null
    : null;
  const fetchedCurrentRoute = routeAlternativeViewPayload?.current && typeof routeAlternativeViewPayload.current === "object"
    ? routeAlternativeViewPayload.current
    : null;
  const segmentRouteAlternativeCurrent = segment?.routeAlternativeCurrent && typeof segment.routeAlternativeCurrent === "object"
    ? segment.routeAlternativeCurrent
    : null;
  const routeAlternativeCurrent = fetchedCurrentRoute || segmentRouteAlternativeCurrent;
  const routeAlternativeComparison = fetchedRouteAlternative || routeAlternativeSummary;
  const fetchedDeltaDurationMinutes = Number.isFinite(Number(fetchedRouteAlternative?.delta_duration_seconds))
    ? Number(fetchedRouteAlternative.delta_duration_seconds) / 60
    : null;
  const fetchedDeltaDistanceMeters = Number.isFinite(Number(fetchedRouteAlternative?.delta_distance_meters))
    ? Number(fetchedRouteAlternative.delta_distance_meters)
    : null;
  const routeAlternativeDurationLabel = routeAlternativeComparison?.duration_label || routeAlternativeComparison?.durationLabel || null;
  const routeAlternativeDistanceLabel = routeAlternativeComparison?.distance_label || routeAlternativeComparison?.distanceLabel || null;
  const routeAlternativeEstimatedArrivalAt = routeAlternativeComparison?.estimated_arrival_at || routeAlternativeComparison?.estimatedArrivalAt || "";
  const routeAlternativeArrivalLabel = routeAlternativeEstimatedArrivalAt
    ? formatSegmentManualBufferUpdatedAt(routeAlternativeEstimatedArrivalAt)
    : "";
  const routeAlternativeDeltaDurationLabel = formatRouteAlternativeSignedDuration(fetchedDeltaDurationMinutes ?? segment?.routeAlternativeDeltaDurationMinutes);
  const routeAlternativeDeltaDistanceLabel = formatRouteAlternativeSignedDistance(fetchedDeltaDistanceMeters ?? segment?.routeAlternativeDeltaDistanceMeters);
  const currentRouteLabel = routeAlternativeCurrent?.label || "Actuel via Montréal";
  const currentRouteDurationLabel = routeAlternativeCurrent?.duration_label || segment?.travelDurationLabel || segment?.timelineDurationLabel || "";
  const currentRouteDistanceLabel = routeAlternativeCurrent?.distance_label || segment?.distanceLabel || "";
  const routeAlternativeDocks = Array.isArray(routeAlternativeComparison?.docks) ? routeAlternativeComparison.docks : [];
  const routeAlternativeHasGeometry = Boolean(routeAlternativeComparison?.geometry)
    || (Array.isArray(routeAlternativeComparison?.segments) && routeAlternativeComparison.segments.length > 0);
  const canPreviewRouteAlternative = Boolean(routeAlternativeCurrent?.geometry && routeAlternativeHasGeometry);
  const routeAlternativeReason = segment?.routeAlternativeReason || segment?.montrealRiskAlert?.reason || "Trajet alternatif pertinent pour éviter l’île de Montréal.";
  const routeAlternativeSegmentIndexes = normalizeFleetRouteSegmentIndexes(
    segment?.routeSegmentIndex,
    segment?.routeAlternativeSegmentIndexes,
  );
  const routeAlternativeSegmentIndexesKey = routeAlternativeSegmentIndexes.join("|");
  const routeAlternativePrimarySegmentIndex = routeAlternativeSegmentIndexes[0] || segment?.routeSegmentIndex;
  const routeAlternativeKeyPayload = {
    alternativeId: routeAlternativeId,
    routeSegmentIndex: routeAlternativePrimarySegmentIndex,
    routeSegmentIndexes: routeAlternativeSegmentIndexes,
    tripId: segment?.tripId || row?.tripId || "",
  };

  React.useEffect(() => {
    if (
      !isRouteAlternativeExpanded
      || !routeAlternativeAvailable
      || routeAlternativeApplied
      || !routeAlternativeKeyPayload.tripId
      || !routeAlternativeKeyPayload.routeSegmentIndex
    ) {
      return undefined;
    }

    const abortController = new AbortController();
    setRouteAlternativeViewState({ errorMessage: "", isLoading: true, payload: null });
    fetchRouteAlternatives({
      routeSegmentIndex: routeAlternativeKeyPayload.routeSegmentIndex,
      signal: abortController.signal,
      tripId: routeAlternativeKeyPayload.tripId,
    })
      .then((payload) => {
        if (abortController.signal.aborted) {
          return;
        }
        setRouteAlternativeViewState({
          errorMessage: "",
          isLoading: false,
          payload,
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setRouteAlternativeViewState({
          errorMessage: error?.message || "Impossible de charger l'alternative de route.",
          isLoading: false,
          payload: null,
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    isRouteAlternativeExpanded,
    routeAlternativeApplied,
    routeAlternativeAvailable,
    routeAlternativeKeyPayload.routeSegmentIndex,
    routeAlternativeKeyPayload.tripId,
  ]);
  const orderEditorEditable = Boolean(editableOrder?.order_id);
  const deliveryWindowEditable = orderEditorEditable && canEditSingleDeliveryWindowFields(editableOrder);
  const orderDraftErrors = orderEditorEditable
    ? getOrderDraftErrors(orderEditorDraft, {
      clientCorrespondenceOptionsByLookupKey: editableOrderValidationLookup,
      includeOrderId: true,
    })
    : {};
  const orderHasChanges = orderEditorEditable && hasOrderDraftChanges(editableOrder, orderEditorDraft);
  const loadingWindowFieldMessages = resolveSegmentOrderFieldMessages(orderDraftErrors, ["loading_window_start", "loading_priority"]);
  const deliveryWindowFieldMessages = resolveSegmentOrderFieldMessages(orderDraftErrors, ["delivery_window_start", "delivery_priority"]);
  const orderHasValidationErrors = loadingWindowFieldMessages.length > 0 || deliveryWindowFieldMessages.length > 0;
  const buildingValueLabel = orderEditorEditable
    ? resolveSegmentOrderTextLabel(orderEditorDraft.building || editableOrder?.building, "—")
    : "";

  const toggleOrderEditorKey = (nextKey) => {
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setOpenOrderEditorKey((currentKey) => (currentKey === nextKey ? "" : nextKey));
  };

  const toggleManualBufferEditor = () => {
    setOpenOrderEditorKey("");
    setIsRouteAlternativeExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setIsManualBufferExpanded((currentValue) => !currentValue);
  };

  const toggleRouteAlternativeEditor = () => {
    setOpenOrderEditorKey("");
    setIsManualBufferExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setIsRouteAlternativeExpanded((currentValue) => !currentValue);
  };

  const updateManualBufferEditorField = (field, value) => {
    setManualBufferEditorState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const updateOrderEditorField = (field, value) => {
    setOrderEditorDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  };

  const openSaveConfirmationDialog = React.useCallback((dialogConfig, onConfirmAction) => {
    const dismissDialog = () => {
      setSaveConfirmationDialog(null);
    };

    setSaveConfirmationDialog({
      ...dialogConfig,
      isOpen: true,
      onCancel: dismissDialog,
      onClose: dismissDialog,
      onConfirm: () => {
        dismissDialog();
        onConfirmAction?.();
      },
    });
  }, []);

  const requestManualBufferSaveConfirmation = React.useCallback(() => {
    if (!manualBufferEditable || hasManualBufferValidationError || manualBufferRequiresReason) {
      return;
    }

    assignmentInteractions.onSetSegmentBuffer?.({
      manualBufferMinutes: hasValidManualBufferMinutes ? parsedManualBufferMinutes : null,
      manualBufferNote: manualBufferNoteInput,
      manualBufferReason: hasValidManualBufferMinutes ? manualBufferReasonInput : null,
      orderId: segment?.manualBufferOrderId || segment?.operationOrderId || "",
      segmentBufferKey: segment?.manualBufferStepKey || "",
    });
  }, [
    assignmentInteractions,
    hasManualBufferValidationError,
    hasValidManualBufferMinutes,
    manualBufferEditable,
    manualBufferNoteInput,
    manualBufferReasonInput,
    manualBufferRequiresReason,
    parsedManualBufferMinutes,
    segment?.manualBufferOrderId,
    segment?.manualBufferStepKey,
    segment?.operationOrderId,
  ]);

  const handleRemoveManualBuffer = () => {
    if (!manualBufferEditable) {
      return;
    }

    assignmentInteractions.onSetSegmentBuffer?.({
      manualBufferMinutes: null,
      manualBufferNote: "",
      manualBufferReason: null,
      orderId: segment?.manualBufferOrderId || segment?.operationOrderId || "",
      segmentBufferKey: segment?.manualBufferStepKey || "",
    });
  };

  const handleApplyRouteAlternative = React.useCallback(() => {
    if (!routeAlternativeAvailable || !routeAlternativeKeyPayload.tripId || !routeAlternativeKeyPayload.routeSegmentIndex) {
      return;
    }

    if (selectedRouteAlternativeMode === "alternative") {
      if (routeAlternativeApplied) {
        return;
      }
      assignmentInteractions.onApplyRouteAlternative?.(routeAlternativeKeyPayload);
      return;
    }

    if (!routeAlternativeApplied) {
      return;
    }
    assignmentInteractions.onRemoveRouteAlternative?.(routeAlternativeKeyPayload);
  }, [
    assignmentInteractions,
    routeAlternativeApplied,
    routeAlternativeAvailable,
    routeAlternativeKeyPayload.alternativeId,
    routeAlternativeKeyPayload.routeSegmentIndex,
    routeAlternativeSegmentIndexesKey,
    routeAlternativeKeyPayload.tripId,
    selectedRouteAlternativeMode,
  ]);

  const handleRemoveRouteAlternative = React.useCallback(() => {
    if (!routeAlternativeApplied || !routeAlternativeKeyPayload.tripId || !routeAlternativeKeyPayload.routeSegmentIndex) {
      return;
    }

    assignmentInteractions.onRemoveRouteAlternative?.(routeAlternativeKeyPayload);
  }, [
    assignmentInteractions,
    routeAlternativeApplied,
    routeAlternativeKeyPayload.alternativeId,
    routeAlternativeKeyPayload.routeSegmentIndex,
    routeAlternativeSegmentIndexesKey,
    routeAlternativeKeyPayload.tripId,
  ]);

  const handlePreviewRouteAlternative = React.useCallback((mode) => {
    if (!canPreviewRouteAlternative || !routeAlternativeKeyPayload.tripId || !routeAlternativeKeyPayload.routeSegmentIndex) {
      return;
    }

    const previewMode = mode === "current" ? "current" : "alternative";
    setRouteAlternativeSelectedMode(previewMode);
    assignmentInteractions.onPreviewRouteAlternative?.({
      ...routeAlternativeKeyPayload,
      alternative: routeAlternativeComparison,
      current: routeAlternativeCurrent,
      label: previewMode === "current" ? currentRouteLabel : routeAlternativeLabel,
      mode: previewMode,
    });
  }, [
    assignmentInteractions,
    canPreviewRouteAlternative,
    currentRouteLabel,
    routeAlternativeComparison,
    routeAlternativeCurrent,
    routeAlternativeKeyPayload.alternativeId,
    routeAlternativeKeyPayload.routeSegmentIndex,
    routeAlternativeSegmentIndexesKey,
    routeAlternativeKeyPayload.tripId,
    routeAlternativeLabel,
  ]);
  const routeAlternativeActionIsCurrent = selectedRouteAlternativeMode === "current";
  const routeAlternativeActionIsNoop = routeAlternativeActionIsCurrent
    ? !routeAlternativeApplied
    : routeAlternativeApplied;
  const routeAlternativeActionLabel = routeAlternativeActionIsCurrent
    ? (routeAlternativeApplied ? `Appliquer ${currentRouteLabel}` : "Actuel déjà utilisé")
    : (routeAlternativeApplied ? "Traverse appliquée" : "Appliquer la traverse");
  const routeAlternativeActionDisabled = Boolean(
    routeAlternativeActionIsNoop
    || (!routeAlternativeActionIsCurrent && (
      routeAlternativeViewState.isLoading
      || routeAlternativeViewState.errorMessage
    )),
  );

  const toggleEndOfDayLoadEditor = React.useCallback(() => {
    setOpenOrderEditorKey("");
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setIsPreloadedPreviousDayEditorOpen(false);
    setIsEndOfDayLoadEditorOpen((currentValue) => !currentValue);
  }, []);

  const toggleTransferActionMenu = React.useCallback(() => {
    setOpenOrderEditorKey("");
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsPreloadedPreviousDayEditorOpen(false);
    setIsEndOfDayLoadEditorOpen(false);
    setIsEveningStandardEditorOpen(false);
    setIsTransferActionMenuOpen((currentValue) => !currentValue);
  }, []);

  const openEveningStandardEditor = React.useCallback(() => {
    setOpenOrderEditorKey("");
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsPreloadedPreviousDayEditorOpen(false);
    setIsEndOfDayLoadEditorOpen(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen((currentValue) => !currentValue);
  }, []);

  const handleModifyEndOfDayLoad = React.useCallback(() => {
    if (!canEditEndOfDayLoad) {
      return;
    }

    setEndOfDayLoadRemovalDialog(null);
    toggleEndOfDayLoadEditor();
  }, [canEditEndOfDayLoad, toggleEndOfDayLoadEditor]);

  const applyPreloadedPreviousDay = React.useCallback((scopeOverride = null) => {
    const resolvedScope = scopeOverride || selectedPreloadedPreviousDayScope;
    const orderIds = resolvedScope === "group"
      ? preloadedPreviousDayGroupOrderIds
      : [selectedPreloadedPreviousDayOrderId].filter(Boolean);
    if (!canAssignPreloadedPreviousDay || orderIds.length === 0) {
      return;
    }

    openSaveConfirmationDialog({
      ...SEGMENT_PRELOAD_ASSIGNMENT_CONFIRMATION,
      summaryItems: [
        { label: "Portée", value: resolvedScope === "group" ? `Groupe (${orderIds.length})` : "Commande" },
        { label: "Commande", value: orderIds.join(", "), span: "fill" },
      ],
    }, () => {
      assignmentInteractions.onAssignPreloadedPreviousDay?.({
        dispatchPeriod,
        groupStepIds: resolvedScope === "group" ? preloadedPreviousDayGroupStepIds : [],
        orderIds,
        scope: resolvedScope,
        sourceGroupKey: resolvedScope === "group" ? normalizeSegmentMetaValue(segment?.sameSiteGroupKey) : "",
        sourceTruckId: normalizeSegmentMetaValue(row?.assignmentId || segment?.assignmentId),
      });
      setIsPreloadedPreviousDayEditorOpen(false);
    });
  }, [
    assignmentInteractions,
    canAssignPreloadedPreviousDay,
    dispatchPeriod,
    openSaveConfirmationDialog,
    preloadedPreviousDayGroupOrderIds,
    preloadedPreviousDayGroupStepIds,
    row?.assignmentId,
    segment?.assignmentId,
    segment?.sameSiteGroupKey,
    selectedPreloadedPreviousDayOrderId,
    selectedPreloadedPreviousDayScope,
  ]);

  const handlePreloadedPreviousDayButtonClick = React.useCallback(() => {
    setOpenOrderEditorKey("");
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setIsEndOfDayLoadEditorOpen(false);
    setPreloadedPreviousDayEditorMode("assign");
    if (!preloadedPreviousDayUsesGroup) {
      applyPreloadedPreviousDay("order");
      return;
    }
    setPreloadedPreviousDayScope("group");
    setIsPreloadedPreviousDayEditorOpen((currentValue) => (
      preloadedPreviousDayEditorMode === "assign" ? !currentValue : true
    ));
  }, [applyPreloadedPreviousDay, preloadedPreviousDayEditorMode, preloadedPreviousDayUsesGroup]);

  const removePreloadedPreviousDay = React.useCallback((scopeOverride = null, orderIdsOverride = null) => {
    const resolvedScope = scopeOverride || selectedPreloadedPreviousDayScope;
    const overrideOrderIds = (Array.isArray(orderIdsOverride) ? orderIdsOverride : [])
      .map((orderId) => normalizeSegmentMetaValue(orderId))
      .filter(Boolean)
      .filter((orderId, index, orderIds) => orderIds.indexOf(orderId) === index);
    const orderIds = overrideOrderIds.length > 0
      ? overrideOrderIds
      : resolvedScope === "group"
        ? preloadedPreviousDayGroupOrderIds
        : [selectedPreloadedPreviousDayOrderId].filter(Boolean);
    if (!canRemovePreloadedPreviousDay || orderIds.length === 0) {
      return;
    }

    openSaveConfirmationDialog({
      ...SEGMENT_PRELOAD_REMOVAL_CONFIRMATION,
      summaryItems: [
        { label: "Portée", value: resolvedScope === "group" ? `Groupe (${orderIds.length})` : "Commande" },
        { label: "Commande", value: orderIds.join(", "), span: "fill" },
      ],
    }, () => {
      assignmentInteractions.onRemovePreloadedPreviousDay?.({
        dispatchPeriod,
        groupStepIds: resolvedScope === "group" ? preloadedPreviousDayGroupStepIds : [],
        orderIds,
        scope: resolvedScope,
        sourceGroupKey: resolvedScope === "group" ? normalizeSegmentMetaValue(segment?.sameSiteGroupKey) : "",
        sourceTruckId: normalizeSegmentMetaValue(row?.assignmentId || segment?.assignmentId),
      });
      setIsPreloadedPreviousDayEditorOpen(false);
    });
  }, [
    assignmentInteractions,
    canRemovePreloadedPreviousDay,
    dispatchPeriod,
    openSaveConfirmationDialog,
    preloadedPreviousDayGroupOrderIds,
    preloadedPreviousDayGroupStepIds,
    row?.assignmentId,
    segment?.assignmentId,
    segment?.sameSiteGroupKey,
    selectedPreloadedPreviousDayOrderId,
    selectedPreloadedPreviousDayScope,
  ]);

  const handleRemovePreloadedPreviousDayButtonClick = React.useCallback(() => {
    setOpenOrderEditorKey("");
    setIsManualBufferExpanded(false);
    setIsRouteAlternativeExpanded(false);
    setIsTransferActionMenuOpen(false);
    setIsEveningStandardEditorOpen(false);
    setIsEndOfDayLoadEditorOpen(false);
    setPreloadedPreviousDayEditorMode("remove");
    if (!preloadedPreviousDayUsesGroup) {
      removePreloadedPreviousDay("order");
      return;
    }
    setPreloadedPreviousDayScope("group");
    setIsPreloadedPreviousDayEditorOpen((currentValue) => (
      preloadedPreviousDayEditorMode === "remove" ? !currentValue : true
    ));
  }, [preloadedPreviousDayEditorMode, preloadedPreviousDayUsesGroup, removePreloadedPreviousDay]);

  const isPreloadedPreviousDayRemovalMode = preloadedPreviousDayEditorMode === "remove";
  const canShowPreloadedPreviousDayEditor = Boolean(
    isPreloadedPreviousDayEditorOpen
    && (
      (isPreloadedPreviousDayRemovalMode && canRemovePreloadedPreviousDay)
      || (!isPreloadedPreviousDayRemovalMode && canAssignPreloadedPreviousDay)
    ),
  );
  const showPreloadedPreviousDayRemovalCommandList = Boolean(
    isPreloadedPreviousDayRemovalMode
    && selectedPreloadedPreviousDayScope === "order"
    && preloadedPreviousDayCommandOptions.length > 1
  );
  const preloadedPreviousDayEditorTitle = isPreloadedPreviousDayRemovalMode ? "Retirer Preload" : "Preload";
  const preloadedPreviousDayEditorDescription = isPreloadedPreviousDayRemovalMode
    ? "Remet Chargé la veille à Non sans relancer une optimisation globale."
    : "Active Chargé la veille sans relancer une optimisation globale.";
  const preloadedPreviousDayEditorSubmitLabel = isPreloadedPreviousDayRemovalMode
    ? (selectedPreloadedPreviousDayScope === "group" ? "Retirer le groupe" : "Retirer Preload")
    : "Activer";
  const selectedEveningStandardTargetTruckId = normalizeSegmentMetaValue(
    eveningStandardTargetOption?.truckId || eveningStandardTargetTruckId,
  );
  const eveningStandardDisabledReason = eveningStandardTargetsState.isLoading
    ? `Chargement des camions du dispatch ${standardTransferTargetLabel.toLowerCase()}...`
    : eveningStandardTargetsState.errorMessage
      ? eveningStandardTargetsState.errorMessage
      : !standardTransferTargetOptions.length
        ? buildEndOfDayLoadTargetTrucksEmptyMessage("")
        : "";
  const isEveningStandardSubmitDisabled = Boolean(
    !canAssignEveningStandard
    || !selectedEveningStandardTargetTruckId
    || !dispatchDate
    || endOfDayLoadDeliveryIds.length === 0
    || eveningStandardDisabledReason,
  );

  const handleApplyEndOfDayLoad = React.useCallback(() => {
    const sourceDispatchDate = normalizedEndOfDayLoadEditorSourceDispatchDate;
    const sourceTruckId = selectedEndOfDayLoadSourceTruckId;
    const targetDispatchDate = selectedEndOfDayLoadTargetDispatchDate;
    if (
      !canManageEndOfDayLoad
      || !sourceDispatchDate
      || !sourceTruckId
      || !targetDispatchDate
      || endOfDayLoadSourceDateValidationMessage
      || endOfDayLoadSourceTruckValidationMessage
      || endOfDayLoadSourceChangesValidationMessage
      || endOfDayLoadSourceOptionsBlockedMessage
      || endOfDayLoadDateValidationMessage
      || endOfDayLoadTargetTrailerMessage
      || endOfDayLoadTargetSourceChangesValidationMessage
      || endOfDayLoadSubmitDisabledReason
      || endOfDayLoadDeliveryIds.length === 0
    ) {
      return;
    }

    const confirmationConfig = isExistingEndOfDayLoad
      ? SEGMENT_END_OF_DAY_LOAD_EDIT_CONFIRMATION
      : SEGMENT_END_OF_DAY_LOAD_ASSIGNMENT_CONFIRMATION;
    openSaveConfirmationDialog({
      ...confirmationConfig,
      ...(isEveningEndOfDayLoadSource ? {
        impactItems: [`La commande revient d'abord au jour sur ${resolveEndOfDayLoadTrailerWords(endOfDayLoadSourceTrailer, "").the}.`, ...confirmationConfig.impactItems],
      } : {}),
      summaryItems: [
        { label: "Portée", value: endOfDayLoadUsesGroup ? `Groupe (${endOfDayLoadDeliveryIds.length})` : "Commande" },
        { label: "Mode", value: endOfDayLoadModeLongLabel },
        { label: "Date source", value: sourceDispatchDate },
        { label: endOfDayLoadSourceFieldLabel, value: endOfDayLoadSourceTrailerLabel },
        { label: "Date cible", value: targetDispatchDate },
        { label: endOfDayLoadTargetFieldLabel, value: endOfDayLoadTargetTrailerLabel },
      ],
    }, () => {
      assignmentInteractions.onAssignEndOfDayLoad?.({
        deliveryMode: normalizedEndOfDayLoadDeliveryMode,
        deliveryId: endOfDayLoadDeliveryIds[0] || "",
        deliveryIds: endOfDayLoadDeliveryIds,
        dispatchDate,
        groupStepIds: endOfDayLoadGroupStepIds,
        markerDispatchDate: isExistingEndOfDayLoad ? endOfDayLoadMarkerDispatchDate : "",
        markerKey: endOfDayLoadMarkerKey,
        scope: endOfDayLoadUsesGroup ? "group" : "delivery",
        sourceGroupKey: endOfDayLoadUsesGroup ? normalizeSegmentMetaValue(segment?.sameSiteGroupKey) : "",
        sourceDispatchDate,
        ...(isEveningEndOfDayLoadSource ? {
          eveningTruckId: ganttClickedEndOfDayLoadSourceTruckId,
          sourceDispatchPeriod: "evening",
          sourceTruckName: endOfDayLoadSourceTrailerLabel,
        } : {}),
        sourceTruckId,
        targetPeriod: endOfDayLoadTargetPeriod,
        targetDispatchDate,
        targetTruckId: sourceTruckId,
        targetTruckName: endOfDayLoadTargetTrailerLabel,
      });
      setIsEndOfDayLoadEditorOpen(false);
    });
  }, [
    assignmentInteractions,
    canManageEndOfDayLoad,
    dispatchDate,
    endOfDayLoadDeliveryIds,
    endOfDayLoadDateValidationMessage,
    endOfDayLoadGroupStepIds,
    endOfDayLoadMarkerDispatchDate,
    endOfDayLoadMarkerKey,
    endOfDayLoadModeLongLabel,
    endOfDayLoadSourceDateValidationMessage,
    endOfDayLoadSourceOptionsBlockedMessage,
    endOfDayLoadSourceChangesValidationMessage,
    endOfDayLoadSourceFieldLabel,
    endOfDayLoadSourceTrailer,
    endOfDayLoadSourceTrailerLabel,
    endOfDayLoadSourceTruckValidationMessage,
    endOfDayLoadTargetFieldLabel,
    endOfDayLoadTargetPeriod,
    endOfDayLoadTargetSourceChangesValidationMessage,
    endOfDayLoadTargetTrailerLabel,
    endOfDayLoadTargetTrailerMessage,
    endOfDayLoadUsesGroup,
    endOfDayLoadSubmitDisabledReason,
    ganttClickedEndOfDayLoadSourceTruckId,
    isEveningEndOfDayLoadSource,
    isExistingEndOfDayLoad,
    normalizedEndOfDayLoadDeliveryMode,
    openSaveConfirmationDialog,
    normalizedEndOfDayLoadEditorSourceDispatchDate,
    selectedEndOfDayLoadSourceTruckId,
    selectedEndOfDayLoadTargetDispatchDate,
    segment?.sameSiteGroupKey,
  ]);

  const handleApplyEveningStandard = React.useCallback(() => {
    const targetTruckId = selectedEveningStandardTargetTruckId;
    if (
      !canAssignEveningStandard
      || !targetTruckId
      || !dispatchDate
      || endOfDayLoadDeliveryIds.length === 0
      || eveningStandardDisabledReason
    ) {
      return;
    }

    const sourceTruckId = normalizeSegmentMetaValue(row?.assignmentId || segment?.assignmentId);
    openSaveConfirmationDialog({
      ...(standardTransferTargetPeriod === "day"
        ? SEGMENT_DAY_STANDARD_TRANSFER_CONFIRMATION
        : SEGMENT_EVENING_STANDARD_TRANSFER_CONFIRMATION),
      summaryItems: [
        { label: "Portée", value: endOfDayLoadUsesGroup ? `Groupe (${endOfDayLoadDeliveryIds.length})` : "Commande" },
        { label: "Mode", value: `${standardTransferTargetLabel} standard` },
        { label: "Date", value: dispatchDate },
        { label: "Camion", value: eveningStandardTargetOption?.truckName || targetTruckId },
      ],
    }, () => {
      assignmentInteractions.onAssignEveningStandard?.({
        deliveryId: endOfDayLoadDeliveryIds[0] || "",
        deliveryIds: endOfDayLoadDeliveryIds,
        dispatchDate,
        groupStepIds: endOfDayLoadGroupStepIds,
        scope: endOfDayLoadUsesGroup ? "group" : "delivery",
        sourceGroupKey: endOfDayLoadUsesGroup ? normalizeSegmentMetaValue(segment?.sameSiteGroupKey) : "",
        sourceTruckId,
        targetPeriod: standardTransferTargetPeriod,
        targetTruckId,
        targetTruckName: eveningStandardTargetOption?.truckName || targetTruckId,
      });
      setIsEveningStandardEditorOpen(false);
    });
  }, [
    assignmentInteractions,
    canAssignEveningStandard,
    dispatchDate,
    endOfDayLoadDeliveryIds,
    endOfDayLoadGroupStepIds,
    endOfDayLoadUsesGroup,
    eveningStandardDisabledReason,
    eveningStandardTargetOption?.truckId,
    eveningStandardTargetOption?.truckName,
    openSaveConfirmationDialog,
    row?.assignmentId,
    selectedEveningStandardTargetTruckId,
    segment?.assignmentId,
    segment?.sameSiteGroupKey,
    standardTransferTargetLabel,
    standardTransferTargetPeriod,
  ]);

  const handleRemoveEndOfDayLoad = React.useCallback(() => {
    if (!canRemoveEndOfDayLoad) {
      return;
    }

    setIsEndOfDayLoadEditorOpen(false);
    const removalPayload = {
      dispatchDate,
      isSourcePreload: segment?.isEndOfDayLoadSourcePreload === true,
      markerDispatchDate: normalizeSegmentMetaValue(segment?.endOfDayLoadMarkerDispatchDate),
      markerKey: endOfDayLoadMarkerKey,
      sourceDispatchDate: normalizeSegmentMetaValue(segment?.endOfDayLoadSourceDispatchDate),
      targetDispatchDate: normalizeSegmentMetaValue(segment?.endOfDayLoadTargetDispatchDate),
    };
    setEndOfDayLoadRemovalDialog({
      groupOrderCount: 0,
      groupOrderPreview: "",
      markerKey: endOfDayLoadMarkerKey,
      sourceDate: normalizeSegmentMetaValue(segment?.endOfDayLoadSourceDispatchDate) || dispatchDate,
      sourceFieldLabel: endOfDayLoadSourceFieldLabel,
      sourceTruckLabel: endOfDayLoadInitialTruckLabel,
      subtitle: segment?.title || segment?.label || "",
      summaryItems: [
        { label: "Marqueur", value: endOfDayLoadMarkerKey, multiline: true },
      ],
      summaryLayout: "stack",
      targetDate: normalizeSegmentMetaValue(segment?.endOfDayLoadTargetDispatchDate),
      targetTruckLabel: endOfDayLoadTargetTruckLabel,
      title: SEGMENT_END_OF_DAY_LOAD_REMOVAL_CONFIRMATION.title,
      onConfirm: (removalMode) => {
        setEndOfDayLoadRemovalDialog(null);
        assignmentInteractions.onRemoveEndOfDayLoad?.({
          ...removalPayload,
          removalMode,
        });
      },
    });
  }, [
    assignmentInteractions,
    canRemoveEndOfDayLoad,
    dispatchDate,
    endOfDayLoadInitialTruckLabel,
    endOfDayLoadMarkerKey,
    endOfDayLoadSourceFieldLabel,
    endOfDayLoadTargetTruckLabel,
    segment?.endOfDayLoadMarkerDispatchDate,
    segment?.endOfDayLoadSourceDispatchDate,
    segment?.endOfDayLoadTargetDispatchDate,
    segment?.isEndOfDayLoadSourcePreload,
    segment?.label,
    segment?.title,
  ]);

  const handleCloseEndOfDayLoadRemovalDialog = React.useCallback(() => {
    setEndOfDayLoadRemovalDialog(null);
  }, []);

  const handleConfirmEndOfDayLoadRemovalDialog = React.useCallback((removalMode) => {
    endOfDayLoadRemovalDialog?.onConfirm?.(removalMode);
  }, [endOfDayLoadRemovalDialog]);

  const requestOrderSaveConfirmation = React.useCallback(() => {
    if (!orderEditorEditable || orderHasValidationErrors || !orderHasChanges) {
      return;
    }

    const payload = buildUpdatePayload(editableOrder, orderEditorDraft);
    openSaveConfirmationDialog({
      ...SEGMENT_ORDER_SAVE_CONFIRMATION,
      summaryItems: [
        { label: "Commande", value: editableOrder?.order_id || "—" },
        { label: "Étape", value: isLoadingStep ? "Chargement" : isDeliveryStep ? "Déchargement" : "Commande" },
      ],
    }, () => {
      const submission = buildOrderEditSubmission(editableOrder, payload, {
        submitted_at: new Date().toISOString(),
        truck_id: segment?.assignmentId || row?.assignmentId,
        truck_name: row?.truckName || row?.truck_name,
        loading_step_id: isLoadingStep ? businessStep?.stepId : null,
        delivery_id: isDeliveryStep ? businessStep?.deliveryId : null,
        step_kind: isLoadingStep ? "loading" : isDeliveryStep ? "delivery" : "order",
        dispatch_date: dispatchDate,
      });
      assignmentInteractions.onUpdateOrderDetails?.({
        orderEditSubmission: submission,
        orderId: editableOrder?.order_id || "",
        payload,
        deliveryId: isDeliveryStep ? businessStep?.deliveryId : null,
        loadingStepId: isLoadingStep ? businessStep?.stepId : null,
      });
    });
  }, [
    assignmentInteractions,
    dispatchDate,
    row,
    segment,
    editableOrder,
    isDeliveryStep,
    isLoadingStep,
    openSaveConfirmationDialog,
    orderEditorDraft,
    orderEditorEditable,
    orderHasChanges,
    orderHasValidationErrors,
  ]);

  const handleOrderEditorKeyDownCapture = React.useCallback((event) => {
    if (event.key !== "Enter" || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.isComposing) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement) || (target.tagName !== "INPUT" && target.tagName !== "SELECT")) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    requestOrderSaveConfirmation();
  }, [requestOrderSaveConfirmation]);

  const handleManualBufferKeyDownCapture = React.useCallback((event) => {
    if (event.key !== "Enter" || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.isComposing) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement) || (target.tagName !== "INPUT" && target.tagName !== "SELECT")) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    requestManualBufferSaveConfirmation();
  }, [requestManualBufferSaveConfirmation]);

  if (canRemovePreloadedPreviousDay) {
    actionButtons.push(
      <SegmentActionButton
        key="remove-preloaded-previous-day"
        ariaExpanded={isPreloadedPreviousDayRemovalMode && isPreloadedPreviousDayEditorOpen}
        ariaLabel="Retirer Preload"
        className={isPreloadedPreviousDayRemovalMode && isPreloadedPreviousDayEditorOpen
          ? "border-[rgba(8,145,178,0.28)] bg-[rgba(236,254,255,0.98)] text-[rgb(14,116,144)] hover:border-[rgba(8,145,178,0.36)] hover:bg-white"
          : "border-[rgba(8,145,178,0.2)] bg-[rgba(236,254,255,0.94)] text-[rgb(14,116,144)] hover:border-[rgba(8,145,178,0.34)] hover:bg-white"
        }
        hoverCardAccentColor="rgba(8,145,178,0.26)"
        hoverCardContent={(
          <div className="grid gap-1.25">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">
              {preloadedPreviousDayUsesGroup ? "Remet Chargé la veille à Non pour la commande ou le groupe visible." : "Remet Chargé la veille à Non pour cette commande."}
            </div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              Le camion courant sera reconstruit localement sans relancer une optimisation globale.
            </div>
          </div>
        )}
        hoverCardTitle="Retirer Preload"
        icon="moon"
        label="Retirer Preload"
        onClick={handleRemovePreloadedPreviousDayButtonClick}
        trailingContent={preloadedPreviousDayUsesGroup ? (
          <span className="text-[0.58rem] font-bold opacity-75">{preloadedPreviousDayGroupOrderIds.length}</span>
        ) : null}
      />,
    );
  }

  if (canEditEndOfDayLoad) {
    actionButtons.push(
      <SegmentActionButton
        key="edit-end-of-day-load"
        ariaExpanded={isEndOfDayLoadEditorOpen}
        ariaLabel={`Modifier ${currentEndOfDayLoadModeShortLabel}`}
        className={isEndOfDayLoadEditorOpen ? currentEndOfDayLoadModeButtonOpenClassName : currentEndOfDayLoadModeButtonClassName}
        hoverCardAccentColor={currentEndOfDayLoadModeAccentColor}
        hoverCardContent={(
          <div className="grid gap-1.25">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">
              Mode actuel: {currentEndOfDayLoadModeLongLabel}.
            </div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              Le Chargé PM sera déplacé selon vos choix.
            </div>
          </div>
        )}
        hoverCardTitle={`Modifier · ${currentEndOfDayLoadModeLongLabel}`}
        icon={currentEndOfDayLoadModeIconName}
        label="Modifier"
        onClick={handleModifyEndOfDayLoad}
        topContent={(
          <span className="text-[0.52rem] font-black uppercase tracking-[0.1em] opacity-85">
            Actuel · {currentEndOfDayLoadModeShortLabel}
          </span>
        )}
      />,
    );
  }

  if (canRemoveEndOfDayLoad) {
    actionButtons.push(
      <SegmentActionButton
        key="remove-end-of-day-load"
        ariaLabel="Retirer PM"
        className="border-[rgba(190,18,60,0.18)] bg-[rgba(255,241,242,0.94)] text-[rgb(159,18,57)] hover:border-[rgba(190,18,60,0.3)] hover:bg-white"
        hoverCardAccentColor="rgba(190,18,60,0.26)"
        hoverCardContent={(
          <div className="grid gap-1.25">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">
              Ouvre le choix de retrait du Chargé en PM.
            </div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              Vous pourrez remettre la commande sur la remorque source ou conserver la date cible et l'envoyer dans Suspendues.
            </div>
          </div>
        )}
        hoverCardTitle="Retirer PM"
        icon="rotate-ccw"
        label="Retirer PM"
        onClick={handleRemoveEndOfDayLoad}
      />,
    );
  }

  const handleGanttDispatcherAction = async (button) => {
    if (!button) {
      return;
    }
    if (button.mode === "unfreeze") {
      if (button.requiresPortalUnfreezeConfirmation) {
        if (!await confirmInGrain(
          "Dégeler une commande confirmée par le portail ?\n\n" +
          "La confirmation passera à « à revalider » et le chauffeur devra revalider cette livraison."
        )) {
          return;
        }
      }
      if (button.unfreezeMode === "group" && Array.isArray(button.deliveryIds) && button.deliveryIds.length >= 2) {
        assignmentInteractions.onUnfreezeDeliveryGroup?.({
          deliveryIds: button.deliveryIds,
          groupStepIds: Array.isArray(button.groupStepIds) ? button.groupStepIds : [],
        });
        return;
      }
      assignmentInteractions.onUnfreezeDelivery?.(button.deliveryId);
      return;
    }
    assignmentInteractions.onFreezeDelivery?.(button.deliveryId);
  };

  if (ganttDispatcherActionButton) {
    if (isGroupActionMode) {
      actionButtons.push(
        <SegmentActionButton
          key="dispatcher-action-group-tail"
          ariaLabel={ganttDispatcherActionButton.ariaLabel}
          className={SEGMENT_BUTTON_NEUTRAL_CLASS_NAME}
          disabled={ganttDispatcherActionButton.disabled
            || (ganttDispatcherActionButton.unfreezeMode === "group"
              ? typeof assignmentInteractions.onUnfreezeDeliveryGroup !== "function"
              : typeof assignmentInteractions.onUnfreezeDelivery !== "function")}
          hoverCardAccentColor="var(--hovercard-accent-warning)"
          hoverCardContent={dispatcherStatusHoverCardContent}
          hoverCardTitle={ganttDispatcherActionButton.title || ganttDispatcherActionButton.label}
          icon={ganttDispatcherActionButton.icon}
          label={ganttDispatcherActionButton.label}
          trailingContent={Array.isArray(ganttDispatcherActionButton.deliveryIds) && ganttDispatcherActionButton.deliveryIds.length > 1
            ? <span className="text-[0.58rem] font-bold opacity-75">{ganttDispatcherActionButton.deliveryIds.length}</span>
            : null}
          onClick={() => handleGanttDispatcherAction(ganttDispatcherActionButton)}
        />,
      );
    } else if (!isGroupActionMode && ganttDispatcherActionButton) {
      actionButtons.push(
        <SegmentActionButton
          key="dispatcher-action"
          ariaLabel={ganttDispatcherActionButton.ariaLabel}
          className={ganttDispatcherActionButton.mode === "unfreeze"
            ? SEGMENT_BUTTON_NEUTRAL_CLASS_NAME
            : "border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)] hover:border-[rgba(217,119,6,0.3)] hover:bg-white"
          }
          disabled={ganttDispatcherActionButton.disabled}
          hoverCardAccentColor={dispatcherStatusCode === "delivered" ? "var(--color-accent)" : "var(--hovercard-accent-warning)"}
          hoverCardContent={dispatcherStatusHoverCardContent}
          hoverCardTitle={dispatcherStatus?.label ? `${ganttDispatcherActionButton.label} · ${dispatcherStatus.label}` : ganttDispatcherActionButton.label}
          icon={ganttDispatcherActionButton.icon}
          label={ganttDispatcherActionButton.label}
          topContent={dispatcherStatusCode === "planifiable" ? null : dispatcherStatusTrailingContent}
          onClick={() => handleGanttDispatcherAction(ganttDispatcherActionButton)}
        />,
      );
    }
  } else if (isGroupActionMode && !groupHasImplicitFrozenMember && !groupIsNonTailSharedLoadingMember
    // Sans l'action (droit absent), le bouton est absent, comme sur un segment seul.
    && typeof (groupAllFrozen ? assignmentInteractions.onUnfreezeDeliveryGroup : assignmentInteractions.onFreezeDeliveryGroup) === "function") {
    const groupHasDeliveredMember = groupDispatcherStatusCodes.some((code) => code === "delivered");
    const groupRequiresPortalUnfreezeConfirmation = groupMemberSegments.some((candidate) => (
      candidate?.businessStep?.dispatcherActionButton?.requiresPortalUnfreezeConfirmation === true
    ));
    actionButtons.push(
      <SegmentActionButton
        key="dispatcher-action-group"
        ariaLabel={`${groupAllFrozen ? "Dégeler" : "Geler"} le groupe de ${groupDeliveryIds.length} commandes`}
        className={groupAllFrozen
          ? SEGMENT_BUTTON_NEUTRAL_CLASS_NAME
          : "border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)] hover:border-[rgba(217,119,6,0.3)] hover:bg-white"
        }
        disabled={
          !groupScopeIsComplete
          || groupHasDeliveredMember
          || (groupAllFrozen
            ? typeof assignmentInteractions.onUnfreezeDeliveryGroup !== "function"
            : typeof assignmentInteractions.onFreezeDeliveryGroup !== "function")
        }
        hoverCardAccentColor="var(--hovercard-accent-warning)"
        hoverCardTitle={groupAllFrozen ? "Dégeler le groupe" : "Geler le groupe"}
        icon="pause"
        label={groupAllFrozen ? "Dégeler" : "Geler"}
        trailingContent={<span className="text-[0.58rem] font-bold opacity-75">{groupDeliveryIds.length}</span>}
        onClick={async () => {
          if (groupAllFrozen) {
            if (groupRequiresPortalUnfreezeConfirmation && !await confirmInGrain(
              "Dégeler un groupe contenant une commande confirmée par le portail ?\n\n" +
              "Les confirmations concernées passeront à « à revalider » et les chauffeurs devront les revalider."
            )) {
              return;
            }
            assignmentInteractions.onUnfreezeDeliveryGroup?.(groupMutationPayload);
            return;
          }
          assignmentInteractions.onFreezeDeliveryGroup?.(groupMutationPayload);
        }}
      />,
    );
  }

  if (!ganttDispatcherActionButton && !businessStep?.dispatcherActionButton && dispatcherStatusCode === "delivered") {
    actionButtons.push(
      <SegmentActionButton
        key="dispatcher-status"
        ariaLabel={`Statut d'exécution ${dispatcherStatus.label}`}
        className={SEGMENT_BUTTON_DISABLED_CLASS_NAME}
        disabled
        hoverCardAccentColor="var(--color-accent)"
        hoverCardContent={dispatcherStatusHoverCardContent}
        hoverCardTitle={dispatcherStatus.label}
        icon={dispatcherStatus.icon}
        label={dispatcherStatus.label}
        tone="subtle"
      />,
    );
  }

  const confirmGroupButton = React.useMemo(() => {
    if (isGroupActionMode || shouldHideDispatcherActions) {
      return null;
    }

    const sameSiteGroupKey = typeof segment?.sameSiteGroupKey === "string"
      ? segment.sameSiteGroupKey.trim()
      : "";
    if (!sameSiteGroupKey) {
      return null;
    }

    const allSegments = Array.isArray(row?.segments) ? row.segments : [];
    const groupSegments = allSegments.filter((candidate) => (
      typeof candidate?.sameSiteGroupKey === "string"
      && candidate.sameSiteGroupKey.trim() === sameSiteGroupKey
      && candidate?.businessStep?.confirmButton?.isActive
      && candidate?.businessStep?.confirmButton?.type === "delivery"
      && candidate?.businessStep?.confirmButton?.targetId
    ));

    if (groupSegments.length < 2) {
      return null;
    }

    const allExplicitlyProtected = groupSegments.every((candidate) => (
      candidate?.businessStep?.manualStatus?.code === "protected"
      && !candidate?.businessStep?.confirmButton?.disabled
    ));
    if (!allExplicitlyProtected) {
      return null;
    }

    const groupDeliveryIds = groupSegments
      .map((candidate) => candidate.businessStep.confirmButton.targetId)
      .filter(Boolean);
    const groupStepIdList = groupSegments
      .map((candidate) => candidate?.segmentReorder?.stepId || "")
      .filter(Boolean);
    const safeSourceGroupKey = sameSiteGroupKey;

    return (
      <SegmentActionButton
        key="confirm-group"
        ariaLabel={`Retirer la protection du groupe de ${groupDeliveryIds.length} commandes`}
        className={joinClasses([SEGMENT_BUTTON_ACTIVE_CLASS_NAME, "min-w-0! gap-1! px-1.25! py-0.5! text-[0.6rem]! font-bold! leading-none!"])}
        hoverCardAccentColor="var(--color-accent)"
        hoverCardContent={(
          <div className="grid gap-2">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">Retirer la protection du groupe</div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              {`Retire la protection explicite des ${groupDeliveryIds.length} commandes du groupe visible. Elles redeviendront déplaçables par Réaffecter et Optimiser.`}
            </div>
          </div>
        )}
        hoverCardTitle="Retirer la protection du groupe"
        icon="shield"
        label="Groupe"
        onClick={() => {
          assignmentInteractions.onUnconfirmDeliveryGroup?.({
            deliveryIds: groupDeliveryIds,
            groupStepIds: groupStepIdList,
            sourceGroupKey: safeSourceGroupKey,
          });
        }}
        tone="icon"
        topContent={(
          <span className="inline-flex items-center gap-0.5 text-[0.55rem] font-semibold opacity-80">
            {groupDeliveryIds.length} commandes
          </span>
        )}
      />
    );
  }, [
    businessStep?.confirmButton?.disabled,
    businessStep?.confirmButton?.isActive,
    businessStep?.confirmButton?.targetId,
    businessStep?.confirmButton?.type,
    businessStep?.manualStatus?.code,
    row?.segments,
    segment?.sameSiteGroupKey,
    isGroupActionMode,
    shouldHideDispatcherActions,
  ]);

  if (isGroupActionMode && !shouldHideDispatcherActions) {
    const groupConfirmButtons = groupMemberSegments.map((candidate) => candidate?.businessStep?.confirmButton).filter(Boolean);
    const groupConfirmDisabled = groupAllManualReleaseLock
      ? !groupScopeIsComplete || typeof assignmentInteractions.onReleaseDeliveryGroup !== "function"
      : !groupScopeIsComplete
        || groupConfirmButtons.length !== groupMemberSegments.length
        || groupConfirmButtons.some((button) => button.disabled && !button.isActive);
    const groupManualReleaseTitle = groupManualReleaseIsReordered
      ? "Libérer le réordonnancement manuel"
      : "Libérer la réaffectation manuelle";
    confirmButtonElement = (
      <SegmentActionButton
        key="confirm-group-primary"
        ariaLabel={`${groupAllManualReleaseLock ? `${groupManualReleaseTitle} du` : groupAllProtected ? "Libérer la protection du" : "Protéger le"} groupe de ${groupDeliveryIds.length} commandes`}
        ariaPressed={groupAllProtected || groupAllManualReleaseLock}
        className={groupAllManualReleaseLock
          ? releaseActionPresentation.className
          : groupAllProtected
          ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME
          : "border-[rgba(59,130,246,0.16)] bg-[rgba(239,246,255,0.92)] text-[rgb(29,78,216)] hover:border-[rgba(59,130,246,0.26)] hover:bg-white"
        }
        disabled={groupConfirmDisabled}
        hoverCardAccentColor={groupAllManualReleaseLock
          ? releaseActionPresentation.hoverCardAccentColor
          : groupAllProtected
            ? "var(--color-accent)"
            : "var(--hovercard-accent-warning)"
        }
        hoverCardContent={groupAllManualReleaseLock ? (
          <div className="grid gap-2">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">{groupManualReleaseTitle}</div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              Retire les verrous manuels du groupe visible et rend ses commandes à nouveau éligibles à l'optimisation.
            </div>
          </div>
        ) : null}
        hoverCardTitle={groupAllManualReleaseLock
          ? groupManualReleaseTitle
          : groupAllProtected
            ? "Libérer la protection du groupe"
            : "Protéger le groupe"
        }
        icon="shield"
        label={groupAllProtected || groupAllManualReleaseLock ? "Libérer" : "Protéger"}
        trailingContent={<span className="text-[0.58rem] font-bold opacity-75">{groupDeliveryIds.length}</span>}
        tone={groupAllProtected || groupAllManualReleaseLock ? "icon" : "subtle"}
        onClick={() => {
          if (groupAllManualReleaseLock) {
            assignmentInteractions.onReleaseDeliveryGroup?.(groupMutationPayload);
            return;
          }
          if (groupAllProtected) {
            assignmentInteractions.onUnconfirmDeliveryGroup?.(groupMutationPayload);
            return;
          }
          assignmentInteractions.onConfirmDeliveryGroup?.(groupMutationPayload);
        }}
      />
    );
  } else if (!shouldHideDispatcherActions && businessStep?.confirmButton && !shouldHideSupersededSegmentConfirmButton(businessStep)) {
    confirmButtonElement = (
      <SegmentActionButton
        key="confirm"
        ariaLabel={businessStep.confirmButton.ariaLabel}
        ariaPressed={businessStep.confirmButton.ariaPressed}
        className={businessStep.confirmButton.isActive
          ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME
          : "border-[rgba(59,130,246,0.16)] bg-[rgba(239,246,255,0.92)] text-[rgb(29,78,216)] hover:border-[rgba(59,130,246,0.26)] hover:bg-white"
        }
        disabled={businessStep.confirmButton.disabled}
        hoverCardAccentColor={businessStep.confirmButton.isActive
          ? "var(--color-accent)"
          : "var(--hovercard-accent-warning)"
        }
        hoverCardContent={businessStep.confirmButton.disabled && confirmButtonDisabledReason ? (
          <div className="grid gap-1">
            <div className="text-[0.7rem] font-semibold leading-[1.45] text-content-ink">{confirmButtonDisabledReason}</div>
          </div>
        ) : businessStep.confirmButton.isActive ? (
          <div className="grid gap-2">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">Libérer la protection</div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              Retire la protection explicite et rend cette commande à nouveau déplaçable par Réaffecter et Optimiser.
            </div>
          </div>
        ) : null}
        hoverCardTitle={businessStep.confirmButton.disabled && confirmButtonDisabledReason
          ? "Protection"
          : (businessStep.confirmButton.isActive ? "Libérer la protection" : "")
        }
        icon={businessStep.confirmButton.icon}
        label={businessStep.confirmButton.label
          || (businessStep.confirmButton.isActive ? "Libérer" : "Protéger")
        }
        tone={businessStep.confirmButton.isActive ? "icon" : "subtle"}
        onClick={() => {
          if (businessStep.confirmButton.type === "loading") {
            assignmentInteractions.onToggleLoadingConfirm?.({
              isConfirmed: businessStep.confirmButton.isActive,
              loadingStepId: businessStep.confirmButton.targetId,
            });
            return;
          }

          assignmentInteractions.onToggleDeliveryConfirm?.({
            deliveryId: businessStep.confirmButton.targetId,
            isConfirmed: businessStep.confirmButton.isActive,
          });
        }}
      />
    );
  }

  if (
    isGroupTransferScopeComplete
    && (canAssignPreloadedPreviousDay || (canAssignEndOfDayLoad && !isExistingEndOfDayLoad) || canAssignEveningStandard)
  ) {
    transferActionButton = (
      <SegmentActionButton
        key="segment-transfer-menu"
        ariaExpanded={isTransferActionMenuOpen || isPreloadedPreviousDayEditorOpen || isEndOfDayLoadEditorOpen || isEveningStandardEditorOpen}
        ariaLabel="Choisir un transfert"
        className={isTransferActionMenuOpen || isPreloadedPreviousDayEditorOpen || isEndOfDayLoadEditorOpen || isEveningStandardEditorOpen
          ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME
          : SEGMENT_BUTTON_NEUTRAL_CLASS_NAME
        }
        hoverCardAccentColor="rgba(92,138,120,0.34)"
        hoverCardContent={(
          <div className="grid gap-1.25">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">
              Choisissez Preload, Chargé PM ou {standardTransferTargetLabel} pour la portée {isGroupActionMode ? "du groupe" : "du segment"}.
            </div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              {standardTransferTargetLabel} transfère la livraison vers le dispatch {standardTransferTargetLabel.toLowerCase()} courant.
            </div>
          </div>
        )}
        hoverCardTitle="Transférer"
        icon="shuffle"
        label="Transférer"
        onClick={toggleTransferActionMenu}
        trailingContent={endOfDayLoadUsesGroup || preloadedPreviousDayUsesGroup ? (
          <span className="text-[0.58rem] font-bold opacity-75">{endOfDayLoadDeliveryIds.length}</span>
        ) : null}
      />
    );
  }

  if (confirmButtonElement) {
    if (confirmGroupButton && businessStep?.confirmButton?.isActive) {
      actionButtons.push(
        <div key="confirm-stack" className="relative inline-flex flex-col items-stretch">
          <span className="absolute bottom-full right-0 mb-1">
            {confirmGroupButton}
          </span>
          {confirmButtonElement}
        </div>,
      );
    } else {
      actionButtons.push(confirmButtonElement);
    }
  }

  // — Bouton "Libérer le groupe" (same-site, verrous manuels libérables) —
  const releaseGroupButton = React.useMemo(() => {
    if (shouldHideDispatcherActions) {
      return null;
    }

    const sameSiteGroupKey = typeof segment?.sameSiteGroupKey === "string"
      ? segment.sameSiteGroupKey.trim()
      : "";
    if (!sameSiteGroupKey) {
      return null;
    }

    const allSegments = isGroupActionMode ? groupMemberSegments : (Array.isArray(row?.segments) ? row.segments : []);
    const groupSegments = allSegments.filter((candidate) => (
      typeof candidate?.sameSiteGroupKey === "string"
      && candidate.sameSiteGroupKey.trim() === sameSiteGroupKey
      && candidate?.businessStep?.releaseButton?.deliveryId
    ));

    if (
      groupSegments.length < 2
      || (isGroupActionMode && groupSegments.length !== groupMemberSegments.length)
    ) {
      return null;
    }

    const allReleasableManualLock = groupSegments.every((candidate) => {
      const manualStatusCode = candidate?.businessStep?.manualStatus?.code;
      return manualStatusCode === "reassigned" || manualStatusCode === "reordered";
    });
    if (!allReleasableManualLock) {
      return null;
    }

    const allHaveReleaseDeliveryId = groupSegments.every(
      (candidate) => typeof candidate?.businessStep?.releaseButton?.deliveryId === "string"
        && candidate.businessStep.releaseButton.deliveryId,
    );
    if (!allHaveReleaseDeliveryId) {
      return null;
    }

    const releaseDeliveryIds = isGroupActionMode
      ? groupDeliveryIds
      : Array.from(new Set(groupSegments.map(
          (candidate) => candidate.businessStep.releaseButton.deliveryId,
        ).filter(Boolean)));
    const groupStepIdList = isGroupActionMode
      ? groupStepIds
      : groupSegments.map((candidate) => candidate?.segmentReorder?.stepId || "").filter(Boolean);
    const safeSourceGroupKey = sameSiteGroupKey;
    const groupReleaseActionPresentation = resolveSegmentReleaseActionPresentation(
      groupSegments.every((candidate) => resolveProtectionVariant(candidate?.businessStep?.manualStatus) === PROTECTION_VARIANT_REORDERED)
        ? { code: "reordered" }
        : null,
    );

    return (
      <SegmentActionButton
        key="release-group"
        ariaLabel={`Libérer le groupe de ${releaseDeliveryIds.length} commandes`}
        className={joinClasses([
          "text-[0.6rem]! font-bold! leading-none! min-w-0! gap-1! px-1.25! py-0.5!",
          groupReleaseActionPresentation.className,
        ])}
        hoverCardAccentColor={groupReleaseActionPresentation.hoverCardAccentColor}
        hoverCardContent={(
          <div className="grid gap-2">
            <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">Libérer le groupe pour optimisation</div>
            <div className="text-[0.68rem] leading-normal text-content-muted">
              {`Retire les verrous manuels des ${releaseDeliveryIds.length} commandes du groupe visible et les rend à nouveau éligibles au prochain clic sur Optimiser.`}
            </div>
          </div>
        )}
        hoverCardTitle="Libérer le groupe"
        icon="rotate-ccw"
        label={isGroupActionMode ? "Libérer" : "Groupe"}
        onClick={() => {
          assignmentInteractions.onReleaseDeliveryGroup?.({
            deliveryIds: releaseDeliveryIds,
            dispatchPeriod,
            groupStepIds: groupStepIdList,
            sourceGroupKey: safeSourceGroupKey,
          });
        }}
        tone="icon"
        topContent={(
          <span className="inline-flex items-center gap-0.5 text-[0.55rem] font-semibold opacity-80">
            {releaseDeliveryIds.length} commandes
          </span>
        )}
      />
    );
  }, [
    businessStep?.manualStatus?.code,
    businessStep?.releaseButton?.deliveryId,
    dispatchPeriod,
    row?.segments,
    groupDeliveryIds,
    groupMemberSegments,
    groupStepIds,
    isGroupActionMode,
    segment?.sameSiteGroupKey,
    shouldHideDispatcherActions,
  ]);

  if (!shouldHideDispatcherActions && businessStep?.releaseButton) {
    if (isGroupActionMode) {
      if (releaseGroupButton && !groupAllManualReleaseLock) {
        actionButtons.push(releaseGroupButton);
      }
    } else if (releaseGroupButton) {
      actionButtons.push(
        <div key="release-stack" className="relative inline-flex flex-col items-stretch">
          <span className="absolute bottom-full right-0 mb-1">
            {releaseGroupButton}
          </span>
          <SegmentActionButton
            key="release"
            ariaLabel={businessStep.releaseButton.ariaLabel}
            className={releaseActionPresentation.className}
            disabled={businessStep.releaseButton.disabled}
            hoverCardAccentColor={releaseActionPresentation.hoverCardAccentColor}
            hoverCardContent={<ReleaseActionHoverCardContent segment={segment} />}
            hoverCardTitle={businessStep.releaseButton.title || "Libérer pour optimisation"}
            icon={businessStep.releaseButton.icon}
            label={businessStep.releaseButton.label || "Libérer"}
            onClick={() => {
              assignmentInteractions.onReleaseDelivery?.(businessStep.releaseButton.deliveryId, {
                dispatchPeriod,
              });
            }}
            tone="icon"
          />
        </div>,
      );
    } else {
      actionButtons.push(
        <SegmentActionButton
          key="release"
          ariaLabel={businessStep.releaseButton.ariaLabel}
          className={releaseActionPresentation.className}
          disabled={businessStep.releaseButton.disabled}
          hoverCardAccentColor={releaseActionPresentation.hoverCardAccentColor}
          hoverCardContent={<ReleaseActionHoverCardContent segment={segment} />}
          hoverCardTitle={businessStep.releaseButton.title || "Libérer pour optimisation"}
          icon={businessStep.releaseButton.icon}
          label={businessStep.releaseButton.label || "Libérer"}
          onClick={() => {
            assignmentInteractions.onReleaseDelivery?.(businessStep.releaseButton.deliveryId, {
              dispatchPeriod,
            });
          }}
          tone="icon"
        />,
      );
    }
  }

  if (isGroupActionMode && transferActionButton) {
    actionButtons.push(transferActionButton);
  }

  return (
    <section style={{ "--fleet-context-accent": getSegmentHoverCardAccentColor(segment?.stopType, segment?.isInitialPreload, segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload, resolveEndOfDayLoadVisualMode(segment)) }} className={joinClasses([
      "fleet-context-surface grid gap-2.25 rounded-[4px] border px-3 py-3",
      isExistingEndOfDayLoad ? "fleet-context-pm min-w-0" : "",
      getSegmentHoverSurfaceClassName(segment?.stopType, segment?.isInitialPreload, segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload, resolveEndOfDayLoadVisualMode(segment)),
      className,
    ])}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1.25">
        <div className="col-start-1 row-start-1 flex min-w-0 flex-wrap items-center gap-1.5">
          <p className="m-0 text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">{isGroupActionMode ? "Actions groupe" : "Actions segment"}</p>
          <span className="inline-flex items-center rounded-full border border-[rgba(148,163,184,0.14)] bg-white/84 px-2 py-0.75 text-[0.58rem] font-bold tracking-[0.02em] text-content-copy shadow-[inset_0_1px_0_rgba(255,255,255,0.76)]">
            {segment.typeLabel}
          </span>
          {row?.truckLabel ? <span data-fleet-context-identity>{row.truckLabel}</span> : null}
        </div>

        <div className="col-start-2 row-start-1 flex shrink-0 items-center justify-self-end gap-1.5">
          {ordersHoverIcon}
          <SegmentStatusPills segment={segment} />
        </div>

        <div className="col-span-2 grid min-w-0 gap-1">
          <SegmentSummaryBlock
            row={row}
            segment={segment}
            headingClassName="text-[0.95rem] font-semibold leading-tight text-text"
            addressClassName="text-[0.72rem] leading-[1.45] text-content-muted"
            routeClassName="text-[0.72rem] leading-5 text-content-muted"
            showInfoPills={false}
            showRouteSummary={!isPreloadedPreviousDayActionOnly}
          />
        </div>
      </div>

      <div className="grid gap-1.5">
        {!isPreloadedPreviousDayActionOnly && (segment.timelineDurationLabel || segment.distanceLabel || segmentMetaLabel) ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[0.7rem] text-content-muted">
            {segment.timelineDurationLabel ? <span className="whitespace-nowrap">{segment.timelineDurationLabel}</span> : null}
            {segment.distanceLabel ? <span className="whitespace-nowrap">· {segment.distanceLabel}</span> : null}
            {segmentMetaLabel ? <span className="whitespace-nowrap">· {segmentMetaLabel}</span> : null}
          </div>
        ) : null}
        <div className={joinClasses([
          "fleet-context-actions flex min-w-0 max-w-full flex-wrap items-center gap-0.75",
        ])}>
          {actionButtons.length ? actionButtons : (
            <span className="text-[0.72rem] font-medium text-content-muted">Aucune action directe disponible sur cette étape.</span>
          )}
        </div>
      </div>
      {feedbackLines.length ? (
        <div data-fleet-context-feedback className="grid gap-1.25 rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-white/88 px-3 py-2">
          {feedbackLines.map((line) => (
            <p key={line} className="m-0 text-[0.72rem] leading-5 text-content-copy">{line}</p>
          ))}
        </div>
      ) : null}

      {isTransferActionMenuOpen ? (
        <SegmentActionEditorPanel errorMessages={[]}>
          <div className="grid gap-1">
            <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">Transférer</div>
            <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
              {endOfDayLoadUsesGroup
                ? `Groupe complet de ${endOfDayLoadDeliveryIds.length} commandes.`
                : "Commande sélectionnée."}
            </p>
          </div>
          <div className="grid gap-1.25">
            {[
              {
                description: preloadedPreviousDayUsesGroup
                  ? "Active Chargé la veille pour le groupe visible."
                  : "Active Chargé la veille pour cette commande.",
                disabled: !canAssignPreloadedPreviousDay,
                icon: "moon",
                key: "preload",
                label: "Preload",
                onClick: handlePreloadedPreviousDayButtonClick,
              },
              {
                description: "Ouvre le flux Chargé PM existant.",
                disabled: !(canAssignEndOfDayLoad && !isExistingEndOfDayLoad),
                icon: endOfDayLoadModeIconName,
                key: "pm",
                label: "Chargé PM",
                onClick: toggleEndOfDayLoadEditor,
              },
              {
                description: `Ouvre le choix du camion ${standardTransferTargetLabel.toLowerCase()} standard.`,
                disabled: !canAssignEveningStandard,
                icon: "moon",
                key: "standard-period",
                label: standardTransferTargetLabel,
                onClick: openEveningStandardEditor,
              },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className={joinClasses([
                  "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 rounded-xl border px-2.5 py-2 text-left transition",
                  option.disabled
                    ? "cursor-not-allowed border-[rgba(148,163,184,0.16)] bg-slate-50/76 text-content-faint"
                    : SEGMENT_BUTTON_NEUTRAL_CLASS_NAME,
                ])}
                disabled={option.disabled}
                onClick={option.disabled ? undefined : option.onClick}
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(148,163,184,0.18)] bg-white">
                  {renderSegmentStatusIcon(option.icon, "h-3.5 w-3.5")}
                </span>
                <span className="min-w-0">
                  <span className="block text-[0.72rem] font-extrabold leading-5">{option.label}</span>
                  <span className="block text-[0.64rem] font-medium leading-4 text-content-muted">{option.description}</span>
                </span>
              </button>
            ))}
          </div>
        </SegmentActionEditorPanel>
      ) : null}

      {canShowPreloadedPreviousDayEditor ? (
        <SegmentActionEditorPanel errorMessages={[]}>
          <div className="grid gap-1">
            <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">{preloadedPreviousDayEditorTitle}</div>
            <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
              {preloadedPreviousDayEditorDescription}
            </p>
          </div>
          <div className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
            <span>Portée</span>
            <div className="inline-flex w-fit rounded-xl border border-[rgba(14,116,144,0.18)] bg-white p-0.75">
              {preloadedPreviousDayScopeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={joinClasses([
                    "rounded-lg px-2.5 py-1 text-[0.7rem] font-semibold transition",
                    selectedPreloadedPreviousDayScope === option.value
                      ? "bg-[rgba(14,116,144,0.12)] text-[rgb(14,116,144)]"
                      : "text-content-muted hover:bg-slate-50 hover:text-content-copy",
                  ])}
                  onClick={() => setPreloadedPreviousDayScope(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {showPreloadedPreviousDayRemovalCommandList ? (
            <div className="grid gap-1.25">
              {preloadedPreviousDayCommandOptions.map((option) => (
                <div
                  key={option.key}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-xl border border-[rgba(14,116,144,0.12)] bg-[rgba(236,254,255,0.42)] px-2.5 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[0.72rem] font-semibold leading-5 text-content-ink" title={option.title}>
                      {option.title}
                    </div>
                    {option.secondaryLabel ? (
                      <div className="truncate text-[0.64rem] font-medium leading-4 text-content-muted" title={option.secondaryLabel}>
                        {option.secondaryLabel}
                      </div>
                    ) : null}
                    {option.address ? (
                      <div className="truncate text-[0.62rem] leading-4 text-content-faint" title={option.address}>
                        {option.address}
                      </div>
                    ) : null}
                  </div>
                  <ActionButton
                    className="h-8 rounded-xl border-[rgba(14,116,144,0.24)] bg-white px-2.5 text-[0.68rem] font-semibold text-[rgb(14,116,144)] hover:border-[rgba(14,116,144,0.34)] hover:bg-[rgba(236,254,255,0.64)]"
                    disabled={!option.orderId}
                    onClick={() => removePreloadedPreviousDay("order", [option.orderId])}
                  >
                    Retirer
                  </ActionButton>
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {!showPreloadedPreviousDayRemovalCommandList ? (
              <ActionButton
                className="h-9 rounded-xl border-[rgba(14,116,144,0.24)] bg-[rgba(236,254,255,0.96)] px-3 text-[0.72rem] font-semibold text-[rgb(14,116,144)] hover:border-[rgba(14,116,144,0.34)] hover:bg-white"
                disabled={selectedPreloadedPreviousDayOrderIds.length === 0}
                onClick={() => {
                  if (isPreloadedPreviousDayRemovalMode) {
                    removePreloadedPreviousDay();
                    return;
                  }
                  applyPreloadedPreviousDay();
                }}
              >
                {preloadedPreviousDayEditorSubmitLabel}
              </ActionButton>
            ) : null}
            <ActionButton
              className="h-9 rounded-xl border-[rgba(148,163,184,0.18)] bg-white px-3 text-[0.72rem] font-semibold text-content-copy hover:border-[rgba(148,163,184,0.3)] hover:bg-white"
              onClick={() => setIsPreloadedPreviousDayEditorOpen(false)}
            >
              Fermer
            </ActionButton>
          </div>
        </SegmentActionEditorPanel>
      ) : null}

      {canAssignEveningStandard && isEveningStandardEditorOpen ? (
        <SegmentActionEditorPanel errorMessages={[eveningStandardDisabledReason].filter(Boolean)}>
          <div className="grid gap-1">
            <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">{standardTransferTargetLabel} standard</div>
            <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
              {endOfDayLoadUsesGroup
                ? `Groupe complet de ${endOfDayLoadDeliveryIds.length} commandes.`
                : "Commande sélectionnée."}
            </p>
          </div>
          <div className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
            <span>Date</span>
            <div className="h-9 rounded-xl border border-[rgba(92,138,120,0.18)] bg-[rgba(242,248,245,0.86)] px-3 py-2 text-[0.82rem] font-semibold text-text">
              {dispatchDate || "Date courante"}
            </div>
          </div>
          <label className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
            <span>Camion {standardTransferTargetLabel.toLowerCase()}</span>
            <select
              className="h-9 rounded-xl border border-[rgba(92,138,120,0.24)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-[rgba(92,138,120,0.5)] focus:ring-2 focus:ring-[rgba(92,138,120,0.14)]"
              disabled={!standardTransferTargetOptions.length}
              value={eveningStandardTargetOption?.truckId || ""}
              onChange={(event) => setEveningStandardTargetTruckId(event.target.value)}
            >
              {standardTransferTargetOptions.map((target) => (
                <option key={target.truckId} value={target.truckId} disabled={target.isTractorMissing}>
                  {transferTargetLabel(target)}
                </option>
              ))}
            </select>
          </label>
          <p className="m-0 text-[0.7rem] leading-5 text-content-muted">
            Livraison standard de {standardTransferTargetLabel.toLowerCase()}, sans Preload et sans Chargé PM.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Tooltip
              title="Transfert indisponible"
              content={eveningStandardDisabledReason}
              placement="top"
              disabled={!eveningStandardDisabledReason}
            >
              <span className={joinClasses(["inline-flex", isEveningStandardSubmitDisabled ? "cursor-not-allowed" : ""])}>
                <ActionButton
                  className={joinClasses([
                    "h-9 rounded-xl px-3 text-[0.72rem] font-semibold",
                    SEGMENT_BUTTON_ACTIVE_CLASS_NAME,
                    isEveningStandardSubmitDisabled ? "pointer-events-none" : "",
                  ])}
                  disabled={isEveningStandardSubmitDisabled}
                  onClick={handleApplyEveningStandard}
                >
                  Transférer {standardTransferTargetLabel.toLowerCase()}
                </ActionButton>
              </span>
            </Tooltip>
            <ActionButton
              className="h-9 rounded-xl border-[rgba(148,163,184,0.18)] bg-white px-3 text-[0.72rem] font-semibold text-content-copy hover:border-[rgba(148,163,184,0.3)] hover:bg-white"
              onClick={() => setIsEveningStandardEditorOpen(false)}
            >
              Fermer
            </ActionButton>
          </div>
        </SegmentActionEditorPanel>
      ) : null}

      {canManageEndOfDayLoad && isEndOfDayLoadEditorOpen ? (
        <SegmentActionEditorPanel className={END_OF_DAY_LOAD_EDITOR_PANEL_CLASS_NAME} errorMessages={[
          endOfDayLoadSourceOptionsBlockedMessage,
          endOfDayLoadSourceDateValidationMessage,
          endOfDayLoadSourceTruckValidationMessage,
          endOfDayLoadSourceChangesValidationMessage,
          endOfDayLoadTargetTrailerMessage,
          endOfDayLoadDateValidationMessage,
          endOfDayLoadTargetSourceChangesValidationMessage,
        ].filter(Boolean)}>
          <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">
            {isExistingEndOfDayLoad ? "Modifier PM" : "Chargé en PM"}
          </div>
          <EndOfDayLoadDeliveryModeField value={normalizedEndOfDayLoadDeliveryMode} onChange={handleEndOfDayLoadDeliveryModeChange} />
          <div className={END_OF_DAY_LOAD_FIELDS_GRID_CLASS_NAME}>
            <div className={END_OF_DAY_LOAD_FIELD_COLUMN_CLASS_NAME}>
              <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                <span>Date source</span>
                <input
                  className={END_OF_DAY_LOAD_FIELD_CLASS_NAME}
                  type="date"
                  value={endOfDayLoadEditorSourceDispatchDate}
                  onChange={(event) => handleEndOfDayLoadSourceDispatchDateChange(event.target.value)}
                />
              </label>
              <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                <span>{endOfDayLoadSourceFieldLabel}</span>
                <input
                  className={END_OF_DAY_LOAD_FIELD_CLASS_NAME}
                  disabled
                  type="text"
                  value={`${endOfDayLoadSourceTrailerLabel}${endOfDayLoadSourceTruckIsUnavailable ? " — indisponible" : ""}`}
                />
              </label>
            </div>
            <div className={END_OF_DAY_LOAD_FIELD_COLUMN_CLASS_NAME}>
              <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                <span>Date cible</span>
                <input
                  className={END_OF_DAY_LOAD_FIELD_CLASS_NAME}
                  disabled={isEndOfDayLoadEveningMode}
                  type="date"
                  value={endOfDayLoadTargetDispatchDate}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    endOfDayLoadDayTargetDispatchDateRef.current = nextValue;
                    setEndOfDayLoadTargetDispatchDate(nextValue);
                  }}
                />
              </label>
              <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                <span>{endOfDayLoadTargetFieldLabel}</span>
                <input className={END_OF_DAY_LOAD_FIELD_CLASS_NAME} disabled type="text" value={endOfDayLoadTargetTrailerLabel} />
              </label>
            </div>
          </div>
          <EndOfDayLoadEditorActions
            blockedMessage={endOfDayLoadSubmitBlockedMessage}
            isSubmitDisabled={isEndOfDayLoadSubmitDisabled}
            onClose={() => setIsEndOfDayLoadEditorOpen(false)}
            onSubmit={handleApplyEndOfDayLoad}
            submitLabel={isExistingEndOfDayLoad ? "Modifier" : "Assigner"}
          />
        </SegmentActionEditorPanel>
      ) : null}

      {!isGroupActionMode && !shouldHideDispatcherActions ? (
      <div data-fleet-pm-footer={isExistingEndOfDayLoad || undefined} className="inline-flex w-fit max-w-full flex-col justify-self-start gap-1.5 self-start rounded-[16px] border border-[rgba(15,23,42,0.1)] bg-[rgba(255,255,255,0.5)] px-1.5 py-1.25">
        <div className="flex max-w-full flex-wrap gap-1">
          {!isGroupActionMode && orderEditorEditable ? (
            <>
              {isLoadingStep ? (
                <SegmentActionEditorButton
                  hasError={Boolean(loadingWindowFieldMessages.length)}
                  isActive={openOrderEditorKey === "loading_window"}
                  title="Pri"
                  valueLabel={resolveSegmentOrderWindowLabel(orderEditorDraft.loading_window_start, orderEditorDraft.loading_priority)}
                  onClick={() => toggleOrderEditorKey("loading_window")}
                />
              ) : null}

              {isDeliveryStep ? (
                deliveryWindowEditable ? (
                  <SegmentActionEditorButton
                    hasError={Boolean(deliveryWindowFieldMessages.length)}
                    isActive={openOrderEditorKey === "delivery_window"}
                    title="Pri"
                    valueLabel={resolveSegmentOrderWindowLabel(orderEditorDraft.delivery_window_start, orderEditorDraft.delivery_priority)}
                    onClick={() => toggleOrderEditorKey("delivery_window")}
                  />
                ) : (
                  <SegmentActionEditorButton
                    title="Pri"
                    valueLabel="Géré depuis la table Commandes"
                    onClick={null}
                  />
                )
              ) : null}
            </>
          ) : !isGroupActionMode && !isPreloadedPreviousDayActionOnly ? (
            <div className="col-span-full rounded-[14px] border border-[rgba(148,163,184,0.16)] bg-white/96 px-3 py-2.5 text-[0.72rem] leading-5 text-content-muted shadow-inset-highlight">
              {endOfDayLoadEditorEmptyMessage || "Aucune commande modifiable n'est associée à ce segment."}
            </div>
          ) : null}

          {!isGroupActionMode && !canRemoveEndOfDayLoad && !isPreloadedPreviousDayActionOnly ? (
            <SegmentActionEditorButton
              hasError={Boolean(manualBufferValidationMessage)}
              isActive={manualBufferEditable && isManualBufferExpanded}
              onClick={manualBufferEditable ? toggleManualBufferEditor : null}
              title="Buf"
              valueLabel={segment?.manualBufferLabel || "+0 min"}
            />
          ) : null}

          {transferActionButton}

          {!isGroupActionMode && routeAlternativeAvailable ? (
            <SegmentActionEditorButton
              isActive={isRouteAlternativeExpanded}
              onClick={toggleRouteAlternativeEditor}
              title="Trav"
              valueLabel={routeAlternativeApplied ? "Sorel-Tracy appliquée" : routeAlternativeLabel}
            />
          ) : null}

          {!isGroupActionMode && orderEditorEditable && isDeliveryStep ? (
            <SegmentActionEditorButton
              isActive={openOrderEditorKey === "loading_silo"}
              title="Silo"
              valueLabel={resolveSegmentOrderTextLabel(orderEditorDraft.loading_silo)}
              onClick={() => toggleOrderEditorKey("loading_silo")}
            />
          ) : null}

          {!isGroupActionMode && orderEditorEditable && isDeliveryStep ? (
            <SegmentActionEditorButton
              isActive={openOrderEditorKey === "building"}
              title="Bât."
              valueLabel={buildingValueLabel}
              onClick={() => toggleOrderEditorKey("building")}
            />
          ) : null}
        </div>

        {!isGroupActionMode && openOrderEditorKey === "loading_window" ? (
          <SegmentActionEditorPanel errorMessages={loadingWindowFieldMessages} onKeyDownCapture={handleOrderEditorKeyDownCapture}>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                type="time"
                className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                value={orderEditorDraft.loading_window_start}
                onChange={(event) => updateOrderEditorField("loading_window_start", event.target.value)}
                onBlur={(event) => {
                  if (event.target.value === "") {
                    updateOrderEditorField("loading_priority", "");
                  }
                }}
              />
              <select
                className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                value={orderEditorDraft.loading_priority}
                onChange={(event) => updateOrderEditorField("loading_priority", event.target.value)}
              >
                {ORDER_LOADING_PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </SegmentActionEditorPanel>
        ) : null}

        {!isGroupActionMode && openOrderEditorKey === "delivery_window" ? (
          <SegmentActionEditorPanel errorMessages={deliveryWindowFieldMessages} onKeyDownCapture={handleOrderEditorKeyDownCapture}>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                type="time"
                className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                value={orderEditorDraft.delivery_window_start}
                onChange={(event) => updateOrderEditorField("delivery_window_start", event.target.value)}
                onBlur={(event) => {
                  if (event.target.value === "") {
                    updateOrderEditorField("delivery_priority", "");
                  }
                }}
              />
              <select
                className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                value={orderEditorDraft.delivery_priority}
                onChange={(event) => updateOrderEditorField("delivery_priority", event.target.value)}
              >
                {ORDER_LOADING_PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </SegmentActionEditorPanel>
        ) : null}

        {!isGroupActionMode && openOrderEditorKey === "loading_silo" ? (
          <SegmentActionEditorPanel onKeyDownCapture={handleOrderEditorKeyDownCapture}>
            <input
              type="text"
              className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
              value={orderEditorDraft.loading_silo}
              onChange={(event) => updateOrderEditorField("loading_silo", event.target.value)}
              placeholder="Ex. 48E DROIT SILO 10"
            />
          </SegmentActionEditorPanel>
        ) : null}

        {!isGroupActionMode && openOrderEditorKey === "building" ? (
          <SegmentActionEditorPanel onKeyDownCapture={handleOrderEditorKeyDownCapture}>
            <input
              type="text"
              className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
              value={orderEditorDraft.building}
              onChange={(event) => updateOrderEditorField("building", event.target.value)}
              placeholder="Ex. B-12"
            />
          </SegmentActionEditorPanel>
        ) : null}

        {!isGroupActionMode && manualBufferEditable && isManualBufferExpanded ? (
          <SegmentActionEditorPanel errorMessages={manualBufferValidationMessage ? [manualBufferValidationMessage] : []} onKeyDownCapture={handleManualBufferKeyDownCapture}>
            <div className="flex flex-wrap items-center gap-1.5">
              {manualBufferSummaryItems.map((item) => (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-1 rounded-full border border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.9)] px-2 py-1 text-[0.64rem] font-semibold tracking-[0.02em] text-content-copy"
                >
                  <span className="text-content-muted">{item.label}</span>
                  <span className="text-text">{item.value}</span>
                </span>
              ))}
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <label className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
                <span>Buffer (min)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                  value={manualBufferMinutesInput}
                  onChange={(event) => updateManualBufferEditorField("minutes", event.target.value)}
                  placeholder="Ex. 12"
                />
              </label>

              <label className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
                <span>Motif</span>
                <select
                  className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                  value={manualBufferReasonInput}
                  onChange={(event) => updateManualBufferEditorField("reason", event.target.value)}
                >
                  <option value="">Choisir un motif</option>
                  {MANUAL_BUFFER_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Note</span>
              <input
                type="text"
                className="h-9 rounded-xl border border-[rgba(148,163,184,0.22)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-page-loading-ring-strong focus:ring-2 focus:ring-border-accent-subtle"
                value={manualBufferNoteInput}
                onChange={(event) => updateManualBufferEditorField("note", event.target.value)}
                placeholder="Précision libre visible dans le planning"
              />
            </label>

            {segment?.manualBufferUpdatedBy || manualBufferUpdatedAtLabel ? (
              <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
                {segment?.manualBufferUpdatedBy ? `Mis à jour par ${segment.manualBufferUpdatedBy}` : "Mis à jour"}
                {manualBufferUpdatedAtLabel ? ` · ${manualBufferUpdatedAtLabel}` : ""}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <ActionButton
                className={joinClasses(["h-9 rounded-xl px-3 text-[0.72rem] font-semibold", SEGMENT_BUTTON_ACTIVE_CLASS_NAME])}
                disabled={Boolean(manualBufferValidationMessage)}
                onClick={requestManualBufferSaveConfirmation}
              >
                Enregistrer le buffer
              </ActionButton>
              <ActionButton
                className="h-9 rounded-xl border-border-warning-badge bg-[rgba(255,247,237,0.9)] px-3 text-[0.72rem] font-semibold text-[rgb(154,52,18)] hover:border-[rgba(217,119,6,0.28)] hover:bg-white"
                disabled={!hasPersistedManualBuffer && !manualBufferMinutesInput.trim() && !manualBufferReasonInput && !manualBufferNoteInput.trim()}
                onClick={handleRemoveManualBuffer}
              >
                Retirer le buffer
              </ActionButton>
            </div>
          </SegmentActionEditorPanel>
        ) : null}

        {!isGroupActionMode && routeAlternativeAvailable && isRouteAlternativeExpanded ? (
          <SegmentActionEditorPanel>
            <div className="flex items-start justify-between gap-3">
              <div className="grid min-w-0 gap-1">
                <div className="flex min-w-0 items-center gap-1.5 text-[0.72rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">
                  <Sailboat aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
                  <span>{routeAlternativeLabel}</span>
                </div>
                <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
                  {routeAlternativeReason}
                </p>
              </div>
              {routeAlternativeApplied ? (
                <span className="inline-flex shrink-0 rounded-full border border-[rgba(15,92,87,0.16)] bg-[rgba(238,247,245,0.96)] px-2 py-1 text-[0.62rem] font-bold uppercase tracking-[0.08em] text-(--color-legacy-teal)">
                  Appliquée
                </span>
              ) : null}
            </div>

            <div className="grid gap-1.5 sm:grid-cols-2">
              <button
                type="button"
                aria-pressed={isCurrentRouteSelected}
                aria-label={`Afficher ${currentRouteLabel} sur la carte`}
                className={joinClasses([
                  "grid gap-1 rounded-[14px] border px-3 py-2 text-left transition hover:bg-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
                  isCurrentRouteSelected
                    ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME
                    : SEGMENT_BUTTON_NEUTRAL_CLASS_NAME,
                ])}
                disabled={!canPreviewRouteAlternative}
                title={canPreviewRouteAlternative ? currentRouteLabel : "Comparatif de route indisponible"}
                onClick={() => handlePreviewRouteAlternative("current")}
              >
                <span className={joinClasses(["text-[0.62rem] font-bold uppercase tracking-[0.12em]", isCurrentRouteSelected ? "text-white/80" : "text-slate-600"])}>{currentRouteLabel}</span>
                <span className={joinClasses(["text-[0.78rem] font-semibold", isCurrentRouteSelected ? "text-white" : "text-slate-900"])}>{currentRouteDurationLabel || "Durée n/d"}</span>
                <span className={joinClasses(["text-[0.7rem]", isCurrentRouteSelected ? "text-white/80" : "text-slate-700"])}>{currentRouteDistanceLabel || "Distance n/d"}</span>
              </button>
              <button
                type="button"
                aria-pressed={isAlternativeRouteSelected}
                aria-label={`Afficher ${routeAlternativeLabel} sur la carte`}
                className={joinClasses([
                  "grid gap-1 rounded-[14px] border px-3 py-2 text-left transition hover:bg-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
                  isAlternativeRouteSelected
                    ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME
                    : SEGMENT_BUTTON_NEUTRAL_CLASS_NAME,
                ])}
                disabled={!canPreviewRouteAlternative}
                title={canPreviewRouteAlternative ? routeAlternativeLabel : "Comparatif de route indisponible"}
                onClick={() => handlePreviewRouteAlternative("alternative")}
              >
                <span className={joinClasses(["text-[0.62rem] font-bold uppercase tracking-[0.12em]", isAlternativeRouteSelected ? "text-white/80" : "text-slate-600"])}>Traverse Sorel-Tracy</span>
                <span className={joinClasses(["text-[0.78rem] font-semibold", isAlternativeRouteSelected ? "text-white" : "text-slate-900"])}>{routeAlternativeDurationLabel || "Durée après application"}</span>
                <span className={joinClasses(["text-[0.7rem]", isAlternativeRouteSelected ? "text-white/80" : "text-slate-700"])}>{routeAlternativeDistanceLabel || "Distance après application"}</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {routeAlternativeViewState.isLoading ? (
                <span className="inline-flex rounded-full border border-[rgba(148,163,184,0.18)] bg-white/92 px-2 py-1 text-[0.64rem] font-semibold text-content-muted">
                  Chargement du comparatif...
                </span>
              ) : null}
              {routeAlternativeViewState.errorMessage ? (
                <span className="inline-flex rounded-full border border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] px-2 py-1 text-[0.64rem] font-semibold text-[rgb(154,52,18)]">
                  {routeAlternativeViewState.errorMessage}
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(148,163,184,0.18)] bg-white/92 px-2 py-1 text-[0.64rem] font-semibold text-content-copy">
                <span className="text-content-muted">Durée</span>
                <span className={routeAlternativeDeltaDurationLabel.startsWith("+") ? "text-[rgb(154,52,18)]" : "text-(--color-legacy-teal)"}>
                  {routeAlternativeDeltaDurationLabel}
                </span>
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(148,163,184,0.18)] bg-white/92 px-2 py-1 text-[0.64rem] font-semibold text-content-copy">
                <span className="text-content-muted">Distance</span>
                <span className={routeAlternativeDeltaDistanceLabel.startsWith("+") ? "text-[rgb(154,52,18)]" : "text-(--color-legacy-teal)"}>
                  {routeAlternativeDeltaDistanceLabel}
                </span>
              </span>
              {routeAlternativeArrivalLabel ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(148,163,184,0.18)] bg-white/92 px-2 py-1 text-[0.64rem] font-semibold text-content-copy">
                  <span className="text-content-muted">Arrivée</span>
                  <span className="text-text">{routeAlternativeArrivalLabel}</span>
                </span>
              ) : null}
            </div>

            {routeAlternativeDocks.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {routeAlternativeDocks.map((dock) => (
                  <span
                    key={dock?.id || dock?.label}
                    className="inline-flex rounded-full border border-[rgba(148,163,184,0.18)] bg-white/92 px-2 py-1 text-[0.62rem] font-semibold text-content-copy"
                  >
                    {dock?.label || "Quai"}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <ActionButton
                className={joinClasses([
                  "h-9 rounded-xl px-3 text-[0.72rem] font-semibold hover:bg-white",
                  routeAlternativeActionIsCurrent
                    ? "border-border-warning-badge bg-[rgba(255,247,237,0.9)] text-[rgb(154,52,18)] hover:border-[rgba(217,119,6,0.28)]"
                    : SEGMENT_BUTTON_ACTIVE_CLASS_NAME,
                ])}
                disabled={routeAlternativeActionDisabled}
                onClick={handleApplyRouteAlternative}
              >
                {routeAlternativeActionLabel}
              </ActionButton>
            </div>
          </SegmentActionEditorPanel>
        ) : null}
      </div>
      ) : null}

      {!isGroupActionMode && orderEditorEditable && (orderHasChanges || orderHasValidationErrors) ? (
        <div className="flex justify-end">
          <ActionButton
            className={joinClasses(["h-8 rounded-xl px-3 text-[0.68rem] font-semibold", SEGMENT_BUTTON_ACTIVE_CLASS_NAME])}
            disabled={!orderHasChanges || orderHasValidationErrors}
            onClick={requestOrderSaveConfirmation}
          >
            Enregistrer
          </ActionButton>
        </div>
      ) : null}

      {saveConfirmationDialog ? <ConfirmationModal {...saveConfirmationDialog} /> : null}
      {endOfDayLoadRemovalDialog ? (
        <EndOfDayLoadRemovalDialog
          groupOrderCount={endOfDayLoadRemovalDialog.groupOrderCount}
          groupOrderPreview={endOfDayLoadRemovalDialog.groupOrderPreview}
          onCancel={handleCloseEndOfDayLoadRemovalDialog}
          onClose={handleCloseEndOfDayLoadRemovalDialog}
          onConfirm={handleConfirmEndOfDayLoadRemovalDialog}
          sourceDate={endOfDayLoadRemovalDialog.sourceDate}
          sourceFieldLabel={endOfDayLoadRemovalDialog.sourceFieldLabel}
          sourceTruckLabel={endOfDayLoadRemovalDialog.sourceTruckLabel}
          subtitle={endOfDayLoadRemovalDialog.subtitle}
          targetDate={endOfDayLoadRemovalDialog.targetDate}
          targetTruckLabel={endOfDayLoadRemovalDialog.targetTruckLabel}
          title={endOfDayLoadRemovalDialog.title}
        />
      ) : null}
    </section>
  );
}

function SegmentActionPopover({ anchorElement = null, assignmentInteractions = {}, dispatchDate = "", dispatchPeriod = "day", panelRef = null, row = null, segment = null, truckReassignmentTargets = [] }) {
  const isOpen = Boolean(anchorElement instanceof HTMLElement && segment);
  const popoverWidthClassName = getSegmentActionPopoverWidthClass(segment);
  const isEndOfDayLoad = Boolean(segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload || segment?.isPmOperationalLoading);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    // Keep the initial hidden panel out of the portal root's document overflow.
    strategy: "fixed",
    placement: "top-start",
    middleware: [
      offset(10),
      flip({ padding: 12 }),
      shift({ padding: 12, crossAxis: isEndOfDayLoad }),
    ],
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: isEndOfDayLoad }),
  });

  React.useEffect(() => {
    refs.setReference(anchorElement);
  }, [anchorElement, refs]);
  const setFloatingRef = React.useCallback((element) => {
    if (panelRef && typeof panelRef === "object") {
      panelRef.current = element;
    }

    refs.setFloating(element);
  }, [panelRef, refs]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      ref={setFloatingRef}
      data-fleet-segment-action-popover="true"
      data-grain-floating-surface="segment-actions"
      className={joinClasses([
        "z-popover max-h-[min(82vh,44rem)] overflow-y-auto pr-1",
        popoverWidthClassName,
        isPositioned ? "" : "pointer-events-none",
      ])}
      style={{
        ...floatingStyles,
        visibility: isPositioned ? "visible" : "hidden",
      }}
    >
      <SegmentPlanningRightContext.Provider value={assignmentInteractions.canMutatePlanning === true ? "" : "Votre rôle n'a pas ce droit."}>
        <SegmentActionPanel
          assignmentInteractions={assignmentInteractions}
          className="fleet-context-popover-surface"
          dispatchDate={dispatchDate}
          dispatchPeriod={dispatchPeriod}
          row={row}
          segment={segment}
          truckReassignmentTargets={truckReassignmentTargets}
        />
      </SegmentPlanningRightContext.Provider>
    </div>,
    getReactPortalRoot(),
  );
}

function normalizeSuspendedTransferIdList(values = []) {
  return (Array.isArray(values) ? values : [values])
    .map((value) => normalizeSegmentMetaValue(value))
    .filter(Boolean)
    .filter((value, index, items) => items.indexOf(value) === index);
}

function SuspendedTransferAction({
  assignmentInteractions = {},
  dispatchDate = "",
  item = null,
  onTargetAssignmentSelect = null,
  sourceDispatchPeriod = "day",
  truckCapabilityById = null,
  truckReassignmentTargets = [],
}) {
  const normalizedSourceDispatchPeriod = sourceDispatchPeriod === "evening" || item?.dispatchPeriod === "evening"
    ? "evening"
    : "day";
  const standardTransferTargetPeriod = normalizedSourceDispatchPeriod === "evening" ? "day" : "evening";
  const standardTransferTargetLabel = standardTransferTargetPeriod === "evening" ? "Soir" : "Jour";
  const isEveningSuspendedSource = normalizedSourceDispatchPeriod === "evening";
  const deliveryIds = normalizeSuspendedTransferIdList(
    Array.isArray(item?.deliveryIds) && item.deliveryIds.length ? item.deliveryIds : [item?.deliveryId],
  );
  const orderIds = normalizeSuspendedTransferIdList(
    Array.isArray(item?.orderIds) && item.orderIds.length
      ? item.orderIds
      : (Array.isArray(item?.memberItems) ? item.memberItems.map((memberItem) => memberItem?.orderLabel) : []),
  );
  const sourceTruckId = normalizeSegmentMetaValue(item?.currentTruckId || item?.sourceTruckId);
  const sourceGroupKey = normalizeSegmentMetaValue(item?.sourceGroupKey || item?.groupKey);
  const sourceProductLabel = normalizeSegmentMetaValue(item?.productLabel) === "Mixte"
    ? ""
    : normalizeSegmentMetaValue(item?.productLabel);
  const sourceRequiresAspirator = item?.requiresAspirator === true;
  const sourceIsDompeur = item?.isDompeur === true;
  const sourceRequiresOntario = item?.requiresOntario === true;
  const isPending = item?.isPending === true;
  const deliveryScope = deliveryIds.length > 1 ? "group" : "delivery";
  const orderScope = orderIds.length > 1 ? "group" : "order";
  const [anchorElement, setAnchorElement] = React.useState(null);
  const [isTransferActionMenuOpen, setIsTransferActionMenuOpen] = React.useState(false);
  const [isEndOfDayLoadEditorOpen, setIsEndOfDayLoadEditorOpen] = React.useState(false);
  const [endOfDayLoadDeliveryMode, setEndOfDayLoadDeliveryMode] = React.useState("next_day");
  const [endOfDayLoadSourceDispatchDate, setEndOfDayLoadSourceDispatchDate] = React.useState(() => (
    normalizeEndOfDayLoadDateValue(dispatchDate)
  ));
  const [endOfDayLoadTargetDispatchDate, setEndOfDayLoadTargetDispatchDate] = React.useState(() => (
    resolveEndOfDayLoadTargetDispatchDateForMode(dispatchDate, "next_day", "")
  ));
  const endOfDayLoadDayTargetDispatchDateRef = React.useRef(
    resolveEndOfDayLoadTargetDispatchDateForMode(dispatchDate, "next_day", ""),
  );
  const [endOfDayLoadSourceTruckId, setEndOfDayLoadSourceTruckId] = React.useState("");
  const [isPreloadedPreviousDayEditorOpen, setIsPreloadedPreviousDayEditorOpen] = React.useState(false);
  const [preloadedPreviousDayTargetTruckId, setPreloadedPreviousDayTargetTruckId] = React.useState("");
  const [isEveningStandardEditorOpen, setIsEveningStandardEditorOpen] = React.useState(false);
  const [eveningStandardTargetTruckId, setEveningStandardTargetTruckId] = React.useState("");
  const [eveningStandardTargetsState, setEveningStandardTargetsState] = React.useState({
    errorMessage: "",
    isLoading: false,
    options: [],
  });
  const [saveConfirmationDialog, setSaveConfirmationDialog] = React.useState(null);
  const isOpen = Boolean(anchorElement instanceof HTMLElement);
  const isOpenRef = React.useRef(false);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    placement: "left-start",
    middleware: [
      offset(8),
      flip({ padding: 12 }),
      shift({ padding: 12 }),
    ],
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: false }),
  });

  React.useEffect(() => {
    refs.setReference(anchorElement);
  }, [anchorElement, refs]);

  React.useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const suspendedCompatibilityByTruckId = React.useMemo(() => (
    buildTransferTargetCompatibilityIndex(item?.compatibilities)
  ), [item?.compatibilities]);
  const normalizedEndOfDayLoadDeliveryMode = normalizeEndOfDayLoadDeliveryMode(endOfDayLoadDeliveryMode);
  const isEndOfDayLoadEveningMode = normalizedEndOfDayLoadDeliveryMode === "evening";
  const endOfDayLoadTargetPeriod = resolveEndOfDayLoadTargetPeriod(normalizedEndOfDayLoadDeliveryMode);
  const normalizedEndOfDayLoadSourceDispatchDate = normalizeEndOfDayLoadDateValue(endOfDayLoadSourceDispatchDate);
  const endOfDayLoadSourceTrucksState = useEndOfDayLoadSourceTruckOptions({
    dispatchDate: normalizedEndOfDayLoadSourceDispatchDate,
    dispatchPeriod: "day",
    enabled: isEndOfDayLoadEditorOpen,
  });
  const eveningStandardTargetOptions = eveningStandardTargetsState.options;
  // La remorque source livre aussi le Chargé PM : elle suit les règles d'une cible (aspirateur, Ontario, Dompeur), lues
  // sur sa ligne du dispatch source chargé, pas sur les lignes du Gantt affiché. Le compte sans la règle du produit associé
  // sert au message de liste vide.
  const [endOfDayLoadSourceTruckOptions, endOfDayLoadSourceCountWithoutProduct] = React.useMemo(() => {
    const filterSourceTrucks = (productLabel) => filterEndOfDayLoadTargetTrucks(
      filterEndOfDayLoadSourceTrucks(endOfDayLoadSourceTrucksState.options, { keepTruckId: endOfDayLoadSourceTruckId, sourceProductLabel: productLabel }),
      {
        excludeDompeurTargets: deliveryScope === "group",
        includeProtected: true,
        requiresAspirator: sourceRequiresAspirator,
        requiresOntario: sourceRequiresOntario,
      },
    );
    return [
      attachTransferTargetCompatibilities(filterSourceTrucks(sourceProductLabel), suspendedCompatibilityByTruckId, { sourceIsDompeur }),
      filterSourceTrucks("").length,
    ];
  }, [
    deliveryScope,
    endOfDayLoadSourceTruckId,
    endOfDayLoadSourceTrucksState.options,
    sourceIsDompeur,
    sourceProductLabel,
    sourceRequiresAspirator,
    sourceRequiresOntario,
    suspendedCompatibilityByTruckId,
  ]);
  const [preloadedPreviousDayTargetOptions, preloadedPreviousDayTargetCountWithoutProduct] = React.useMemo(() => {
    const baseTargets = attachTransferTargetCompatibilities(
      buildTransferTargetOptionsFromTruckReassignmentTargets(truckReassignmentTargets),
      suspendedCompatibilityByTruckId,
      { sourceIsDompeur },
    );
    const targetFilters = {
      requiresAspirator: sourceRequiresAspirator,
      requiresInitialPreloadEnabled: true,
      requiresOntario: sourceRequiresOntario,
      truckCapabilityById,
    };
    return [
      filterEndOfDayLoadTargetTrucks(baseTargets, { ...targetFilters, sourceProductLabel }),
      filterEndOfDayLoadTargetTrucks(baseTargets, targetFilters).length,
    ];
  }, [
    sourceProductLabel,
    sourceRequiresAspirator,
    sourceIsDompeur,
    sourceRequiresOntario,
    suspendedCompatibilityByTruckId,
    truckCapabilityById,
    truckReassignmentTargets,
  ]);
  const preloadedPreviousDayTargetOption = preloadedPreviousDayTargetOptions.find((target) => target.truckId === preloadedPreviousDayTargetTruckId)
    || null;
  const eveningStandardTargetOption = resolveSelectableTransferTarget(eveningStandardTargetOptions, eveningStandardTargetTruckId);
  const canAssignSuspendedPreloadedPreviousDay = Boolean(
    !isPending
    && deliveryIds.length > 0
    && orderIds.length > 0
    && typeof assignmentInteractions.onAssignSuspendedPreloadedPreviousDay === "function",
  );
  const canOpenPreloadedPreviousDay = !isEveningSuspendedSource && canAssignSuspendedPreloadedPreviousDay;
  const canAssignEndOfDayLoad = Boolean(
    !isEveningSuspendedSource
    && !isPending
    && deliveryIds.length > 0
    && typeof assignmentInteractions.onAssignEndOfDayLoad === "function",
  );
  const canAssignEveningStandard = Boolean(
    !isPending
    && deliveryIds.length > 0
    && typeof assignmentInteractions.onAssignEveningStandard === "function",
  );
  // Sans le droit « Modifier les affectations dispatch », « Transférer » est grisé, avec la bulle.
  const canWritePlanning = assignmentInteractions.canMutatePlanning === true;
  const canOpenTransfer = canWritePlanning && (canOpenPreloadedPreviousDay || canAssignEndOfDayLoad || canAssignEveningStandard);
  const selectedEndOfDayLoadTargetDispatchDate = isEndOfDayLoadEveningMode
    ? normalizedEndOfDayLoadSourceDispatchDate
    : normalizeEndOfDayLoadDateValue(endOfDayLoadTargetDispatchDate);
  const endOfDayLoadTargetUsesSourceDispatchState = endOfDayLoadTargetPeriod === "day"
    && selectedEndOfDayLoadTargetDispatchDate === normalizedEndOfDayLoadSourceDispatchDate;
  const endOfDayLoadTargetTrucksState = useEndOfDayLoadSourceTruckOptions({
    allowMissingSnapshot: true,
    dispatchDate: selectedEndOfDayLoadTargetDispatchDate,
    dispatchPeriod: endOfDayLoadTargetPeriod,
    enabled: isEndOfDayLoadEditorOpen
      && Boolean(selectedEndOfDayLoadTargetDispatchDate)
      && !endOfDayLoadTargetUsesSourceDispatchState,
  });
  const endOfDayLoadTargetPolicyState = endOfDayLoadTargetUsesSourceDispatchState
    ? endOfDayLoadSourceTrucksState
    : endOfDayLoadTargetTrucksState;
  const selectedEndOfDayLoadSourceTruckId = normalizeSegmentMetaValue(endOfDayLoadSourceTruckId);
  const selectedEndOfDayLoadSourceTruck = endOfDayLoadSourceTruckOptions.find(
    (target) => target.truckId === selectedEndOfDayLoadSourceTruckId,
  ) || null;
  const endOfDayLoadSourceTruckIsUnavailable = Boolean(
    selectedEndOfDayLoadSourceTruckId
    && !endOfDayLoadSourceTrucksState.isLoading
    && !endOfDayLoadSourceTrucksState.errorMessage
    && endOfDayLoadSourceTrucksState.dispatchDate === normalizedEndOfDayLoadSourceDispatchDate
    && !selectedEndOfDayLoadSourceTruck,
  );
  const endOfDayLoadSourceFieldLabel = resolveEndOfDayLoadTrailerFieldLabel(selectedEndOfDayLoadSourceTruck, "source");
  const endOfDayLoadTargetTrailerLabel = resolveEndOfDayLoadTrailerLabel(selectedEndOfDayLoadSourceTruck);
  const endOfDayLoadTargetTrailerMessage = resolveEndOfDayLoadTargetTrailerMessage({
    dispatchDate: selectedEndOfDayLoadTargetDispatchDate,
    dispatchPeriod: endOfDayLoadTargetPeriod,
    dispatchState: endOfDayLoadTargetPolicyState,
    truck: selectedEndOfDayLoadSourceTruck,
  });
  const selectedPreloadedPreviousDayTargetTruckId = normalizeSegmentMetaValue(preloadedPreviousDayTargetTruckId);
  const preloadedPreviousDayTargetOptionsBlockedMessage = !preloadedPreviousDayTargetOptions.length
    ? buildEndOfDayLoadTargetTrucksEmptyMessage(sourceProductLabel, { countWithoutProduct: preloadedPreviousDayTargetCountWithoutProduct })
    : "";
  const isPreloadedPreviousDaySubmitDisabled = Boolean(
    !canOpenPreloadedPreviousDay
    || !selectedPreloadedPreviousDayTargetTruckId
    || preloadedPreviousDayTargetOptionsBlockedMessage,
  );
  const endOfDayLoadSourceDateValidationMessage = !normalizedEndOfDayLoadSourceDispatchDate
    ? "Date source obligatoire."
    : "";
  const endOfDayLoadSourceOptionsBlockedMessage = isEndOfDayLoadEditorOpen
    && normalizedEndOfDayLoadSourceDispatchDate
    && (
      endOfDayLoadSourceTrucksState.isLoading
      || endOfDayLoadSourceTrucksState.dispatchDate !== normalizedEndOfDayLoadSourceDispatchDate
    )
    ? "Chargement des remorques du dispatch source..."
    : endOfDayLoadSourceTrucksState.errorMessage
      ? endOfDayLoadSourceTrucksState.errorMessage
      : normalizedEndOfDayLoadSourceDispatchDate
        && endOfDayLoadSourceTrucksState.dispatchDate === normalizedEndOfDayLoadSourceDispatchDate
        && !endOfDayLoadSourceTruckOptions.length
        ? buildEndOfDayLoadTargetTrucksEmptyMessage(sourceProductLabel, { countWithoutProduct: endOfDayLoadSourceCountWithoutProduct })
        : "";
  const endOfDayLoadDateValidationMessage = !selectedEndOfDayLoadTargetDispatchDate
    ? "Date cible obligatoire."
    : "";
  const endOfDayLoadSourceTruckValidationMessage = !selectedEndOfDayLoadSourceTruckId
    ? `${endOfDayLoadSourceFieldLabel} obligatoire.`
    : endOfDayLoadSourceTruckIsUnavailable
      ? `${endOfDayLoadSourceFieldLabel} indisponible pour cette date. Choisissez une source active.`
      : "";
  const endOfDayLoadSourceChangesValidationMessage = (
    !endOfDayLoadSourceOptionsBlockedMessage
    && !endOfDayLoadSourceTruckValidationMessage
  )
    ? resolveEndOfDayLoadTruckSourceChangesMessage({
      dispatchDate: normalizedEndOfDayLoadSourceDispatchDate,
      dispatchPeriod: "day",
      dispatchState: endOfDayLoadSourceTrucksState,
      role: "source",
      truck: selectedEndOfDayLoadSourceTruck,
    })
    : "";
  const endOfDayLoadTargetSourceChangesValidationMessage = (
    !endOfDayLoadTargetTrailerMessage
    && !endOfDayLoadSourceTruckValidationMessage
    && selectedEndOfDayLoadTargetDispatchDate
  )
    ? resolveEndOfDayLoadTruckSourceChangesMessage({
      allowMissingSnapshot: true,
      dispatchDate: selectedEndOfDayLoadTargetDispatchDate,
      dispatchPeriod: endOfDayLoadTargetPeriod,
      dispatchState: endOfDayLoadTargetPolicyState,
      role: "cible",
      truck: selectedEndOfDayLoadSourceTruck,
    })
    : "";
  const endOfDayLoadSubmitBlockedMessage = endOfDayLoadSourceOptionsBlockedMessage
    || endOfDayLoadSourceDateValidationMessage
    || endOfDayLoadSourceTruckValidationMessage
    || endOfDayLoadSourceChangesValidationMessage
    || endOfDayLoadTargetTrailerMessage
    || endOfDayLoadDateValidationMessage
    || endOfDayLoadTargetSourceChangesValidationMessage;
  const isEndOfDayLoadSubmitDisabled = Boolean(
    !canAssignEndOfDayLoad
    || !normalizedEndOfDayLoadSourceDispatchDate
    || !selectedEndOfDayLoadSourceTruckId
    || !selectedEndOfDayLoadTargetDispatchDate
    || endOfDayLoadSubmitBlockedMessage,
  );
  const selectedEveningStandardTargetTruckId = normalizeSegmentMetaValue(
    eveningStandardTargetOption?.truckId || eveningStandardTargetTruckId,
  );
  const eveningStandardDisabledReason = eveningStandardTargetsState.isLoading
    ? `Chargement des camions du dispatch ${standardTransferTargetLabel.toLowerCase()}...`
    : eveningStandardTargetsState.errorMessage
      ? eveningStandardTargetsState.errorMessage
      : !eveningStandardTargetOptions.length
        ? buildEndOfDayLoadTargetTrucksEmptyMessage("")
        : "";
  const isEveningStandardSubmitDisabled = Boolean(
    !canAssignEveningStandard
    || !selectedEveningStandardTargetTruckId
    || !dispatchDate
    || deliveryIds.length === 0
    || eveningStandardDisabledReason,
  );

  const closePopover = React.useCallback(() => {
    isOpenRef.current = false;
    refs.setReference(null);
    setAnchorElement(null);
    setIsTransferActionMenuOpen(false);
    setIsPreloadedPreviousDayEditorOpen(false);
    setPreloadedPreviousDayTargetTruckId("");
    setIsEndOfDayLoadEditorOpen(false);
    setEndOfDayLoadSourceTruckId("");
    setIsEveningStandardEditorOpen(false);
  }, [refs]);
  const isSuspendedTransferActionTarget = React.useCallback((target) => (
    typeof HTMLElement !== "undefined"
    && target instanceof HTMLElement
    && Boolean(target.closest('[data-suspended-transfer-action="true"]'))
  ), []);

  usePopoverDismiss({
    anchorElement,
    floatingRef: refs.floating,
    isOpen,
    onClose: closePopover,
    shouldIgnoreTarget: isSuspendedTransferActionTarget,
  });

  React.useEffect(() => {
    closePopover();
    setEndOfDayLoadDeliveryMode("next_day");
    setEndOfDayLoadSourceDispatchDate(normalizeEndOfDayLoadDateValue(dispatchDate));
    setEndOfDayLoadTargetDispatchDate(resolveEndOfDayLoadTargetDispatchDateForMode(dispatchDate, "next_day", ""));
    endOfDayLoadDayTargetDispatchDateRef.current = resolveEndOfDayLoadTargetDispatchDateForMode(dispatchDate, "next_day", "");
    setEndOfDayLoadSourceTruckId("");
    setPreloadedPreviousDayTargetTruckId("");
    setEveningStandardTargetTruckId("");
    setEveningStandardTargetsState({ errorMessage: "", isLoading: false, options: [] });
    setSaveConfirmationDialog(null);
  }, [closePopover, dispatchDate, item?.key, normalizedSourceDispatchPeriod]);

  React.useEffect(() => {
    if (!isPreloadedPreviousDayEditorOpen) {
      return;
    }

    if (!preloadedPreviousDayTargetOptions.some((target) => target.truckId === preloadedPreviousDayTargetTruckId)) {
      setPreloadedPreviousDayTargetTruckId("");
    }
  }, [isPreloadedPreviousDayEditorOpen, preloadedPreviousDayTargetOptions, preloadedPreviousDayTargetTruckId]);

  React.useEffect(() => {
    if (!isEveningStandardEditorOpen) {
      return;
    }

    const preferredTarget = resolveSelectableTransferTarget(eveningStandardTargetOptions, sourceTruckId);
    if (!preferredTarget) {
      setEveningStandardTargetTruckId("");
      return;
    }

    setEveningStandardTargetTruckId((currentValue) => (
      eveningStandardTargetOptions.some((target) => target.truckId === currentValue && !target.isTractorMissing)
        ? currentValue
        : preferredTarget.truckId
    ));
  }, [eveningStandardTargetOptions, isEveningStandardEditorOpen, sourceTruckId]);

  React.useEffect(() => {
    const shouldLoadEveningTargets = Boolean(
      dispatchDate
      && canAssignEveningStandard
      && (isTransferActionMenuOpen || isEveningStandardEditorOpen)
    );
    if (!shouldLoadEveningTargets) {
      return undefined;
    }

    const abortController = new AbortController();
    setEveningStandardTargetsState((currentState) => ({
      ...currentState,
      errorMessage: "",
      isLoading: true,
      options: [],
    }));
    const searchParams = new URLSearchParams();
    searchParams.set("dispatch_date", dispatchDate);
    searchParams.set("dispatch_period", standardTransferTargetPeriod);
    fetchWithSupabaseAuth(`/api/results/latest?${searchParams.toString()}`, { signal: abortController.signal })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = `Impossible de charger les camions du dispatch ${standardTransferTargetLabel.toLowerCase()}.`;
          try {
            const errorPayload = await response.json();
            errorMessage = normalizeSegmentMetaValue(errorPayload?.error || errorPayload?.detail) || errorMessage;
          } catch {
            // Keep the generic message; the response may not be JSON.
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((payload) => {
        if (abortController.signal.aborted) {
          return;
        }
        const baseTargets = attachTransferTargetCompatibilities(
          buildTransferTargetOptionsFromOptimizationAssignments(payload?.assignments),
          suspendedCompatibilityByTruckId,
          { sourceIsDompeur },
        );
        // Cibles de l'autre période : leurs propres champs (camion attelé de cette période), pas les lignes du Gantt affiché.
        setEveningStandardTargetsState({
          errorMessage: "",
          isLoading: false,
          options: buildEveningStandardTargetOptions(baseTargets, [], {
            requiresAspirator: sourceRequiresAspirator,
            requiresOntario: sourceRequiresOntario,
            sourceProductLabel,
          }),
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setEveningStandardTargetsState({
          errorMessage: error?.message || `Impossible de charger les camions du dispatch ${standardTransferTargetLabel.toLowerCase()}.`,
          isLoading: false,
          options: [],
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    canAssignEveningStandard,
    dispatchDate,
    isEveningStandardEditorOpen,
    isTransferActionMenuOpen,
    sourceProductLabel,
    sourceRequiresAspirator,
    sourceIsDompeur,
    sourceRequiresOntario,
    standardTransferTargetLabel,
    standardTransferTargetPeriod,
    suspendedCompatibilityByTruckId,
  ]);

  const openSaveConfirmationDialog = React.useCallback((dialogConfig, onConfirmAction) => {
    const dismissDialog = () => {
      setSaveConfirmationDialog(null);
    };

    setSaveConfirmationDialog({
      ...dialogConfig,
      isOpen: true,
      onCancel: dismissDialog,
      onClose: dismissDialog,
      onConfirm: () => {
        dismissDialog();
        onConfirmAction?.();
        closePopover();
      },
    });
  }, [closePopover]);

  const handleTransferButtonPointerDown = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleTransferButtonClick = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canOpenTransfer) {
      return;
    }

    if (isOpenRef.current) {
      closePopover();
      return;
    }

    isOpenRef.current = true;
    refs.setReference(event.currentTarget);
    setAnchorElement(event.currentTarget);
    setIsTransferActionMenuOpen(true);
  }, [canOpenTransfer, closePopover, refs]);

  const handleEndOfDayLoadDeliveryModeChange = React.useCallback((nextMode) => {
    const deliveryMode = normalizeEndOfDayLoadDeliveryMode(nextMode);
    const currentMode = normalizeEndOfDayLoadDeliveryMode(endOfDayLoadDeliveryMode);
    if (currentMode !== "evening" && deliveryMode === "evening") {
      endOfDayLoadDayTargetDispatchDateRef.current = normalizeEndOfDayLoadDateValue(endOfDayLoadTargetDispatchDate);
    }
    setEndOfDayLoadDeliveryMode(deliveryMode);
    setEndOfDayLoadTargetDispatchDate((currentValue) => (
      deliveryMode === "evening"
        ? normalizedEndOfDayLoadSourceDispatchDate
        : resolveEndOfDayLoadTargetDispatchDateForMode(
          normalizedEndOfDayLoadSourceDispatchDate,
          "next_day",
          endOfDayLoadDayTargetDispatchDateRef.current || currentValue,
        )
    ));
  }, [endOfDayLoadDeliveryMode, endOfDayLoadTargetDispatchDate, normalizedEndOfDayLoadSourceDispatchDate]);

  const handleEndOfDayLoadSourceDispatchDateChange = React.useCallback((nextDispatchDate) => {
    const normalizedDate = normalizeEndOfDayLoadDateValue(nextDispatchDate);
    setEndOfDayLoadSourceDispatchDate(normalizedDate);
    if (isEndOfDayLoadEveningMode) {
      setEndOfDayLoadTargetDispatchDate(normalizedDate);
    }
  }, [isEndOfDayLoadEveningMode]);

  const applyPreloadedPreviousDay = React.useCallback(() => {
    const targetTruckId = normalizeSegmentMetaValue(selectedPreloadedPreviousDayTargetTruckId);
    const summaryTruckName = preloadedPreviousDayTargetOption?.truckName || targetTruckId;
    if (
      !canOpenPreloadedPreviousDay
      || !targetTruckId
      || orderIds.length === 0
      || deliveryIds.length === 0
      || preloadedPreviousDayTargetOptionsBlockedMessage
    ) {
      return;
    }

    openSaveConfirmationDialog({
      ...SEGMENT_PRELOAD_ASSIGNMENT_CONFIRMATION,
      summaryItems: [
        { label: "Source", value: "Suspendues" },
        { label: "Portée", value: orderScope === "group" ? `Groupe (${orderIds.length})` : "Commande" },
        { label: "Commande", value: orderIds.join(", "), span: "fill" },
        { label: "Camion Preload", value: summaryTruckName },
      ],
    }, () => {
      assignmentInteractions.onAssignSuspendedPreloadedPreviousDay?.({
        deliveryId: deliveryIds[0] || "",
        deliveryIds,
        orderIds,
        sourceGroupKey: deliveryScope === "group" ? sourceGroupKey : "",
        targetCompatibility: preloadedPreviousDayTargetOption?.targetCompatibility || null,
        targetTruckId,
        targetTruckName: preloadedPreviousDayTargetOption?.truckName || targetTruckId,
      });
    });
  }, [
    assignmentInteractions,
    canOpenPreloadedPreviousDay,
    deliveryIds,
    deliveryScope,
    openSaveConfirmationDialog,
    orderIds,
    orderScope,
    preloadedPreviousDayTargetOption?.truckName,
    preloadedPreviousDayTargetOption?.targetCompatibility,
    preloadedPreviousDayTargetOptionsBlockedMessage,
    selectedPreloadedPreviousDayTargetTruckId,
    sourceGroupKey,
  ]);

  const handlePreloadedPreviousDayButtonClick = React.useCallback(() => {
    if (!canOpenPreloadedPreviousDay || orderIds.length === 0) {
      return;
    }

    setIsTransferActionMenuOpen(false);
    setIsEndOfDayLoadEditorOpen(false);
    setIsEveningStandardEditorOpen(false);
    setPreloadedPreviousDayTargetTruckId("");

    setIsPreloadedPreviousDayEditorOpen((currentValue) => !currentValue);
  }, [
    canOpenPreloadedPreviousDay,
    orderIds.length,
  ]);

  const openEndOfDayLoadEditor = React.useCallback(() => {
    setIsTransferActionMenuOpen(false);
    setIsPreloadedPreviousDayEditorOpen(false);
    setIsEveningStandardEditorOpen(false);
    setEndOfDayLoadSourceTruckId("");
    setIsEndOfDayLoadEditorOpen((currentValue) => !currentValue);
  }, []);

  const openEveningStandardEditor = React.useCallback(() => {
    setIsTransferActionMenuOpen(false);
    setIsPreloadedPreviousDayEditorOpen(false);
    setIsEndOfDayLoadEditorOpen(false);
    setIsEveningStandardEditorOpen((currentValue) => !currentValue);
  }, []);

  const handleApplyEndOfDayLoad = React.useCallback(() => {
    const sourceDispatchDate = normalizedEndOfDayLoadSourceDispatchDate;
    const targetDispatchDate = selectedEndOfDayLoadTargetDispatchDate;
    if (
      !canAssignEndOfDayLoad
      || !sourceDispatchDate
      || !selectedEndOfDayLoadSourceTruckId
      || !targetDispatchDate
      || endOfDayLoadDateValidationMessage
      || endOfDayLoadSourceDateValidationMessage
      || endOfDayLoadSourceOptionsBlockedMessage
      || endOfDayLoadSourceTruckValidationMessage
      || endOfDayLoadSourceChangesValidationMessage
      || endOfDayLoadTargetTrailerMessage
      || endOfDayLoadTargetSourceChangesValidationMessage
      || deliveryIds.length === 0
    ) {
      return;
    }

    openSaveConfirmationDialog({
      ...SEGMENT_END_OF_DAY_LOAD_ASSIGNMENT_CONFIRMATION,
      summaryItems: [
        { label: "Source", value: "Suspendues" },
        { label: "Portée", value: deliveryScope === "group" ? `Groupe (${deliveryIds.length})` : "Commande" },
        { label: "Mode", value: getEndOfDayLoadModeLongLabel(normalizedEndOfDayLoadDeliveryMode, {
          sourceDispatchDate,
          targetDispatchDate,
        }) },
        { label: "Date source", value: sourceDispatchDate },
        { label: endOfDayLoadSourceFieldLabel, value: resolveEndOfDayLoadTrailerLabel(selectedEndOfDayLoadSourceTruck) || selectedEndOfDayLoadSourceTruckId },
        { label: "Date cible", value: targetDispatchDate },
        { label: resolveEndOfDayLoadTrailerFieldLabel(selectedEndOfDayLoadSourceTruck), value: endOfDayLoadTargetTrailerLabel },
      ],
    }, () => {
      assignmentInteractions.onAssignEndOfDayLoad?.({
        deliveryMode: normalizedEndOfDayLoadDeliveryMode,
        deliveryId: deliveryIds[0] || "",
        deliveryIds,
        dispatchDate,
        groupStepIds: [],
        markerKey: "",
        scope: deliveryScope,
        sourceGroupKey: deliveryScope === "group" ? sourceGroupKey : "",
        sourceDispatchDate,
        sourceTruckId: selectedEndOfDayLoadSourceTruckId,
        targetCompatibility: selectedEndOfDayLoadSourceTruck?.targetCompatibility || null,
        targetPeriod: endOfDayLoadTargetPeriod,
        targetDispatchDate,
        targetTruckId: selectedEndOfDayLoadSourceTruckId,
        targetTruckName: endOfDayLoadTargetTrailerLabel,
      });
    });
  }, [
    assignmentInteractions,
    canAssignEndOfDayLoad,
    deliveryIds,
    deliveryScope,
    dispatchDate,
    endOfDayLoadDateValidationMessage,
    endOfDayLoadSourceDateValidationMessage,
    endOfDayLoadSourceOptionsBlockedMessage,
    endOfDayLoadSourceChangesValidationMessage,
    endOfDayLoadSourceFieldLabel,
    endOfDayLoadSourceTruckValidationMessage,
    endOfDayLoadTargetPeriod,
    endOfDayLoadTargetSourceChangesValidationMessage,
    endOfDayLoadTargetTrailerLabel,
    endOfDayLoadTargetTrailerMessage,
    normalizedEndOfDayLoadDeliveryMode,
    normalizedEndOfDayLoadSourceDispatchDate,
    openSaveConfirmationDialog,
    selectedEndOfDayLoadSourceTruck,
    selectedEndOfDayLoadSourceTruckId,
    selectedEndOfDayLoadTargetDispatchDate,
    sourceGroupKey,
  ]);

  const handleApplyEveningStandard = React.useCallback(() => {
    const targetTruckId = selectedEveningStandardTargetTruckId;
    if (
      !canAssignEveningStandard
      || !targetTruckId
      || !dispatchDate
      || deliveryIds.length === 0
      || eveningStandardDisabledReason
    ) {
      return;
    }

    openSaveConfirmationDialog({
      ...(standardTransferTargetPeriod === "day"
        ? SEGMENT_DAY_STANDARD_TRANSFER_CONFIRMATION
        : SEGMENT_EVENING_STANDARD_TRANSFER_CONFIRMATION),
      summaryItems: [
        { label: "Source", value: "Suspendues" },
        { label: "Portée", value: deliveryScope === "group" ? `Groupe (${deliveryIds.length})` : "Commande" },
        { label: "Mode", value: `${standardTransferTargetLabel} standard` },
        { label: "Date", value: dispatchDate },
        { label: "Camion", value: eveningStandardTargetOption?.truckName || targetTruckId },
      ],
    }, () => {
      assignmentInteractions.onAssignEveningStandard?.({
        deliveryId: deliveryIds[0] || "",
        deliveryIds,
        dispatchDate,
        groupStepIds: [],
        onMutationSuccess: () => {
          onTargetAssignmentSelect?.({
            assignmentId: targetTruckId,
            dispatchPeriod: standardTransferTargetPeriod,
          });
        },
        scope: deliveryScope,
        sourceDispatchPeriod: normalizedSourceDispatchPeriod,
        sourceGroupKey: deliveryScope === "group" ? sourceGroupKey : "",
        sourceTruckId,
        targetCompatibility: eveningStandardTargetOption?.targetCompatibility || null,
        targetPeriod: standardTransferTargetPeriod,
        targetTruckId,
        targetTruckName: eveningStandardTargetOption?.truckName || targetTruckId,
      });
    });
  }, [
    assignmentInteractions,
    canAssignEveningStandard,
    deliveryIds,
    deliveryScope,
    dispatchDate,
    eveningStandardDisabledReason,
    eveningStandardTargetOption?.truckId,
    eveningStandardTargetOption?.truckName,
    eveningStandardTargetOption?.targetCompatibility,
    openSaveConfirmationDialog,
    normalizedSourceDispatchPeriod,
    onTargetAssignmentSelect,
    selectedEveningStandardTargetTruckId,
    sourceGroupKey,
    sourceTruckId,
    standardTransferTargetLabel,
    standardTransferTargetPeriod,
  ]);

  const transferPopover = isOpen && getReactPortalRoot() instanceof HTMLElement ? createPortal(
    <div
      ref={refs.setFloating}
      data-fleet-suspended-transfer-popover="true"
      data-grain-floating-surface="suspended-transfer"
      className={joinClasses([
        "z-popover max-h-[min(82vh,40rem)] overflow-y-auto pr-1",
        isEndOfDayLoadEditorOpen ? "w-[min(26rem,calc(100vw-1.5rem))]" : "w-[min(21rem,calc(100vw-1.5rem))]",
        isPositioned ? "" : "pointer-events-none",
      ])}
      style={{
        ...floatingStyles,
        visibility: isPositioned ? "visible" : "hidden",
      }}
    >
      {/* Chargé PM : même surface bleue de chargement que le menu d'un segment du Gantt. */}
      <div
        style={isEndOfDayLoadEditorOpen ? { "--fleet-context-accent": getSegmentHoverCardAccentColor("loading") } : undefined}
        className={isEndOfDayLoadEditorOpen
          ? joinClasses(["fleet-context-surface fleet-context-popover-surface grid gap-2 rounded-[4px] border px-3 py-3", getSegmentHoverSurfaceClassName("loading")])
          : "grid gap-2 rounded-[18px] border border-[rgba(15,23,42,0.1)] bg-white/96 p-2 shadow-[0_20px_42px_rgba(15,23,42,0.18)]"}
      >
        {isTransferActionMenuOpen ? (
          <SegmentActionEditorPanel errorMessages={[]}>
            <div className="grid gap-1">
              <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">Transférer</div>
              <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
                {deliveryIds.length > 1
                  ? `Groupe suspendu de ${deliveryIds.length} commandes.`
                  : "Commande suspendue sélectionnée."}
              </p>
            </div>
            <div className="grid gap-1.25">
              {[
                ...(!isEveningSuspendedSource ? [{
                  description: "Choisir le camion pour activer Chargé la veille.",
                  disabled: !canOpenPreloadedPreviousDay,
                  icon: "moon",
                  key: "preload",
                  label: "Preload",
                  onClick: handlePreloadedPreviousDayButtonClick,
                }, {
                  description: "Ouvre le flux Chargé PM existant.",
                  disabled: !canAssignEndOfDayLoad,
                  icon: getEndOfDayLoadModeIconName(normalizedEndOfDayLoadDeliveryMode),
                  key: "pm",
                  label: "Chargé PM",
                  onClick: openEndOfDayLoadEditor,
                }] : []),
                ...(!isEveningSuspendedSource ? [{
                  description: "Ouvre le choix du camion soir standard.",
                  disabled: !canAssignEveningStandard,
                  icon: "moon",
                  key: "evening",
                  label: "Soir",
                  onClick: openEveningStandardEditor,
                }] : [{
                  description: "Ouvre le choix du camion jour standard.",
                  disabled: !canAssignEveningStandard,
                  icon: "sun",
                  key: "day",
                  label: "Jour",
                  onClick: openEveningStandardEditor,
                }]),
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={joinClasses([
                    "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 rounded-xl border px-2.5 py-2 text-left transition",
                    option.disabled
                      ? "cursor-not-allowed border-[rgba(148,163,184,0.16)] bg-slate-50/76 text-content-faint"
                      : SEGMENT_BUTTON_NEUTRAL_CLASS_NAME,
                  ])}
                  disabled={option.disabled}
                  onClick={option.disabled ? undefined : option.onClick}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(148,163,184,0.18)] bg-white">
                    {renderSegmentStatusIcon(option.icon, "h-3.5 w-3.5")}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[0.72rem] font-extrabold leading-5">{option.label}</span>
                    <span className="block text-[0.64rem] font-medium leading-4 text-content-muted">{option.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </SegmentActionEditorPanel>
        ) : null}

        {isPreloadedPreviousDayEditorOpen ? (
          <SegmentActionEditorPanel errorMessages={[preloadedPreviousDayTargetOptionsBlockedMessage].filter(Boolean)}>
            <div className="grid gap-1">
              <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">Preload</div>
              <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
                {orderScope === "group"
                  ? `Groupe complet de ${orderIds.length} commandes.`
                  : "Commande sélectionnée."}
              </p>
            </div>
            <label className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Camion Preload</span>
              <select
                className="h-9 rounded-xl border border-[rgba(14,116,144,0.24)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-[rgba(14,116,144,0.48)] focus:ring-2 focus:ring-[rgba(14,116,144,0.14)]"
                disabled={!preloadedPreviousDayTargetOptions.length}
                value={selectedPreloadedPreviousDayTargetTruckId}
                onChange={(event) => setPreloadedPreviousDayTargetTruckId(event.target.value)}
              >
                <option value="">Choisir un camion...</option>
                {preloadedPreviousDayTargetOptions.map((target) => (
                  <option key={target.truckId} value={target.truckId} disabled={target.isTractorMissing}>
                    {transferTargetLabel(target)}
                  </option>
                ))}
              </select>
            </label>
            <p className="m-0 text-[0.7rem] leading-5 text-content-muted">
              Les commandes suspendues seront assignées au camion choisi, puis le camion sera reconstruit avec Chargé la veille.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Tooltip
                title="Preload indisponible"
                content={preloadedPreviousDayTargetOptionsBlockedMessage || "Choisissez un camion Preload."}
                placement="top"
                disabled={!isPreloadedPreviousDaySubmitDisabled}
              >
                <span className={joinClasses(["inline-flex", isPreloadedPreviousDaySubmitDisabled ? "cursor-not-allowed" : ""])}>
                  <ActionButton
                    className={joinClasses([
                      "h-9 rounded-xl border-[rgba(14,116,144,0.24)] bg-[rgba(236,254,255,0.96)] px-3 text-[0.72rem] font-semibold text-[rgb(14,116,144)] hover:border-[rgba(14,116,144,0.34)] hover:bg-white",
                      isPreloadedPreviousDaySubmitDisabled ? "pointer-events-none" : "",
                    ])}
                    disabled={isPreloadedPreviousDaySubmitDisabled}
                    onClick={() => applyPreloadedPreviousDay()}
                  >
                    Activer Preload
                  </ActionButton>
                </span>
              </Tooltip>
              <ActionButton
                className="h-9 rounded-xl border-[rgba(148,163,184,0.18)] bg-white px-3 text-[0.72rem] font-semibold text-content-copy hover:border-[rgba(148,163,184,0.3)] hover:bg-white"
                onClick={() => {
                  setPreloadedPreviousDayTargetTruckId("");
                  setIsPreloadedPreviousDayEditorOpen(false);
                }}
              >
                Fermer
              </ActionButton>
            </div>
          </SegmentActionEditorPanel>
        ) : null}

        {isEndOfDayLoadEditorOpen ? (
          <SegmentActionEditorPanel className={END_OF_DAY_LOAD_EDITOR_PANEL_CLASS_NAME} errorMessages={[
            endOfDayLoadSourceOptionsBlockedMessage,
            endOfDayLoadSourceDateValidationMessage,
            endOfDayLoadSourceTruckValidationMessage,
            endOfDayLoadSourceChangesValidationMessage,
            endOfDayLoadTargetTrailerMessage,
            endOfDayLoadDateValidationMessage,
            endOfDayLoadTargetSourceChangesValidationMessage,
          ].filter(Boolean)}>
            <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">Chargé en PM</div>
            <EndOfDayLoadDeliveryModeField value={normalizedEndOfDayLoadDeliveryMode} onChange={handleEndOfDayLoadDeliveryModeChange} />
            <div className={END_OF_DAY_LOAD_FIELDS_GRID_CLASS_NAME}>
              <div className={END_OF_DAY_LOAD_FIELD_COLUMN_CLASS_NAME}>
                <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                  <span>Date source</span>
                  <input
                    type="date"
                    value={normalizedEndOfDayLoadSourceDispatchDate}
                    onChange={(event) => handleEndOfDayLoadSourceDispatchDateChange(event.target.value)}
                    className={END_OF_DAY_LOAD_FIELD_CLASS_NAME}
                  />
                </label>
                <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                  <span>{endOfDayLoadSourceFieldLabel}</span>
                  <select
                    className={END_OF_DAY_LOAD_FIELD_CLASS_NAME}
                    disabled={endOfDayLoadSourceTrucksState.isLoading || Boolean(endOfDayLoadSourceTrucksState.errorMessage)}
                    value={selectedEndOfDayLoadSourceTruckId}
                    onChange={(event) => setEndOfDayLoadSourceTruckId(event.target.value)}
                  >
                    <option value="">Choisir une remorque...</option>
                    {endOfDayLoadSourceTruckIsUnavailable ? (
                      <option value={selectedEndOfDayLoadSourceTruckId} disabled>
                        {selectedEndOfDayLoadSourceTruckId} — indisponible
                      </option>
                    ) : null}
                    {endOfDayLoadSourceTruckOptions.map((target) => (
                      <option key={target.truckId} value={target.truckId}>
                        {resolveEndOfDayLoadTrailerLabel(target)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className={END_OF_DAY_LOAD_FIELD_COLUMN_CLASS_NAME}>
                <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                  <span>Date cible</span>
                  <input
                    type="date"
                    disabled={isEndOfDayLoadEveningMode}
                    value={selectedEndOfDayLoadTargetDispatchDate || ""}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      endOfDayLoadDayTargetDispatchDateRef.current = nextValue;
                      setEndOfDayLoadTargetDispatchDate(nextValue);
                    }}
                    className={END_OF_DAY_LOAD_FIELD_CLASS_NAME}
                  />
                </label>
                <label className={END_OF_DAY_LOAD_FIELD_LABEL_CLASS_NAME}>
                  <span>{resolveEndOfDayLoadTrailerFieldLabel(selectedEndOfDayLoadSourceTruck)}</span>
                  <input className={END_OF_DAY_LOAD_FIELD_CLASS_NAME} disabled placeholder="Suit la source" type="text" value={endOfDayLoadTargetTrailerLabel} />
                </label>
              </div>
            </div>
            <EndOfDayLoadEditorActions
              blockedMessage={endOfDayLoadSubmitBlockedMessage}
              isSubmitDisabled={isEndOfDayLoadSubmitDisabled}
              onClose={() => {
                setEndOfDayLoadSourceTruckId("");
                setIsEndOfDayLoadEditorOpen(false);
              }}
              onSubmit={handleApplyEndOfDayLoad}
            />
          </SegmentActionEditorPanel>
        ) : null}

        {isEveningStandardEditorOpen ? (
          <SegmentActionEditorPanel errorMessages={[eveningStandardDisabledReason].filter(Boolean)}>
            <div className="grid gap-1">
              <div className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">{standardTransferTargetLabel} standard</div>
              <p className="m-0 text-[0.72rem] leading-5 text-content-muted">
                {deliveryScope === "group" ? `Groupe complet de ${deliveryIds.length} commandes.` : "Commande sélectionnée."}
              </p>
            </div>
            <div className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Date</span>
              <div className="h-9 rounded-xl border border-[rgba(92,138,120,0.18)] bg-[rgba(242,248,245,0.86)] px-3 py-2 text-[0.82rem] font-semibold text-text">
                {dispatchDate || "Date courante"}
              </div>
            </div>
            <label className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Camion {standardTransferTargetLabel.toLowerCase()}</span>
              <select
                className="h-9 rounded-xl border border-[rgba(92,138,120,0.24)] bg-white px-3 text-[0.82rem] text-text outline-none transition focus:border-[rgba(92,138,120,0.5)] focus:ring-2 focus:ring-[rgba(92,138,120,0.14)]"
                disabled={!eveningStandardTargetOptions.length}
                value={eveningStandardTargetOption?.truckId || ""}
                onChange={(event) => setEveningStandardTargetTruckId(event.target.value)}
              >
                {eveningStandardTargetOptions.map((target) => (
                  <option key={target.truckId} value={target.truckId} disabled={target.isTractorMissing}>
                    {transferTargetLabel(target)}
                  </option>
                ))}
              </select>
            </label>
            <p className="m-0 text-[0.7rem] leading-5 text-content-muted">
              Transfert standard vers le dispatch {standardTransferTargetLabel}, sans modifier la période active affichée.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Tooltip
                title="Transfert indisponible"
                content={eveningStandardDisabledReason}
                placement="top"
                disabled={!eveningStandardDisabledReason}
              >
                <span className={joinClasses(["inline-flex", isEveningStandardSubmitDisabled ? "cursor-not-allowed" : ""])}>
                  <ActionButton
                    className={joinClasses([
                      "h-9 rounded-xl px-3 text-[0.72rem] font-semibold",
                      SEGMENT_BUTTON_ACTIVE_CLASS_NAME,
                      isEveningStandardSubmitDisabled ? "pointer-events-none" : "",
                    ])}
                    disabled={isEveningStandardSubmitDisabled}
                    onClick={handleApplyEveningStandard}
                  >
                    Transférer {standardTransferTargetLabel.toLowerCase()}
                  </ActionButton>
                </span>
              </Tooltip>
              <ActionButton
                className="h-9 rounded-xl border-[rgba(148,163,184,0.18)] bg-white px-3 text-[0.72rem] font-semibold text-content-copy hover:border-[rgba(148,163,184,0.3)] hover:bg-white"
                onClick={() => setIsEveningStandardEditorOpen(false)}
              >
                Fermer
              </ActionButton>
            </div>
          </SegmentActionEditorPanel>
        ) : null}
      </div>
    </div>,
    getReactPortalRoot(),
  ) : null;

  return (
    <span
      className="inline-flex shrink-0"
      data-suspended-transfer-action="true"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <ActionButton
        size="sm"
        tone="subtle"
        aria-expanded={isOpen}
        aria-label="Transférer la commande suspendue"
        className={joinClasses([
          "h-8! min-h-8! w-8! gap-0! rounded-full! border! p-0! shadow-none!",
          isOpen
            ? SEGMENT_BUTTON_ACTIVE_CLASS_NAME
            : SUSPENDED_TRANSFER_BUTTON_NEUTRAL_CLASS_NAME,
        ])}
        disabled={!canOpenTransfer}
        onClick={handleTransferButtonClick}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={handleTransferButtonPointerDown}
        title={canWritePlanning ? "Transférer" : "Votre rôle n'a pas ce droit."}
      >
        {renderSegmentStatusIcon("shuffle", "h-3.5 w-3.5")}
      </ActionButton>
      {transferPopover}
      {saveConfirmationDialog ? <ConfirmationModal {...saveConfirmationDialog} /> : null}
    </span>
  );
}

function TruckActionsPopover({
  anchorElement = null,
  assignment = null,
  bulkReassignmentTargets = [],
  currentDriverName = "",
  dispatchDate = "",
  dispatchPeriod = "day",
  incidents = [],
  onClose = null,
  onManageTruckIncidents = null,
  onOptimizeTruckRoutes = null,
  onPrintTruckSheet = null,
  onPublishTruckPortal = null,
  onReassignAllTruckOrders = null,
  onSetTruckPreloadTime = null,
  onUpdateTruckDriverName = null,
  onUpdateTruckTractor = null,
  onUpdateTruckStartTime = null,
  onUpdateTruckTimeSettings = null,
  onToggleTruckDispatchDisabled = null,
  onToggleTruckDompeurMixedLoad = null,
  onToggleTruckOrdersProtection = null,
  onToggleTruckOvertime = null,
  onToggleTruckProtection = null,
  onUnpublishTruckPortal = null,
  portalPublications = null,
  truckSourceChanges = null,
  truckSheetPrintDisabledReason = "",
  truckSheetPrintIsBusy = false,
  truckTimeSettingsDisabledReason = "",
}) {
  const isOpen = Boolean(anchorElement instanceof HTMLElement && assignment);
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    // Keep the initial hidden panel out of the portal root's document overflow.
    strategy: "fixed",
    placement: "right-start",
    middleware: [
      offset(10),
      flip({ padding: 12 }),
      shift({ padding: 12, crossAxis: true }),
    ],
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: false }),
  });

  React.useEffect(() => {
    refs.setReference(anchorElement);
  }, [anchorElement, refs]);

  usePopoverDismiss({
    anchorElement,
    floatingRef: refs.floating,
    isOpen,
    onClose,
    shouldIgnoreTarget: (target) => (
      target instanceof Element
      && Boolean(target.closest('[data-driver-employee-select-list="true"], [data-tractor-select-list="true"]'))
    ),
  });

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      ref={refs.setFloating}
      data-fleet-truck-actions-popover="true"
      data-grain-floating-surface="truck-actions"
      className={joinClasses([
        "z-popover p-1",
        isPositioned ? "" : "pointer-events-none",
      ])}
      style={{
        ...floatingStyles,
        visibility: isPositioned ? "visible" : "hidden",
      }}
    >
      <TruckProtectionActionPanel
        assignment={assignment}
        bulkReassignmentTargets={bulkReassignmentTargets}
        currentDriverName={currentDriverName}
        dispatchDate={dispatchDate}
        dispatchPeriod={dispatchPeriod}
        incidents={incidents}
        onClose={onClose}
        onManageTruckIncidents={onManageTruckIncidents}
        onOptimizeTruckRoutes={onOptimizeTruckRoutes}
        onPrintTruckSheet={onPrintTruckSheet}
        onPublishTruckPortal={onPublishTruckPortal}
        onReassignAllTruckOrders={onReassignAllTruckOrders}
        onSetTruckPreloadTime={onSetTruckPreloadTime}
        onUpdateTruckDriverName={onUpdateTruckDriverName}
        onUpdateTruckTractor={onUpdateTruckTractor}
        onUpdateTruckStartTime={onUpdateTruckStartTime}
        onUpdateTruckTimeSettings={onUpdateTruckTimeSettings}
        onToggleTruckDispatchDisabled={onToggleTruckDispatchDisabled}
        onToggleTruckDompeurMixedLoad={onToggleTruckDompeurMixedLoad}
        onToggleTruckOrdersProtection={onToggleTruckOrdersProtection}
        onToggleTruckOvertime={onToggleTruckOvertime}
        onToggleTruckProtection={onToggleTruckProtection}
        onUnpublishTruckPortal={onUnpublishTruckPortal}
        portalPublications={portalPublications}
        truckSourceChanges={truckSourceChanges}
        truckSheetPrintDisabledReason={truckSheetPrintDisabledReason}
        truckSheetPrintIsBusy={truckSheetPrintIsBusy}
        truckTimeSettingsDisabledReason={truckTimeSettingsDisabledReason}
        variant="popover"
      />
    </div>,
    getReactPortalRoot(),
  );
}

export {
  formatTruckTotalHoverDistance,
  SegmentActionPopover,
  SuspendedTransferAction,
  TruckActionsPopover,
};
